import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  Activity,
  ArrowLeft,
  Bot,
  CarFront,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Clock,
  Copy,
  Database,
  Download,
  ExternalLink,
  FileSpreadsheet,
  GalleryVerticalEnd,
  Grip,
  ImagePlus,
  KeyRound,
  Link2Off,
  LogOut,
  MessageSquare,
  Minus,
  Package,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Send,
  Settings,
  Shield,
  ShoppingCart,
  Square,
  Truck,
  User,
  Users,
  WalletCards,
  X,
  Zap
} from 'lucide-react';
import './styles.css';
import './visual-polish.css';
import './responsive-polish.css';
import { resolveActivitySession } from './activitySession.js';
import { statusClassName, statusLabel } from './ui-status.js';
import { priceExportIntervalOption } from './priceExport.js';
import './accessibility.css';
import { EmptyState, ErrorState, InlineNotice, LoadingState } from './components/AsyncState.jsx';
import AIChatWidget from './components/AIChatWidget.jsx';
import PriceExportPanel from './components/PriceExportPanel.jsx';
import ProductImage from './components/ProductImage.jsx';
import './state-media-polish.css';
import './components/ai-chat.css';

const API = '/api';
const CATALOG_VIEW_STORAGE_KEY = 'catalog-view-mode-v2';
const ACTIVITY_SESSION_STORAGE_KEY = 'dixel-activity-session-v2';
let fallbackActivitySession = null;
const DELIVERY_METHOD_OPTIONS = [
  { value: 'pickup', label: 'Самовывоз' },
  { value: 'cdek', label: 'СДЭК' }
];
const CATALOG_PAGE_LIMIT = 36;
const DEFAULT_CATALOG_STATE = { query: '', activeCategory: '', browseParent: '', page: 1, scrollY: 0 };
const RETURN_ITEMS_PER_PAGE = 8;
const ADMIN_PAGE_LIMIT = 30;
const ADMIN_WIDE_PAGE_LIMIT = 60;
const QUANTITY_FORMATTER = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 3 });
const ADMIN_TAB_LABELS = {
  orders: 'Заказы',
  returns: 'Возвраты',
  users: 'Клиенты',
  products: 'Товары',
  import: 'Импорт',
  ms: 'МойСклад',
  auto: 'Авто',
  classifier: 'Классификатор',
  ai: 'AI'
};

async function api(path, options = {}) {
  const headers = { ...(options.headers || {}) };
  if (options.body) headers['Content-Type'] = 'application/json';
  const response = await fetch(`${API}${path}`, {
    credentials: 'include',
    headers,
    ...options,
    body: options.body ? JSON.stringify(options.body) : undefined
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'Ошибка запроса');
  return data;
}

async function apiForm(path, formData) {
  const response = await fetch(`${API}${path}`, {
    method: 'POST',
    credentials: 'include',
    body: formData
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'Ошибка запроса');
  return data;
}

function money(value) {
  return new Intl.NumberFormat('ru-RU').format(Number(value || 0)) + ' ₽';
}

function durationLabel(totalSeconds) {
  const parsed = Number(totalSeconds || 0);
  const seconds = Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : 0;
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days} д ${hours} ч`;
  if (hours > 0) return `${hours} ч ${minutes} мин`;
  return `${minutes} мин`;
}

function getActivitySessionId() {
  try {
    const stored = window.sessionStorage.getItem(ACTIVITY_SESSION_STORAGE_KEY);
    const session = resolveActivitySession(stored);
    window.sessionStorage.setItem(ACTIVITY_SESSION_STORAGE_KEY, JSON.stringify(session));
    return session.id;
  } catch {
    fallbackActivitySession = resolveActivitySession(
      fallbackActivitySession ? JSON.stringify(fallbackActivitySession) : null
    );
    return fallbackActivitySession.id;
  }
}

function aiCost(value) {
  const amount = Number(value || 0);
  return `$${amount.toFixed(amount < 0.01 ? 4 : 2)}`;
}

function sanitizeDescriptionHtml(value) {
  return String(value || '')
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
    .replace(/\son[a-z]+="[^"]*"/gi, '')
    .replace(/\son[a-z]+='[^']*'/gi, '')
    .trim();
}

function clampQty(value, max) {
  const parsed = Math.floor(Number(value || 0));
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.max(0, Math.min(max, parsed));
}

function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null);
}

function numberOrNull(value) {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatQuantity(value) {
  return QUANTITY_FORMATTER.format(numberOrNull(value) ?? 0);
}

function formatDateTime(value) {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString('ru-RU');
}

function getProductStockBreakdown(product) {
  const rows = Array.isArray(product?.stockBreakdown) ? product.stockBreakdown : [];
  const totalStock = numberOrNull(product?.stock) ?? 0;
  const defaults = [
    { label: 'Склад #1', quantity: totalStock },
    { label: 'Склад #2', quantity: 0 }
  ];

  return defaults.map((fallback, index) => {
    const row = rows[index] || {};
    return {
      label: row.label || fallback.label,
      quantity: numberOrNull(firstDefined(row.quantity, row.qty, row.stock)) ?? fallback.quantity
    };
  });
}

function productStockPart(product, index, fallback = null) {
  const rows = Array.isArray(product?.stockBreakdown) ? product.stockBreakdown : [];
  const row = rows[index] || {};
  return numberOrNull(firstDefined(row.quantity, row.qty, row.stock)) ?? fallback;
}

function productSupplierStock(product) {
  return numberOrNull(product?.supplierStock) ?? productStockPart(product, 0, 0) ?? 0;
}

function productMsStock(product) {
  return numberOrNull(product?.msStock) ?? productStockPart(product, 1, 0) ?? 0;
}

function productTotalStock(product) {
  return numberOrNull(product?.stock) ?? productSupplierStock(product) + productMsStock(product);
}

function adminProductDraft(product) {
  return {
    id: product.id,
    vendorCode: product.vendorCode || '',
    name: product.name || '',
    purchasePrice: product.purchasePrice ?? 0,
    prices: { ...(product.prices || {}) },
    supplierStock: productSupplierStock(product),
    available: product.available !== false,
    imageUrls: product.imageUrls || []
  };
}

function adminProductSavePayload(row) {
  return {
    id: row.id,
    vendorCode: row.vendorCode,
    name: row.name,
    purchasePrice: row.purchasePrice,
    prices: row.prices,
    supplierStock: Math.max(0, Number(row.supplierStock || 0)),
    available: row.available,
    imageUrls: row.imageUrls
  };
}

function resolveMsStockStatus(data = {}) {
  const stocksPayload = data.stocks || {};
  const stockSyncPayload = data.stockSync || {};
  const raw = { ...stocksPayload, ...stockSyncPayload };
  const nestedSync = {
    ...(stocksPayload.sync || stocksPayload.stockSync || {}),
    ...(stockSyncPayload.sync || stockSyncPayload.stockSync || {})
  };
  const summary = data.summary?.stocks || stocksPayload.summary || stockSyncPayload.summary || {};
  const counts = {
    ...(summary.counts || {}),
    ...(stocksPayload.counts || {}),
    ...(stockSyncPayload.counts || {}),
    ...(nestedSync.counts || {})
  };
  const getCount = (...values) => numberOrNull(firstDefined(...values));

  return {
    status: firstDefined(nestedSync.status, raw.status, summary.status, 'idle'),
    message: firstDefined(nestedSync.message, raw.message, summary.message, 'Еще не запускалось'),
    processed: getCount(nestedSync.processed, raw.processed, counts.processed, summary.processed),
    total: getCount(
      nestedSync.total,
      raw.total,
      counts.total,
      summary.total,
      nestedSync.totalProducts,
      raw.totalProducts,
      counts.totalProducts,
      summary.totalProducts,
      nestedSync.productCount,
      raw.productCount,
      counts.productCount,
      summary.productCount
    ),
    updated: getCount(nestedSync.updated, nestedSync.updatedCount, raw.updated, raw.updatedCount, counts.updated, summary.updated),
    matched: getCount(nestedSync.matched, nestedSync.matchedCount, raw.matched, raw.matchedCount, counts.matched, summary.matched),
    skipped: getCount(nestedSync.skipped, nestedSync.skippedCount, raw.skipped, raw.skippedCount, counts.skipped, summary.skipped),
    negative: getCount(
      nestedSync.negative,
      nestedSync.negatives,
      nestedSync.negativeCount,
      raw.negative,
      raw.negatives,
      raw.negativeCount,
      counts.negative,
      counts.negatives,
      summary.negative
    ),
    storeName: firstDefined(nestedSync.storeName, raw.storeName, nestedSync.store?.name, raw.store?.name, 'Основной склад'),
    startedAt: firstDefined(nestedSync.startedAt, raw.startedAt, summary.startedAt),
    finishedAt: firstDefined(nestedSync.finishedAt, raw.finishedAt, summary.finishedAt),
    lastRunAt: firstDefined(nestedSync.lastRunAt, raw.lastRunAt, raw.lastSyncAt, summary.lastRunAt, summary.lastSyncAt),
    updatedAt: firstDefined(nestedSync.updatedAt, raw.updatedAt, summary.updatedAt)
  };
}

function getRoute() {
  const { pathname } = window.location;
  const productMatch = pathname.match(/^\/product\/(\d+)\/?$/);
  if (productMatch) return { name: 'product', productId: Number(productMatch[1]) };
  if (pathname === '/auto') return { name: 'auto' };
  if (pathname === '/cart') return { name: 'cart' };
  if (pathname === '/account/return') return { name: 'accountReturn' };
  if (pathname === '/account/export') return { name: 'accountExport' };
  if (pathname === '/account') return { name: 'account' };
  if (pathname === '/admin') return { name: 'admin' };
  return { name: 'catalog' };
}

function routePath(route) {
  if (typeof route === 'string') return route;
  if (route.name === 'product') return `/product/${route.productId}`;
  if (route.name === 'auto') return '/auto';
  if (route.name === 'cart') return '/cart';
  if (route.name === 'accountReturn') return '/account/return';
  if (route.name === 'accountExport') return '/account/export';
  if (route.name === 'account') return '/account';
  if (route.name === 'admin') return '/admin';
  return '/';
}

function routeTitle(route) {
  const titles = {
    catalog: 'Каталог',
    auto: 'Подбор по авто',
    cart: 'Корзина',
    accountReturn: 'Оформить возврат',
    accountExport: 'Экспорт',
    account: 'Личный кабинет',
    admin: 'Админка',
    product: 'Товар'
  };
  return `${titles[route.name] || titles.catalog} — DIXEL`;
}

function useMediaQuery(query) {
  const getMatch = () => (typeof window !== 'undefined' ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(getMatch);

  useEffect(() => {
    const media = window.matchMedia(query);
    const sync = () => setMatches(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, [query]);

  return matches;
}

const DIALOG_FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

function useModalDialog({ open, onClose, dialogRef, initialFocusRef }) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open || !dialogRef.current) return undefined;

    const dialog = dialogRef.current;
    const returnFocusTarget = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const backgroundNodes = [];

    function focusableElements() {
      return Array.from(dialog.querySelectorAll(DIALOG_FOCUSABLE_SELECTOR)).filter(
        (element) => element instanceof HTMLElement && element.getClientRects().length > 0
      );
    }

    const preferredFocus = initialFocusRef?.current;
    const firstFocusTarget =
      (preferredFocus instanceof HTMLElement && preferredFocus) || focusableElements()[0] || dialog;
    firstFocusTarget.focus({ preventScroll: true });

    let branch = dialog;
    const root = document.getElementById('root');
    while (branch && branch !== root) {
      const parent = branch.parentElement;
      if (!parent) break;
      for (const sibling of parent.children) {
        if (sibling === branch || sibling.hasAttribute('data-dialog-backdrop')) continue;
        backgroundNodes.push({
          node: sibling,
          hadInert: sibling.hasAttribute('inert'),
          ariaHidden: sibling.getAttribute('aria-hidden')
        });
        sibling.setAttribute('inert', '');
        sibling.setAttribute('aria-hidden', 'true');
      }
      branch = parent;
    }

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = focusableElements();
      if (!focusable.length) {
        event.preventDefault();
        dialog.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      for (const { node, hadInert, ariaHidden } of backgroundNodes) {
        if (!hadInert) node.removeAttribute('inert');
        if (ariaHidden === null) node.removeAttribute('aria-hidden');
        else node.setAttribute('aria-hidden', ariaHidden);
      }
      window.requestAnimationFrame(() => {
        if (returnFocusTarget?.isConnected) returnFocusTarget.focus({ preventScroll: true });
      });
    };
  }, [open, dialogRef, initialFocusRef]);
}

function traceStepTitle(step) {
  const titles = {
    input: 'Вход',
    session_context: 'Контекст сессии',
    clarification_short_circuit: 'Быстрое уточнение',
    conversation_short_circuit: 'Быстрый ответ без базы',
    tool_remember_context: 'Память контекста',
    tool_catalog_search: 'Поиск по каталогу',
    tool_sql_product_search: 'SQL поиск товаров',
    tool_vector_product_search: 'Vector поиск товаров',
    tool_product_details: 'Проверка карточек',
    tool_attach_products: 'Прикрепление карточек',
    visual_context: 'Контекст изображения',
    usage_accounting: 'Учёт токенов и стоимости',
    final_answer: 'Финальный ответ',
    agent_round_1: 'Agent round 1',
    agent_round_2: 'Agent round 2',
    agent_round_3: 'Agent round 3',
    agent_round_4: 'Agent round 4',
    agent_round_5: 'Agent round 5',
    agent_round_6: 'Agent round 6',
    agent_round_7: 'Agent round 7'
  };
  return titles[step] || 'Технический шаг';
}

function traceStepSummary(step) {
  const payload = step?.payload || {};
  if (step.step === 'input') {
    return payload.image ? 'Получен текст и фото для разбора.' : 'Получен текстовый запрос.';
  }
  if (step.step === 'session_context') {
    return payload.visualContext
      ? 'Агент поднял историю диалога и сохранённый visual context.'
      : 'Агент поднял историю диалога перед новым проходом.';
  }
  if (step.step === 'clarification_short_circuit') {
    return 'Ответ отдан сразу, без лишнего агентного круга.';
  }
  if (step.step === 'conversation_short_circuit') {
    return 'Сообщение не похоже на поиск товара, база и embeddings не запускались.';
  }
  if (step.step === 'tool_remember_context') {
    return `Сохранён контекст: ${payload.summary || 'важные признаки из сообщения или фото'}.`;
  }
  if (step.step === 'tool_catalog_search') {
    return `Запрос по дереву: ${payload.query || 'нет данных'}, веток: ${payload.categories?.length || 0}.`;
  }
  if (step.step === 'tool_sql_product_search') {
    return `Запрос: ${payload.query || 'нет данных'}, товаров: ${payload.products?.length || 0}.`;
  }
  if (step.step === 'tool_vector_product_search') {
    return `Semantic-запрос: ${payload.query || 'нет данных'}, товаров: ${payload.products?.length || 0}.`;
  }
  if (step.step === 'tool_product_details') {
    return `Проверены подробности карточек: ${payload.products?.length || 0}.`;
  }
  if (step.step === 'tool_attach_products') {
    return `К чату прикреплено карточек: ${payload.productIds?.length || 0}.`;
  }
  if (step.step === 'visual_context') {
    return 'Картинка сохранена как текстовый контекст для следующих сообщений сессии.';
  }
  if (step.step === 'usage_accounting') {
    return `Токены: ${Number(payload.usage?.totalTokens || 0).toLocaleString('ru-RU')}, стоимость: ${aiCost(payload.usage?.totalCost || 0)}.`;
  }
  if (step.step?.startsWith('agent_round_')) {
    return `Агент обработал шаг: вызовов tools ${payload.toolCalls?.length || 0}, поисковых rounds использовано ${payload.retrievalRoundsUsed || 0}.`;
  }
  if (step.step === 'final_answer') {
    return `Сформирован ответ клиенту, карточек: ${payload.attachedProductIds?.length || 0}.`;
  }
  return 'Технический шаг обработки.';
}

function Logo() {
  return <img className="logo" src="/dixel-logo.png" alt="DIXEL" />;
}

function IconButton({ icon: Icon, children, className = '', ...props }) {
  return (
    <button className={`button ${className}`} {...props}>
      {Icon && <Icon size={18} />}
      <span>{children}</span>
    </button>
  );
}

function StatusBadge({ status, fallback, className = '', ...props }) {
  const rawStatus = String(status || '').trim();
  return (
    <span className={`${statusClassName(rawStatus)} ${className}`.trim()} {...props}>
      {statusLabel(rawStatus, fallback)}
    </span>
  );
}

function QuantityControl({ qty, max, onChange, className = '', disabled = false, min = 0, label = 'Количество товара' }) {
  const [draft, setDraft] = useState(String(qty || 0));

  useEffect(() => {
    setDraft(String(qty || 0));
  }, [qty]);

  function commit(value) {
    const minQty = Math.max(0, Number(min || 0));
    const parsed = Math.floor(Number(value || 0));
    const nextQty = !Number.isFinite(parsed)
      ? minQty
      : Math.min(Number(max || 0), Math.max(minQty, parsed));
    setDraft(String(nextQty));
    onChange(nextQty);
  }

  return (
    <div className={`qty ${className}`} role="group" aria-label={label}>
      <button
        type="button"
        onClick={() => commit((qty || 0) - 1)}
        disabled={disabled || qty <= min}
        aria-label={`Уменьшить: ${label}`}
        title="Уменьшить количество"
      >
        <Minus size={14} aria-hidden="true" />
      </button>
      <input
        value={draft}
        inputMode="numeric"
        disabled={disabled}
        aria-label={label}
        onChange={(event) => setDraft(event.target.value.replace(/[^\d]/g, ''))}
        onBlur={() => commit(draft)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.currentTarget.blur();
          }
        }}
      />
      <button
        type="button"
        onClick={() => commit((qty || 0) + 1)}
        disabled={disabled || qty >= max}
        aria-label={`Увеличить: ${label}`}
        title="Увеличить количество"
      >
        <Plus size={14} aria-hidden="true" />
      </button>
    </div>
  );
}

function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await api('/auth/login', { method: 'POST', body: { email, password } });
      onLogin(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="login-shell">
      <section className="login-panel">
        <Logo />
        <div className="login-intro">
          <h1>Вход для партнёров</h1>
          <p>Используйте рабочую почту или выданный ранее логин.</p>
        </div>
        <form onSubmit={submit} className="login-form">
          <label>
            Почта или старый логин
            <input
              value={email}
              autoComplete="username"
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <label>
            Пароль
            <input
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          {error && <div className="error" role="alert">{error}</div>}
          <IconButton icon={loading ? RefreshCw : Shield} disabled={loading} className={loading ? 'is-busy' : ''}>
            <span className="login-submit-copy">{loading ? 'Входим…' : 'Войти'}</span>
          </IconButton>
        </form>
      </section>
    </main>
  );
}

function Layout({
  user,
  route,
  navigate,
  onCatalogHome,
  onLogout,
  cartTotal,
  cartCount,
  aiEnabled,
  aiOpen,
  onToggleAi,
  children
}) {
  const isMobile = useMediaQuery('(max-width: 900px)');
  const currentSection =
    aiOpen && isMobile
      ? 'AI-подбор'
      : route.name === 'auto'
      ? 'Подбор по авто'
      : route.name === 'cart'
      ? 'Корзина'
      : route.name === 'accountReturn'
        ? 'Оформить возврат'
        : route.name === 'accountExport'
        ? 'Экспорт'
        : route.name === 'account'
        ? 'Кабинет'
        : route.name === 'admin'
          ? 'Админка'
          : route.name === 'product'
            ? 'Товар'
            : 'Каталог';
  const navItems = [
    {
      key: 'catalog',
      label: 'Каталог',
      icon: Package,
      to: '/',
      active: route.name === 'catalog' || route.name === 'product',
      action: onCatalogHome
    },
    { key: 'auto', label: 'Подбор по авто', mobileLabel: 'Авто', icon: CarFront, to: '/auto', active: route.name === 'auto' },
    ...(aiEnabled && isMobile
      ? [{ key: 'ai', label: 'AI', icon: MessageSquare, active: Boolean(aiOpen), action: () => onToggleAi?.() }]
      : []),
    { key: 'cart', label: 'Корзина', icon: ShoppingCart, to: '/cart', active: route.name === 'cart' },
    ...(user.role === 'admin'
      ? [{ key: 'admin', label: 'Админка', mobileLabel: 'Админ', icon: Shield, to: '/admin', active: route.name === 'admin' }]
      : []),
    { key: 'account', label: 'Кабинет', icon: User, to: '/account', active: ['account', 'accountReturn', 'accountExport'].includes(route.name) }
  ];

  return (
    <div className="app-shell">
      <a
        className="skip-link"
        href="#main-content"
        onClick={(event) => {
          event.preventDefault();
          const main = document.getElementById('main-content');
          main?.focus({ preventScroll: true });
          main?.scrollIntoView({ block: 'start' });
        }}
      >
        Перейти к основному содержимому
      </a>
      <header className="topbar">
        <div className="brand-wrap">
          <button className="brand-button" onClick={onCatalogHome} aria-label="Открыть каталог">
            <Logo />
          </button>
          {isMobile && (
            <div className="mobile-topbar-copy">
              <b>{currentSection}</b>
              <span>{user.contractorName || user.name}</span>
            </div>
          )}
        </div>
        {!isMobile && (
          <nav className="nav">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  className={item.active ? 'active' : ''}
                  onClick={() => (item.action ? item.action() : navigate(item.to))}
                  aria-current={item.active && item.key !== 'ai' ? 'page' : undefined}
                  aria-expanded={item.key === 'ai' ? Boolean(aiOpen) : undefined}
                  aria-controls={item.key === 'ai' ? 'ai-chat-dialog' : undefined}
                >
                  <Icon size={18} aria-hidden="true" /> {item.label}
                  {item.key === 'cart' ? <b>{money(cartTotal)}</b> : null}
                </button>
              );
            })}
          </nav>
        )}
        <div className="topbar-actions">
          <button className="logout" onClick={onLogout} aria-label="Выйти" title="Выйти">
            <LogOut size={18} aria-hidden="true" />
          </button>
        </div>
      </header>
      {children}
      {isMobile && route.name !== 'admin' && (
        <nav className="mobile-nav" aria-label="Основная навигация">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                className={item.active ? 'active' : ''}
                onClick={() => (item.action ? item.action() : navigate(item.to))}
                aria-current={item.active && item.key !== 'ai' ? 'page' : undefined}
                aria-expanded={item.key === 'ai' ? Boolean(aiOpen) : undefined}
                aria-controls={item.key === 'ai' ? 'ai-chat-dialog' : undefined}
              >
                <span className="mobile-nav-icon-wrap">
                  <Icon size={18} aria-hidden="true" />
                  {item.key === 'cart' && cartCount > 0 ? <small className="mobile-nav-badge">{cartCount}</small> : null}
                </span>
                <span>{item.mobileLabel || item.label}</span>
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
}

function CategoryIcon({ name }) {
  const lower = name.toLowerCase();
  const Icon = lower.includes('led') || lower.includes('линз')
    ? Zap
    : lower.includes('рамк') || lower.includes('стек')
      ? Settings
      : lower.includes('элект')
        ? Database
        : Package;
  return (
    <span className="category-icon">
      <Icon size={15} />
    </span>
  );
}

function ProductThumb({ product, variant = '', previewHandlers = null, iconSize = 38 }) {
  return (
    <div
      className={`product-image ${variant}`.trim()}
      onMouseEnter={(event) => previewHandlers?.onEnter(event, product)}
      onMouseMove={(event) => previewHandlers?.onMove(event)}
      onMouseLeave={() => previewHandlers?.onLeave()}
    >
      <ProductImage
        src={product.imageUrl}
        alt=""
        fallbackIconSize={iconSize}
      />
    </div>
  );
}

function ProductCard({ product, qty, onQtyChange, onOpen, previewHandlers }) {
  return (
    <article className="product-card">
      <button className="product-link" onClick={() => onOpen(product.id)}>
        <ProductThumb product={product} previewHandlers={previewHandlers} />
        <div className="product-info">
          <div className="product-meta-top">
            <b>{product.name}</b>
            <span>{product.vendorCode}</span>
          </div>
          <small>{product.categoryPath}</small>
        </div>
      </button>
      <div className="product-actions">
        <strong>{money(product.price)}</strong>
        <span className={product.stock > 0 ? 'stock ok' : 'stock'}>
          {product.stock > 0 ? `Остаток ${product.stock} шт.` : 'Нет в наличии'}
        </span>
        <QuantityControl
          qty={qty}
          max={product.stock}
          onChange={onQtyChange}
          className="qty compact"
          label={`Количество в корзине для ${product.name}, артикул ${product.vendorCode}`}
        />
      </div>
    </article>
  );
}

function ProductCompactRow({ product, qty, onQtyChange, onOpen, previewHandlers }) {
  return (
    <article className="product-compact-row">
      <button className="product-compact-main" onClick={() => onOpen(product.id)}>
        <ProductThumb product={product} variant="compact" previewHandlers={previewHandlers} iconSize={28} />
        <div className="product-compact-copy">
          <b>{product.name}</b>
          <span>{product.vendorCode}</span>
          <small>{product.categoryPath}</small>
        </div>
      </button>
      <div className="product-compact-side">
        <strong>{money(product.price)}</strong>
        <span className={product.stock > 0 ? 'stock ok' : 'stock'}>
          {product.stock > 0 ? `${product.stock} шт.` : 'Нет'}
        </span>
        <QuantityControl
          qty={qty}
          max={product.stock}
          onChange={onQtyChange}
          className="qty compact"
          label={`Количество в корзине для ${product.name}, артикул ${product.vendorCode}`}
        />
      </div>
    </article>
  );
}

function ProductTable({ products, cartQtyById, onSetCartQty, onOpen, previewHandlers }) {
  return (
    <div className="catalog-table">
      <div className="catalog-table-head">
        <span>Товар</span>
        <span>Артикул</span>
        <span>Категория</span>
        <span>Цена</span>
        <span>Остаток</span>
        <span>Кол-во</span>
      </div>
      <div className="catalog-table-body">
        {products.map((product) => (
          <article key={product.id} className="catalog-table-row">
            <button className="catalog-table-product" onClick={() => onOpen(product.id)}>
              <ProductThumb product={product} variant="table" previewHandlers={previewHandlers} iconSize={24} />
              <div className="catalog-table-title">
                <b>{product.name}</b>
              </div>
            </button>
            <span className="catalog-table-code">{product.vendorCode}</span>
            <span className="catalog-table-category">{product.categoryPath}</span>
            <div className="catalog-table-metric catalog-table-price">
              <small>Цена</small>
              <strong>{money(product.price)}</strong>
            </div>
            <div className="catalog-table-metric catalog-table-stock">
              <small>Остаток</small>
              <span className={product.stock > 0 ? 'stock ok' : 'stock'}>
                {product.stock > 0 ? `${product.stock} шт.` : 'Нет'}
              </span>
            </div>
            <div className="catalog-table-metric catalog-table-qty">
              <small>Кол-во</small>
              <QuantityControl
                qty={cartQtyById[product.id] || 0}
                max={product.stock}
                onChange={(qty) => onSetCartQty(product, qty)}
                className="qty compact"
                label={`Количество в корзине для ${product.name}, артикул ${product.vendorCode}`}
              />
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function CatalogPager({ page, total, limit, itemCount, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  if (totalPages <= 1) return null;
  const from = total ? (page - 1) * limit + 1 : 0;
  const to = total ? Math.min(total, from + itemCount - 1) : 0;

  return (
    <div className="catalog-pager">
      <span>
        {from}-{to} из {total}
      </span>
      <div className="pager">
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1} title="Предыдущая страница">
          <ChevronLeft size={16} />
        </button>
        <span>
          {page} / {totalPages}
        </span>
        <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages} title="Следующая страница">
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

function yearRangeLabel(yearFrom, yearTo) {
  if (!yearFrom && !yearTo) return '';
  if (yearFrom && yearTo && yearFrom === yearTo) return String(yearFrom);
  return `${yearFrom || '...'}-${yearTo || 'н.в.'}`;
}

function generationOptionLabel(generation) {
  return `${generation.name} (${yearRangeLabel(generation.yearFrom, generation.yearTo)})`;
}

function modificationSpecLine(modification) {
  const specs = modification?.specs || {};
  const pieces = [];
  if (specs.engine_type) pieces.push(specs.engine_type);
  if (specs.transmission) pieces.push(specs.transmission);
  if (specs.gear_type) pieces.push(specs.gear_type);
  return pieces.join(' • ');
}

function fitmentQuery(selection) {
  const params = new URLSearchParams();
  if (selection.markId) params.set('markId', selection.markId);
  if (selection.modelId) params.set('modelId', selection.modelId);
  if (selection.generationId) params.set('generationId', selection.generationId);
  if (selection.configurationId) params.set('configurationId', selection.configurationId);
  if (selection.modificationId) params.set('modificationId', selection.modificationId);
  return params.toString();
}

function emptyVehicleSelection() {
  return {
    markId: '',
    modelId: '',
    generationId: '',
    configurationId: '',
    modificationId: ''
  };
}

function useAutoCatalogSelector({ includeDetails = true } = {}) {
  const [selection, setSelection] = useState(emptyVehicleSelection());
  const [marks, setMarks] = useState([]);
  const [models, setModels] = useState([]);
  const [generations, setGenerations] = useState([]);
  const [configurations, setConfigurations] = useState([]);
  const [modifications, setModifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const markSearchRequestRef = useRef(0);

  useEffect(() => {
    searchMarks('');
  }, []);

  async function searchMarks(search = '') {
    const requestId = markSearchRequestRef.current + 1;
    markSearchRequestRef.current = requestId;
    const params = new URLSearchParams({ limit: '120' });
    if (search.trim()) params.set('search', search.trim());
    try {
      const data = await api(`/auto-catalog/marks?${params.toString()}`);
      if (markSearchRequestRef.current === requestId) setMarks(data.marks || []);
    } catch {
      if (markSearchRequestRef.current === requestId) setMarks([]);
    }
  }

  useEffect(() => {
    if (!selection.markId) {
      setModels([]);
      return;
    }
    setLoading(true);
    api(`/auto-catalog/models?markId=${encodeURIComponent(selection.markId)}&limit=160`)
      .then((data) => setModels(data.models || []))
      .catch(() => setModels([]))
      .finally(() => setLoading(false));
  }, [selection.markId]);

  useEffect(() => {
    if (!selection.modelId) {
      setGenerations([]);
      return;
    }
    setLoading(true);
    api(`/auto-catalog/generations?modelId=${encodeURIComponent(selection.modelId)}&limit=120`)
      .then((data) => setGenerations(data.generations || []))
      .catch(() => setGenerations([]))
      .finally(() => setLoading(false));
  }, [selection.modelId]);

  useEffect(() => {
    if (!includeDetails) {
      setConfigurations([]);
      setModifications([]);
      return;
    }
    if (!selection.generationId) {
      setConfigurations([]);
      setModifications([]);
      return;
    }
    setLoading(true);
    Promise.all([
      api(`/auto-catalog/configurations?generationId=${encodeURIComponent(selection.generationId)}&limit=40`),
      api(`/auto-catalog/modifications?generationId=${encodeURIComponent(selection.generationId)}&limit=220`)
    ])
      .then(([configs, mods]) => {
        setConfigurations(configs.configurations || []);
        setModifications(mods.modifications || []);
      })
      .catch(() => {
        setConfigurations([]);
        setModifications([]);
      })
      .finally(() => setLoading(false));
  }, [selection.generationId]);

  useEffect(() => {
    if (!includeDetails) return;
    if (!selection.configurationId || !selection.generationId) return;
    setLoading(true);
    api(
      `/auto-catalog/modifications?generationId=${encodeURIComponent(selection.generationId)}&configurationId=${encodeURIComponent(selection.configurationId)}&limit=220`
    )
      .then((data) => setModifications(data.modifications || []))
      .catch(() => setModifications([]))
      .finally(() => setLoading(false));
  }, [selection.configurationId, selection.generationId]);

  function updateSelection(patch) {
    setSelection((current) => ({ ...current, ...patch }));
  }

  function selectMark(markId) {
    setSelection({
      markId,
      modelId: '',
      generationId: '',
      configurationId: '',
      modificationId: ''
    });
  }

  function selectModel(modelId) {
    setSelection((current) => ({
      ...current,
      modelId,
      generationId: '',
      configurationId: '',
      modificationId: ''
    }));
  }

  function selectGeneration(generationId) {
    setSelection((current) => ({
      ...current,
      generationId,
      configurationId: '',
      modificationId: ''
    }));
  }

  function selectConfiguration(configurationId) {
    setSelection((current) => ({
      ...current,
      configurationId,
      modificationId: ''
    }));
  }

  function reset() {
    setSelection(emptyVehicleSelection());
    setModels([]);
    setGenerations([]);
    setConfigurations([]);
    setModifications([]);
  }

  return {
    selection,
    marks,
    models,
    generations,
    configurations,
    modifications,
    loading,
    searchMarks,
    updateSelection,
    selectMark,
    selectModel,
    selectGeneration,
    selectConfiguration,
    reset
  };
}

function defaultVehicleItemLabel(item) {
  return item.name || '';
}

function SearchableVehicleField({
  label,
  value,
  items,
  placeholder,
  onPick,
  onSearch,
  getLabel = defaultVehicleItemLabel,
  disabled = false,
  loading = false,
  emptyText = 'Ничего не найдено'
}) {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const fieldId = useId();
  const inputId = `${fieldId}-input`;
  const labelId = `${fieldId}-label`;
  const listboxId = `${fieldId}-listbox`;
  const selectedItem = items.find((item) => item.id === value) || null;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const lastCommittedValueRef = useRef('');

  useEffect(() => {
    const nextCommittedValue = selectedItem ? getLabel(selectedItem) : '';
    if (lastCommittedValueRef.current === nextCommittedValue) return;
    lastCommittedValueRef.current = nextCommittedValue;
    setQuery(nextCommittedValue);
  }, [selectedItem]);

  useEffect(() => {
    function handleOutside(event) {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
        setActiveIndex(-1);
        setQuery(selectedItem ? getLabel(selectedItem) : '');
        lastCommittedValueRef.current = selectedItem ? getLabel(selectedItem) : '';
      }
    }
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('focusin', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('focusin', handleOutside);
    };
  }, [selectedItem]);

  const filteredItems = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return items.slice(0, 80);
    return items.filter((item) => getLabel(item).toLowerCase().includes(normalized)).slice(0, 80);
  }, [items, query, getLabel]);

  useEffect(() => {
    if (!open || activeIndex < filteredItems.length) return;
    setActiveIndex(filteredItems.length ? filteredItems.length - 1 : -1);
  }, [activeIndex, filteredItems.length, open]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    document.getElementById(`${listboxId}-option-${activeIndex}`)?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, listboxId, open]);

  function handleChange(nextValue) {
    setQuery(nextValue);
    setOpen(true);
    setActiveIndex(-1);
    onSearch?.(nextValue);
    if (value) {
      lastCommittedValueRef.current = '';
      onPick('');
    }
  }

  function handlePick(item) {
    const nextValue = getLabel(item);
    setQuery(nextValue);
    lastCommittedValueRef.current = nextValue;
    onPick(item.id);
    setOpen(false);
    setActiveIndex(-1);
    window.requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
  }

  function closeAndRestore() {
    setOpen(false);
    setActiveIndex(-1);
    const committedValue = selectedItem ? getLabel(selectedItem) : '';
    setQuery(committedValue);
    lastCommittedValueRef.current = committedValue;
  }

  function handleKeyDown(event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => (filteredItems.length ? (current + 1) % filteredItems.length : -1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) =>
        filteredItems.length ? (current <= 0 ? filteredItems.length - 1 : current - 1) : -1
      );
      return;
    }
    if (event.key === 'Enter' && open && activeIndex >= 0 && filteredItems[activeIndex]) {
      event.preventDefault();
      handlePick(filteredItems[activeIndex]);
      return;
    }
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      event.stopPropagation();
      closeAndRestore();
    }
  }

  return (
    <div className={`vehicle-field ${disabled ? 'is-disabled' : ''}`}>
      <label className="vehicle-field-label" id={labelId} htmlFor={inputId}>
        {label}
      </label>
      <div className="vehicle-combobox" ref={rootRef}>
        <div className={`vehicle-combobox-shell ${open ? 'open' : ''} ${value ? 'has-value' : ''}`}>
          <Search size={16} aria-hidden="true" />
          <input
            ref={inputRef}
            id={inputId}
            value={query}
            placeholder={placeholder}
            disabled={disabled}
            role="combobox"
            autoComplete="off"
            aria-autocomplete="list"
            aria-labelledby={labelId}
            aria-expanded={open}
            aria-controls={listboxId}
            aria-activedescendant={
              open && activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined
            }
            aria-busy={loading || undefined}
            onFocus={() => {
              if (disabled) return;
              setOpen(true);
              setActiveIndex(filteredItems.findIndex((item) => item.id === value));
            }}
            onChange={(event) => handleChange(event.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              window.requestAnimationFrame(() => {
                if (!rootRef.current?.contains(document.activeElement)) closeAndRestore();
              });
            }}
          />
          {loading ? (
            <RefreshCw className="spin" size={16} aria-hidden="true" />
          ) : value ? (
            <button
              type="button"
              className="vehicle-combobox-clear"
              onClick={() => {
                setQuery('');
                lastCommittedValueRef.current = '';
                onSearch?.('');
                onPick('');
                setOpen(false);
                setActiveIndex(-1);
                window.requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
              }}
              aria-label={`Очистить поле ${label}`}
              title={`Очистить поле ${label}`}
            >
              <X size={15} aria-hidden="true" />
            </button>
          ) : (
            <ChevronRight size={16} className={`vehicle-combobox-caret ${open ? 'open' : ''}`} aria-hidden="true" />
          )}
        </div>
        {open && !disabled && (
          <div className="vehicle-combobox-menu" id={listboxId} role="listbox" aria-labelledby={labelId}>
            {filteredItems.length ? (
              filteredItems.map((item, index) => (
                <div
                  key={item.id}
                  id={`${listboxId}-option-${index}`}
                  role="option"
                  aria-selected={value === item.id}
                  className={`vehicle-combobox-option ${value === item.id ? 'selected' : ''} ${activeIndex === index ? 'active' : ''}`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                  }}
                  onMouseMove={() => setActiveIndex(index)}
                  onClick={() => handlePick(item)}
                >
                  <b>{getLabel(item)}</b>
                </div>
              ))
            ) : (
              <div className="vehicle-combobox-empty" role="status">{emptyText}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function VehicleFinderPanel({ selector, appliedSelection, onApply, onReset, loading, resultCount = 0, autoApplyOnGeneration = false }) {
  const [vehicleType, setVehicleType] = useState('cars');
  const activeGeneration = selector.generations.find((item) => item.id === selector.selection.generationId);
  const activeMark = selector.marks.find((item) => item.id === selector.selection.markId);
  const activeModel = selector.models.find((item) => item.id === selector.selection.modelId);
  const activePhotoSrc = activeGeneration ? `/api/auto-catalog/generations/${activeGeneration.id}/photo` : '';
  const activeLogoSrc = activeMark?.id ? `/auto-logos/${activeMark.id}.png` : '';
  const isAppliedGeneration = appliedSelection.generationId && appliedSelection.generationId === activeGeneration?.id;

  return (
    <section className="vehicle-finder">
      <div className="vehicle-finder-head">
        <div>
          <b>Поиск по автомобилю</b>
          <span>Модульный каталог совместимости поверх обычного каталога товаров</span>
        </div>
        <div className="vehicle-type-toggle" role="group" aria-label="Тип транспорта">
          <button
            className={vehicleType === 'cars' ? 'active' : ''}
            onClick={() => setVehicleType('cars')}
            type="button"
            aria-pressed={vehicleType === 'cars'}
          >
            <CarFront size={16} /> Легковые
          </button>
          <button
            className={vehicleType === 'trucks' ? 'active disabled' : 'disabled'}
            onClick={() => setVehicleType('trucks')}
            type="button"
            disabled
            aria-pressed={vehicleType === 'trucks'}
          >
            <Truck size={16} /> Грузовые
          </button>
        </div>
      </div>
      <div className="vehicle-finder-grid">
        <SearchableVehicleField
          label="Марка"
          value={selector.selection.markId}
          items={selector.marks}
          placeholder="Начни вводить марку"
          onPick={selector.selectMark}
          onSearch={selector.searchMarks}
          loading={loading && !selector.selection.markId}
        />
        <SearchableVehicleField
          label="Модель"
          value={selector.selection.modelId}
          items={selector.models}
          placeholder={selector.selection.markId ? 'Начни вводить модель' : 'Сначала выбери марку'}
          onPick={selector.selectModel}
          disabled={!selector.selection.markId}
          loading={loading && Boolean(selector.selection.markId) && !selector.selection.modelId}
        />
        <SearchableVehicleField
          label="Поколение"
          value={selector.selection.generationId}
          items={selector.generations}
          placeholder={selector.selection.modelId ? 'Начни вводить поколение' : 'Сначала выбери модель'}
          onPick={selector.selectGeneration}
          getLabel={generationOptionLabel}
          disabled={!selector.selection.modelId}
          loading={loading && Boolean(selector.selection.modelId) && !selector.selection.generationId}
        />
        <button
          type="button"
          className="vehicle-finder-submit"
          onClick={onApply}
          disabled={!selector.selection.generationId}
        >
          {loading ? <RefreshCw className="spin" size={18} /> : 'Подобрать'}
        </button>
      </div>
      {activeGeneration && (
        <div className="vehicle-finder-subgrid vehicle-finder-subgrid-simple">
          <div className="vehicle-generation-summary vehicle-generation-summary-rich">
            <div className="vehicle-generation-media">
              <div className="vehicle-generation-photo">
                {activePhotoSrc ? (
                  <img
                    src={activePhotoSrc}
                    alt={generationOptionLabel(activeGeneration)}
                    onError={(event) => {
                      event.currentTarget.style.display = 'none';
                      const fallback = event.currentTarget.parentElement?.querySelector('.vehicle-generation-photo-fallback');
                      if (fallback) fallback.hidden = false;
                    }}
                  />
                ) : null}
                <div className="vehicle-generation-photo-fallback" hidden={Boolean(activePhotoSrc)}>
                  <CarFront size={22} />
                </div>
              </div>
              <div className="vehicle-generation-logo">
                {activeLogoSrc ? <img src={activeLogoSrc} alt={activeMark?.name || ''} /> : <CarFront size={16} />}
              </div>
            </div>
            <div className="vehicle-generation-copy">
              <b>{activeGeneration.name}</b>
              <span>{activeMark?.name && activeModel?.name ? `${activeMark.name} ${activeModel.name}` : 'Выбранное поколение'}</span>
              <small>
                {yearRangeLabel(activeGeneration.yearFrom, activeGeneration.yearTo)}
                {isAppliedGeneration
                  ? ` • ${resultCount} товаров в выдаче`
                  : autoApplyOnGeneration
                    ? ' • Выдача запускается автоматически'
                    : ' • Нажми "Подобрать" для выдачи'}
              </small>
            </div>
          </div>
          <button type="button" className="vehicle-finder-reset" onClick={onReset}>
            Сбросить
          </button>
        </div>
      )}
      {appliedSelection.markId && (
        <div className="vehicle-finder-applied">
          <span>Фильтр активен</span>
          <b>
            {selector.marks.find((item) => item.id === appliedSelection.markId)?.name || 'Марка'}
            {appliedSelection.modelId
              ? ` / ${selector.models.find((item) => item.id === appliedSelection.modelId)?.name || 'Модель'}`
              : ''}
            {appliedSelection.generationId
              ? ` / ${selector.generations.find((item) => item.id === appliedSelection.generationId)?.name || 'Поколение'}`
              : ''}
          </b>
        </div>
      )}
    </section>
  );
}

function Catalog({ cartQtyById, onSetCartQty, navigate, catalogState, setCatalogState }) {
  const isMobile = useMediaQuery('(max-width: 900px)');
  const isCatalogNarrow = useMediaQuery('(max-width: 1280px)');
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [productCount, setProductCount] = useState(0);
  const { query, activeCategory, browseParent } = catalogState;
  const page = Math.max(1, Number(catalogState.page || 1));
  const [productsStatus, setProductsStatus] = useState('loading');
  const [productsError, setProductsError] = useState('');
  const [productsRetryKey, setProductsRetryKey] = useState(0);
  const [categoriesStatus, setCategoriesStatus] = useState('loading');
  const [categoriesError, setCategoriesError] = useState('');
  const [categoriesRetryKey, setCategoriesRetryKey] = useState(0);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    const savedMode = window.localStorage.getItem(CATALOG_VIEW_STORAGE_KEY);
    return savedMode || 'table';
  });
  const [preview, setPreview] = useState(null);
  const previewTimerRef = useRef(null);
  const restoredScrollKeyRef = useRef('');
  const mobileFiltersDialogRef = useRef(null);
  const mobileFiltersCloseRef = useRef(null);
  const loading = productsStatus === 'loading';

  useModalDialog({
    open: mobileFiltersOpen && isCatalogNarrow,
    onClose: () => setMobileFiltersOpen(false),
    dialogRef: mobileFiltersDialogRef,
    initialFocusRef: mobileFiltersCloseRef
  });

  const categoryIndex = useMemo(() => {
    const byId = new Map(categories.map((category) => [category.id, category]));
    const childrenByParent = new Map();
    for (const category of categories) {
      const parentId = category.parentId || category.parent_id || '';
      if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
      childrenByParent.get(parentId).push(category);
    }
    for (const children of childrenByParent.values()) {
      children.sort((a, b) => (a.sort || 0) - (b.sort || 0) || a.name.localeCompare(b.name, 'ru'));
    }
    return { byId, childrenByParent };
  }, [categories]);

  const visibleCategories = categoryIndex.childrenByParent.get(browseParent) || [];
  const currentCategory = browseParent ? categoryIndex.byId.get(browseParent) : null;
  const activeCategoryRow = activeCategory ? categoryIndex.byId.get(activeCategory) : null;
  const breadcrumbs = useMemo(() => {
    const chain = [];
    let cursor = currentCategory;
    const seen = new Set();
    while (cursor && !seen.has(cursor.id)) {
      seen.add(cursor.id);
      chain.unshift(cursor);
      cursor = cursor.parentId || cursor.parent_id ? categoryIndex.byId.get(cursor.parentId || cursor.parent_id) : null;
    }
    return chain;
  }, [currentCategory, categoryIndex]);

  useEffect(() => {
    let cancelled = false;
    setCategoriesStatus('loading');
    setCategoriesError('');
    api('/catalog/categories')
      .then((data) => {
        if (cancelled) return;
        setCategories(data.categories || []);
        setCategoriesStatus('success');
      })
      .catch((err) => {
        if (cancelled) return;
        setCategories([]);
        setCategoriesError(err.message);
        setCategoriesStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [categoriesRetryKey]);

  useEffect(() => {
    let cancelled = false;
    setProductsLoaded(false);
    setProducts([]);
    setProductCount(0);
    setProductsError('');
    setProductsStatus('loading');
    const timer = setTimeout(async () => {
      try {
        const data = await api(
          `/catalog/products?search=${encodeURIComponent(query)}&categoryId=${encodeURIComponent(activeCategory)}&page=${page}&limit=${CATALOG_PAGE_LIMIT}`
        );
        if (cancelled) return;
        const nextProducts = data.products || [];
        setProducts(nextProducts);
        setProductCount(data.count || nextProducts.length);
        setProductsStatus('success');
      } catch (err) {
        if (cancelled) return;
        setProductsError(err.message);
        setProductsStatus('error');
      } finally {
        if (cancelled) return;
        setProductsLoaded(true);
      }
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, activeCategory, page, productsRetryKey]);

  useEffect(() => {
    window.localStorage.setItem(CATALOG_VIEW_STORAGE_KEY, viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (!isCatalogNarrow) {
      setMobileFiltersOpen(false);
    }
  }, [isCatalogNarrow]);

  useEffect(() => () => {
    if (previewTimerRef.current) window.clearTimeout(previewTimerRef.current);
  }, []);

  useEffect(() => {
    const scrollY = Number(catalogState.scrollY || 0);
    if (!productsLoaded || scrollY <= 0) return;
    const restoreKey = `${query}|${activeCategory}|${browseParent}|${page}|${scrollY}`;
    if (restoredScrollKeyRef.current === restoreKey) return;
    restoredScrollKeyRef.current = restoreKey;
    window.requestAnimationFrame(() => {
      const maxScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      window.scrollTo({ top: Math.min(scrollY, maxScrollY), behavior: 'auto' });
    });
  }, [activeCategory, browseParent, catalogState.scrollY, page, productsLoaded, products.length, query]);

  function updateCatalogState(patch) {
    setCatalogState((current) => ({ ...current, ...patch }));
  }

  function resetCategories() {
    updateCatalogState({ activeCategory: '', browseParent: '', page: 1, scrollY: 0 });
    setMobileFiltersOpen(false);
  }

  function openCategory(category) {
    if (category.child_count > 0) {
      updateCatalogState({ activeCategory: category.id, browseParent: category.id, page: 1, scrollY: 0 });
      return;
    }
    updateCatalogState({
      activeCategory: category.id,
      browseParent: category.parentId || category.parent_id || '',
      page: 1,
      scrollY: 0
    });
    setMobileFiltersOpen(false);
  }

  function openBreadcrumb(category) {
    updateCatalogState({ activeCategory: category.id, browseParent: category.id, page: 1, scrollY: 0 });
    if (isCatalogNarrow) setMobileFiltersOpen(false);
  }

  function goUp() {
    if (!currentCategory) return resetCategories();
    const parentId = currentCategory.parentId || currentCategory.parent_id || '';
    updateCatalogState({ activeCategory: parentId, browseParent: parentId, page: 1, scrollY: 0 });
  }

  function changePage(nextPage) {
    const totalPages = Math.max(1, Math.ceil(productCount / CATALOG_PAGE_LIMIT));
    const safePage = Math.max(1, Math.min(totalPages, Number(nextPage || 1)));
    if (safePage === page) return;
    updateCatalogState({ page: safePage, scrollY: 0 });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function openProduct(productId) {
    updateCatalogState({ scrollY: window.scrollY });
    navigate({ name: 'product', productId });
  }

  function placePreview(event, product) {
    const width = 380;
    const height = 380;
    const margin = 24;
    const nextX = Math.min(event.clientX + margin, window.innerWidth - width - 16);
    const nextY = Math.min(event.clientY + margin, window.innerHeight - height - 16);
    return {
      product,
      x: Math.max(16, nextX),
      y: Math.max(16, nextY)
    };
  }

  const previewHandlers = {
    onEnter(event, product) {
      if (!product.imageUrl) return;
      if (previewTimerRef.current) window.clearTimeout(previewTimerRef.current);
      previewTimerRef.current = window.setTimeout(() => {
        setPreview(placePreview(event, product));
      }, 1000);
    },
    onMove(event) {
      setPreview((current) => (current ? { ...current, ...placePreview(event, current.product) } : current));
    },
    onLeave() {
      if (previewTimerRef.current) window.clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
      setPreview(null);
    }
  };

  const viewOptions = [
    { id: 'comfortable', label: 'Комфорт', icon: GalleryVerticalEnd },
    { id: 'compact', label: 'Плотно', icon: Grip },
    { id: 'table', label: 'Таблица', icon: ClipboardList }
  ];
  const currentViewMode = isMobile ? 'compact' : viewMode;
  const hasQuery = query.trim().length > 0;
  const isEmptySearchResult = productsStatus === 'success' && hasQuery && products.length === 0;
  const isEmptyCatalogResult = productsStatus === 'success' && products.length === 0;
  const hasCatalogProducts = productsStatus === 'success' && products.length > 0;

  const sidebarContent = (
    <>
      <div className="category-breadcrumbs">
        <button className={!activeCategory ? 'selected' : ''} onClick={resetCategories}>
          Все
        </button>
        {breadcrumbs.map((category) => (
          <React.Fragment key={category.id}>
            <ChevronRight size={13} />
            <button
              className={category.id === activeCategory ? 'selected' : ''}
              onClick={() => openBreadcrumb(category)}
            >
              {category.name}
            </button>
          </React.Fragment>
        ))}
      </div>
      {currentCategory && (
        <button className="category-back" onClick={goUp}>
          <ArrowLeft size={16} /> Назад
        </button>
      )}
      <div className="category-scope">
        <b>{activeCategoryRow?.name || 'Все товары'}</b>
        <span>{activeCategoryRow?.product_count || productCount} поз.</span>
      </div>
      {categoriesStatus === 'loading' ? (
        <LoadingState variant="panel" count={2} label="Загружаем разделы каталога…" />
      ) : null}
      {categoriesStatus === 'error' ? (
        <ErrorState
          compact
          title="Разделы не загрузились"
          message={categoriesError}
          onRetry={() => setCategoriesRetryKey((value) => value + 1)}
        />
      ) : null}
      {categoriesStatus === 'success' ? visibleCategories.map((category) => (
        <button
          key={category.id}
          className={`category-row ${activeCategory === category.id ? 'selected' : ''}`}
          onClick={() => openCategory(category)}
        >
          <CategoryIcon name={category.name} />
          <span>{category.name}</span>
          <small>{category.product_count || ''}</small>
          {category.child_count > 0 && <ChevronRight className="category-next" size={16} />}
        </button>
      )) : null}
      {categoriesStatus === 'success' && !visibleCategories.length && (
        <div className="category-empty">Ниже подкатегорий нет, показываю товары выбранного раздела.</div>
      )}
    </>
  );

  return (
    <main className={`catalog-layout ${isCatalogNarrow ? 'catalog-narrow-layout' : ''} ${isMobile ? 'mobile-layout' : ''}`}>
      <h1 className="visually-hidden">Каталог товаров</h1>
      {!isCatalogNarrow && <aside className="sidebar">{sidebarContent}</aside>}
      <section className="workspace">
        <div className="searchbar">
          <Search size={20} />
          <input
            value={query}
            placeholder="Название, артикул, авто, штрихкод"
            aria-label="Поиск по каталогу"
            onChange={(event) => updateCatalogState({ query: event.target.value, page: 1, scrollY: 0 })}
          />
          <span className="searchbar-status" aria-hidden="true">
            {loading ? <RefreshCw className="spin" size={18} /> : null}
          </span>
        </div>
        {isCatalogNarrow && (
          <div className="catalog-mobile-strip">
            <button
              className="mobile-filter-trigger"
              onClick={() => setMobileFiltersOpen(true)}
              aria-expanded={mobileFiltersOpen}
              aria-controls="catalog-sections-dialog"
            >
              <Package size={18} aria-hidden="true" />
              <div className="mobile-filter-trigger-copy">
                <b>{activeCategoryRow?.name || 'Все товары'}</b>
                <span>Разделы и быстрый переход</span>
              </div>
              <ChevronRight size={16} aria-hidden="true" />
            </button>
            <div className="mobile-filter-count">{loading ? '…' : productCount} поз.</div>
          </div>
        )}
        <div className="catalog-toolbar">
          <div className="catalog-summary">
            <b>{activeCategoryRow?.name || 'Каталог'}</b>
            <span>
              {productsStatus === 'loading'
                ? 'Загружаем выдачу…'
                : productsStatus === 'error'
                  ? 'Выдача временно недоступна'
                  : isEmptySearchResult
                    ? 'Ничего не найдено'
                    : `${productCount} позиций в выдаче`}
            </span>
          </div>
          {!isMobile && (
            <div className="view-switch" role="group" aria-label="Режим каталога">
              {viewOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.id}
                    className={currentViewMode === option.id ? 'active' : ''}
                    onClick={() => setViewMode(option.id)}
                    aria-pressed={currentViewMode === option.id}
                  >
                    <Icon size={15} aria-hidden="true" />
                    {option.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {productsStatus === 'loading' ? (
          <LoadingState variant={currentViewMode} count={currentViewMode === 'table' ? 8 : 6} label="Загружаем товары…" />
        ) : null}
        {productsStatus === 'error' ? (
          <ErrorState
            title="Не удалось загрузить товары"
            message={productsError}
            onRetry={() => setProductsRetryKey((value) => value + 1)}
          />
        ) : null}
        {hasCatalogProducts && currentViewMode === 'comfortable' && (
          <div className="products-grid comfortable">
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                qty={cartQtyById[product.id] || 0}
                onQtyChange={(qty) => onSetCartQty(product, qty)}
                onOpen={openProduct}
                previewHandlers={previewHandlers}
              />
            ))}
          </div>
        )}
        {hasCatalogProducts && currentViewMode === 'compact' && (
          <div className="products-compact-list">
            {products.map((product) => (
              <ProductCompactRow
                key={product.id}
                product={product}
                qty={cartQtyById[product.id] || 0}
                onQtyChange={(qty) => onSetCartQty(product, qty)}
                onOpen={openProduct}
                previewHandlers={previewHandlers}
              />
            ))}
          </div>
        )}
        {hasCatalogProducts && currentViewMode === 'table' && (
          <ProductTable
            products={products}
            cartQtyById={cartQtyById}
            onSetCartQty={onSetCartQty}
            onOpen={openProduct}
            previewHandlers={previewHandlers}
          />
        )}
        {isEmptyCatalogResult ? (
          <EmptyState
            title={hasQuery ? 'Ничего не найдено' : 'В разделе пока нет товаров'}
            description={
              hasQuery
                ? 'Попробуйте ввести артикул, часть названия, OEM или штрихкод.'
                : 'Вернитесь ко всему каталогу или выберите соседний раздел.'
            }
            actionLabel={hasQuery ? 'Сбросить поиск' : activeCategory ? 'Все товары' : undefined}
            onAction={
              hasQuery
                ? () => updateCatalogState({ query: '', page: 1, scrollY: 0 })
                : activeCategory
                  ? resetCategories
                  : undefined
            }
          />
        ) : null}
        {hasCatalogProducts && (
          <CatalogPager
            page={page}
            total={productCount}
            limit={CATALOG_PAGE_LIMIT}
            itemCount={products.length}
            onPageChange={changePage}
          />
        )}
        {preview && (
          <div className="product-hover-preview" style={{ left: `${preview.x}px`, top: `${preview.y}px` }}>
            <ProductImage src={preview.product.imageUrl} alt="" loading="eager" />
            <div className="product-hover-preview-meta">
              <b>{preview.product.vendorCode}</b>
              <span>{preview.product.name}</span>
            </div>
          </div>
        )}
      </section>
      {isCatalogNarrow && (
        <>
          <button
            className={`mobile-sidebar-backdrop ${mobileFiltersOpen ? 'open' : ''}`}
            onClick={() => setMobileFiltersOpen(false)}
            data-dialog-backdrop
            aria-hidden="true"
            tabIndex={-1}
          />
          <aside
            className={`sidebar mobile-sidebar ${mobileFiltersOpen ? 'open' : ''}`}
            id="catalog-sections-dialog"
            ref={mobileFiltersDialogRef}
            role="dialog"
            aria-modal={mobileFiltersOpen || undefined}
            aria-labelledby="catalog-sections-title"
            aria-hidden={!mobileFiltersOpen}
            inert={!mobileFiltersOpen}
            tabIndex={-1}
          >
            <div className="mobile-sidebar-header">
              <div>
                <b id="catalog-sections-title">Разделы каталога</b>
                <span>{activeCategoryRow?.name || 'Выберите ветку для точной выдачи'}</span>
              </div>
              <button
                ref={mobileFiltersCloseRef}
                className="mobile-sidebar-close"
                onClick={() => setMobileFiltersOpen(false)}
                aria-label="Закрыть разделы каталога"
                title="Закрыть разделы каталога"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>
            {sidebarContent}
          </aside>
        </>
      )}
    </main>
  );
}

function AutoCatalogPage({ cartQtyById, onSetCartQty, navigate }) {
  const isMobile = useMediaQuery('(max-width: 900px)');
  const selector = useAutoCatalogSelector({ includeDetails: false });
  const [products, setProducts] = useState([]);
  const [productCount, setProductCount] = useState(0);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [productsError, setProductsError] = useState('');
  const [productsRetryKey, setProductsRetryKey] = useState(0);
  const [appliedSelection, setAppliedSelection] = useState(emptyVehicleSelection());
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    const savedMode = window.localStorage.getItem('auto-catalog-view-mode-v1');
    return savedMode || 'comfortable';
  });
  const [preview, setPreview] = useState(null);
  const previewTimerRef = useRef(null);

  useEffect(() => {
    window.localStorage.setItem('auto-catalog-view-mode-v1', viewMode);
  }, [viewMode]);

  useEffect(() => () => {
    if (previewTimerRef.current) window.clearTimeout(previewTimerRef.current);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!appliedSelection.generationId) {
      setProducts([]);
      setProductCount(0);
      setSelectedVehicle(null);
      setProductsError('');
      setLoading(false);
      return;
    }
    setLoading(true);
    setProductsError('');
    setProducts([]);
    setProductCount(0);
    const timer = window.setTimeout(async () => {
      try {
        const fitment = fitmentQuery(appliedSelection);
        const data = await api(`/catalog/products?search=${encodeURIComponent(query)}&limit=48&${fitment}`);
        if (cancelled) return;
        setProducts(data.products || []);
        setProductCount(data.count || 0);
        setSelectedVehicle(data.selectedVehicle || null);
      } catch (err) {
        if (cancelled) return;
        setProductsError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [appliedSelection, query, productsRetryKey]);

  useEffect(() => {
    if (!selector.selection.generationId) {
      setAppliedSelection(emptyVehicleSelection());
      return;
    }
    setAppliedSelection((current) => {
      const nextSelection = {
        markId: selector.selection.markId,
        modelId: selector.selection.modelId,
        generationId: selector.selection.generationId,
        configurationId: '',
        modificationId: ''
      };
      if (
        current.markId === nextSelection.markId &&
        current.modelId === nextSelection.modelId &&
        current.generationId === nextSelection.generationId &&
        current.configurationId === nextSelection.configurationId &&
        current.modificationId === nextSelection.modificationId
      ) {
        return current;
      }
      return nextSelection;
    });
  }, [selector.selection.markId, selector.selection.modelId, selector.selection.generationId]);

  function applySelection() {
    setAppliedSelection({
      markId: selector.selection.markId,
      modelId: selector.selection.modelId,
      generationId: selector.selection.generationId,
      configurationId: '',
      modificationId: ''
    });
  }

  function resetSelection() {
    selector.reset();
    setAppliedSelection(emptyVehicleSelection());
    setProducts([]);
    setProductCount(0);
    setSelectedVehicle(null);
    setProductsError('');
  }

  function placePreview(event, product) {
    const width = 380;
    const height = 380;
    const margin = 24;
    const nextX = Math.min(event.clientX + margin, window.innerWidth - width - 16);
    const nextY = Math.min(event.clientY + margin, window.innerHeight - height - 16);
    return {
      product,
      x: Math.max(16, nextX),
      y: Math.max(16, nextY)
    };
  }

  const previewHandlers = {
    onEnter(event, product) {
      if (!product.imageUrl) return;
      if (previewTimerRef.current) window.clearTimeout(previewTimerRef.current);
      previewTimerRef.current = window.setTimeout(() => {
        setPreview(placePreview(event, product));
      }, 1000);
    },
    onMove(event) {
      setPreview((current) => (current ? { ...current, ...placePreview(event, current.product) } : current));
    },
    onLeave() {
      if (previewTimerRef.current) window.clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
      setPreview(null);
    }
  };

  const viewOptions = [
    { id: 'comfortable', label: 'Комфорт', icon: GalleryVerticalEnd },
    { id: 'compact', label: 'Плотно', icon: Grip },
    { id: 'table', label: 'Таблица', icon: ClipboardList }
  ];
  const currentViewMode = isMobile ? 'compact' : viewMode;
  const generationLabel =
    selector.generations.find((item) => item.id === appliedSelection.generationId)?.name ||
    selectedVehicle?.generationName ||
    '';
  const autoProductsReady = !loading && !productsError;
  const hasAutoProducts = autoProductsReady && products.length > 0;

  return (
    <main className="page auto-page">
      <h1 className="visually-hidden">Подбор товаров по автомобилю</h1>
      <section className="auto-page-hero">
        <VehicleFinderPanel
          selector={selector}
          appliedSelection={appliedSelection}
          onApply={applySelection}
          onReset={resetSelection}
          loading={loading || selector.loading}
          resultCount={productCount}
          autoApplyOnGeneration
        />
      </section>

      {appliedSelection.generationId ? (
        <section className="table-panel auto-results-panel">
          <div className="auto-disclaimer">
            <b>ВНИМАНИЕ</b>
            <span>
              Не все товары могли быть верно разобраны по каталогу. Если не нашли товар здесь, воспользуйтесь
              поиском на главной и попробуйте найти товар вручную.
            </span>
          </div>
          <div className="searchbar">
            <Search size={20} />
            <input
              value={query}
              placeholder="Поиск внутри выбранного поколения"
              aria-label="Поиск товаров для выбранного автомобиля"
              onChange={(event) => setQuery(event.target.value)}
            />
            <span className="searchbar-status" aria-hidden="true">
              {loading ? <RefreshCw className="spin" size={18} /> : null}
            </span>
          </div>
          <div className="catalog-toolbar">
            <div className="catalog-summary">
              <b>{generationLabel}</b>
              <span>
                {loading
                  ? 'Загружаем совместимые товары…'
                  : productsError
                    ? 'Выдача временно недоступна'
                    : `${productCount} совместимых товаров`}
              </span>
            </div>
            {!isMobile && (
              <div className="view-switch" role="group" aria-label="Режим выдачи">
                {viewOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.id}
                      className={currentViewMode === option.id ? 'active' : ''}
                      onClick={() => setViewMode(option.id)}
                      aria-pressed={currentViewMode === option.id}
                    >
                      <Icon size={15} aria-hidden="true" />
                      {option.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {loading ? (
            <LoadingState variant={currentViewMode} count={6} label="Загружаем товары для выбранного автомобиля…" />
          ) : null}
          {productsError ? (
            <ErrorState
              title="Не удалось выполнить подбор"
              message={productsError}
              onRetry={() => setProductsRetryKey((value) => value + 1)}
            />
          ) : null}
          {hasAutoProducts && currentViewMode === 'comfortable' && (
            <div className="products-grid comfortable">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  qty={cartQtyById[product.id] || 0}
                  onQtyChange={(qty) => onSetCartQty(product, qty)}
                  onOpen={(id) => navigate({ name: 'product', productId: id })}
                  previewHandlers={previewHandlers}
                />
              ))}
            </div>
          )}
          {hasAutoProducts && currentViewMode === 'compact' && (
            <div className="products-compact-list">
              {products.map((product) => (
                <ProductCompactRow
                  key={product.id}
                  product={product}
                  qty={cartQtyById[product.id] || 0}
                  onQtyChange={(qty) => onSetCartQty(product, qty)}
                  onOpen={(id) => navigate({ name: 'product', productId: id })}
                  previewHandlers={previewHandlers}
                />
              ))}
            </div>
          )}
          {hasAutoProducts && currentViewMode === 'table' && (
            <ProductTable
              products={products}
              cartQtyById={cartQtyById}
              onSetCartQty={onSetCartQty}
              onOpen={(id) => navigate({ name: 'product', productId: id })}
              previewHandlers={previewHandlers}
            />
          )}
          {autoProductsReady && !products.length ? (
            <EmptyState
              title="Для этого поколения товаров не найдено"
              description={query ? 'Сбросьте поиск или попробуйте найти товар в общем каталоге.' : 'Попробуйте другое поколение или откройте общий каталог.'}
              actionLabel={query ? 'Сбросить поиск' : 'Открыть каталог'}
              onAction={query ? () => setQuery('') : () => navigate('/')}
            />
          ) : null}
          {preview && (
            <div className="product-hover-preview" style={{ left: `${preview.x}px`, top: `${preview.y}px` }}>
              <ProductImage src={preview.product.imageUrl} alt="" loading="eager" />
              <div className="product-hover-preview-meta">
                <b>{preview.product.vendorCode}</b>
                <span>{preview.product.name}</span>
              </div>
            </div>
          )}
        </section>
      ) : (
        <section className="table-panel auto-results-panel">
          <EmptyState
            compact
            title="Выберите автомобиль"
            description="Укажите марку, модель и поколение выше — после этого здесь появятся совместимые товары."
          />
        </section>
      )}
    </main>
  );
}

function Cart({ cart, cartStatus, cartLoadError, user, onSetCartQty, onCheckout, onRefresh, refreshing, navigate }) {
  const [comment, setComment] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState(user.deliveryMethod || '');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setDeliveryMethod(user.deliveryMethod || '');
  }, [user.deliveryMethod]);

  async function checkout() {
    setError('');
    const data = await onCheckout(comment, deliveryMethod);
    setMessage(`Заказ ${data.order.order_number} создан`);
    setComment('');
  }

  return (
    <main className="page cart-page">
      <section className="table-panel">
        <div className="panel-title">
          <h1>Корзина</h1>
          <div className="cart-title-actions">
            <IconButton
              icon={refreshing ? RefreshCw : RefreshCw}
              onClick={onRefresh}
              disabled={refreshing}
              className={refreshing ? 'is-busy' : ''}
            >
              Обновить
            </IconButton>
            <strong>{money(cart.total)}</strong>
          </div>
        </div>
        {cartStatus === 'loading' || cartStatus === 'idle' ? (
          <LoadingState variant="panel" count={3} label="Загружаем корзину…" />
        ) : null}
        {cartStatus === 'error' ? (
          <ErrorState title="Не удалось загрузить корзину" message={cartLoadError} onRetry={onRefresh} />
        ) : null}
        {cartStatus === 'success' && cart.items.length > 0 ? (
          <div className="cart-list">
            {cart.items.map((item) => (
            <div key={item.product.id} className="cart-row">
              <button className="cart-product" onClick={() => navigate({ name: 'product', productId: item.product.id })}>
                <b>{item.product.name}</b>
                <span>{item.product.vendorCode}</span>
              </button>
              <div className="cart-cell cart-cell-price">
                <small>Цена</small>
                <strong>{money(item.product.price)}</strong>
              </div>
              <div className="cart-cell cart-cell-stock">
                <small>Остаток</small>
                <span className={item.product.stock > 0 ? 'stock ok' : 'stock'}>
                  {item.product.stock} шт.
                </span>
              </div>
              <div className="cart-cell cart-cell-qty">
                <small>Кол-во</small>
                <QuantityControl
                  qty={item.qty}
                  max={item.product.stock}
                  onChange={(qty) => onSetCartQty(item.product, qty)}
                  label={`Количество в корзине для ${item.product.name}, артикул ${item.product.vendorCode}`}
                />
              </div>
              <div className="cart-cell cart-cell-total">
                <small>Сумма</small>
                <strong>{money(item.total)}</strong>
              </div>
            </div>
            ))}
          </div>
        ) : cartStatus === 'success' ? (
          <EmptyState
            title="Корзина пуста"
            description="Добавьте товары из каталога — здесь появятся позиции, сумма и форма оформления."
            actionLabel="Перейти в каталог"
            onAction={() => navigate('/')}
          />
        ) : null}
        {cartStatus === 'success' && cart.items.length > 0 ? (
          <div className="cart-actions-panel">
          <label className="cart-delivery-field">
            <span>Способ получения</span>
            <select
              value={deliveryMethod}
              onChange={(event) => setDeliveryMethod(event.target.value)}
              aria-describedby="cart-delivery-hint"
              required
            >
              <option value="" disabled>Выберите способ получения</option>
              {DELIVERY_METHOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <small id="cart-delivery-hint">
              При следующем заказе подставим ваш последний выбор.
            </small>
          </label>
          <textarea
            value={comment}
            aria-label="Комментарий к заказу"
            onChange={(event) => setComment(event.target.value)}
              placeholder="Комментарий к заказу"
            />
            <IconButton
              icon={ClipboardList}
              className="primary cart-checkout-cta"
              disabled={!deliveryMethod}
              onClick={() => checkout().catch((err) => setError(err.message))}
            >
              Оформить заказ
            </IconButton>
          </div>
        ) : null}
        <InlineNotice tone="error">{error}</InlineNotice>
        {cartStatus === 'success' ? <InlineNotice tone="error">{cartLoadError}</InlineNotice> : null}
        <InlineNotice tone="success">{message}</InlineNotice>
      </section>
      {cartStatus === 'success' && cart.items.length > 0 && (
        <div className="cart-mobile-checkout">
          <div className="cart-mobile-checkout-copy">
            <span>Итого</span>
            <strong>{money(cart.total)}</strong>
          </div>
          <IconButton
            icon={ClipboardList}
            className="primary cart-checkout-cta"
            disabled={!deliveryMethod}
            onClick={() => checkout().catch((err) => setError(err.message))}
          >
            Оформить
          </IconButton>
        </div>
      )}
    </main>
  );
}

function CdekCityField({ value, region, onChange, disabled }) {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [query, setQuery] = useState(value || '');
  const [cities, setCities] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shouldSearch, setShouldSearch] = useState(false);

  useEffect(() => {
    setQuery(value || '');
    setOpen(false);
    setShouldSearch(false);
  }, [value]);

  useEffect(() => {
    function closeOutside(event) {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener('mousedown', closeOutside);
    return () => document.removeEventListener('mousedown', closeOutside);
  }, []);

  useEffect(() => {
    if (disabled || !shouldSearch || query.trim().length < 2) {
      setCities([]);
      return undefined;
    }
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const data = await api(`/returns/cities?search=${encodeURIComponent(query.trim())}&limit=12`);
        setCities(data.cities || []);
        setOpen(true);
      } catch {
        setCities([]);
      } finally {
        setLoading(false);
      }
    }, 240);
    return () => window.clearTimeout(timer);
  }, [query, disabled, shouldSearch]);

  function updateRaw(nextValue) {
    setQuery(nextValue);
    setShouldSearch(true);
    onChange({ city: nextValue, region: '', cityCode: null });
    setOpen(nextValue.trim().length >= 2);
  }

  function pick(city) {
    const label = city.region ? `${city.city}, ${city.region}` : city.city;
    setQuery(label);
    setShouldSearch(false);
    onChange({ city: city.city, region: city.region || '', cityCode: city.code || null });
    setOpen(false);
    window.requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
  }

  return (
    <label className="return-city-field">
      <span>Город отправки</span>
      <div className="vehicle-combobox" ref={rootRef}>
        <div className={`vehicle-combobox-shell ${open ? 'open' : ''}`}>
          <Search size={16} />
          <input
            ref={inputRef}
            value={query}
            disabled={disabled}
            placeholder="Начните вводить город"
            onFocus={() => {
              setShouldSearch(true);
              setOpen(query.trim().length >= 2 && cities.length > 0);
            }}
            onChange={(event) => updateRaw(event.target.value)}
          />
          {loading ? <RefreshCw className="spin" size={16} /> : <ChevronRight size={16} className={open ? 'open' : ''} />}
        </div>
        {open && !disabled && cities.length > 0 && (
          <div className="vehicle-combobox-menu">
            {cities.map((city) => (
              <button
                key={`${city.code}-${city.city}-${city.region}`}
                type="button"
                className="vehicle-combobox-option"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(city)}
              >
                <b>{city.city}</b>
                {city.region && <small>{city.region}</small>}
              </button>
            ))}
          </div>
        )}
      </div>
      {region && <small>{region}</small>}
    </label>
  );
}

function ReturnDocuments({ item, compact = false }) {
  if (!item?.documents) return null;
  const waybill = item.documents.waybillA4;
  const label = item.documents.labelA6;
  const track = item.cdekNumber || item.cdekOrderUuid || '';
  if (!waybill?.available && !label?.available && !track) return null;
  return (
    <div className={`return-documents ${compact ? 'compact' : ''}`}>
      <div className="return-documents-copy">
        <span>Документы СДЭК</span>
        {track && <strong>{track}</strong>}
        <p>
          Наклейте этикетку на коробку. Если принтера нет, запишите номер накладной и приходите в СДЭК:
          там распечатают этикетку и наклеят сами.
        </p>
      </div>
      <div className="return-document-actions">
        {waybill?.url && (
          <a className="button" href={waybill.url} target="_blank" rel="noreferrer">
            <Download size={18} />
            <span>Накладная A4</span>
          </a>
        )}
        {label?.url && (
          <a className="button" href={label.url} target="_blank" rel="noreferrer">
            <Download size={18} />
            <span>Этикетка A6</span>
          </a>
        )}
      </div>
    </div>
  );
}

function ReturnPanel({ navigate }) {
  const isMobile = useMediaQuery('(max-width: 900px)');
  const [state, setState] = useState(null);
  const [selectedByVendor, setSelectedByVendor] = useState({});
  const [returnQuery, setReturnQuery] = useState('');
  const [returnPage, setReturnPage] = useState(1);
  const [draft, setDraft] = useState({ city: '', region: '', cityCode: null });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [clock, setClock] = useState(Date.now());
  const [mobileStepOverride, setMobileStepOverride] = useState(null);
  const returnStepRefs = useRef({});

  async function loadReturns() {
    setLoading(true);
    setError('');
    try {
      const data = await api('/returns');
      setState(data);
      setDraft((current) => {
        if (current.city || !data.defaultCity) return current;
        return {
          ...current,
          city: data.defaultCity,
          region: data.defaultDeliveryPoint?.region || '',
          cityCode: null
        };
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadReturns();
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setClock(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setReturnPage(1);
  }, [returnQuery]);

  useEffect(() => {
    if (!state?.items) return;
    const availableByVendor = new Map(state.items.map((item) => [item.vendorCode, item]));
    setSelectedByVendor((current) => {
      const next = {};
      for (const [vendorCode, selected] of Object.entries(current)) {
        const available = availableByVendor.get(vendorCode);
        if (!available) continue;
        next[vendorCode] = {
          ...available,
          qty: clampQty(selected.qty || 1, available.availableQty) || 1,
          reason: selected.reason || ''
        };
      }
      return next;
    });
  }, [state?.items]);

  if (loading && !state) {
    return (
      <section className="table-panel account-return-panel return-workflow">
        <LoadingState variant="panel" count={3} label="Загружаем товары, доступные к возврату…" />
      </section>
    );
  }

  if (error && !state) {
    return (
      <section className="table-panel account-return-panel return-workflow">
        <ErrorState
          title="Не удалось проверить товары для возврата"
          message={error}
          onRetry={loadReturns}
        />
      </section>
    );
  }

  const limit = state?.limit || {};
  const secondsRemaining = limit.nextAvailableAt
    ? Math.max(0, Math.ceil((Date.parse(limit.nextAvailableAt) - clock) / 1000))
    : 0;
  const canCreate = Boolean(limit.canCreate) || secondsRemaining <= 0;
  const hasEligibleItems = (state?.items?.length || 0) > 0;
  const selectedItems = Object.values(selectedByVendor).filter((item) => item.qty > 0);
  const selectedTotal = selectedItems.reduce((sum, item) => sum + item.qty * item.price, 0);
  const disabled = loading || submitting || !canCreate;
  const missingReason = selectedItems.some((item) => !String(item.reason || '').trim());
  const reasonsDone = selectedItems.length - selectedItems.filter((item) => !String(item.reason || '').trim()).length;
  const query = returnQuery.trim().toLowerCase();
  const filteredReturnItems = (state?.items || []).filter((item) => {
    if (selectedByVendor[item.vendorCode]) return false;
    if (!query) return true;
    return (
      item.vendorCode.toLowerCase().includes(query) ||
      item.name.toLowerCase().includes(query)
    );
  });
  const returnTotalPages = Math.max(1, Math.ceil(filteredReturnItems.length / RETURN_ITEMS_PER_PAGE));
  const safeReturnPage = Math.min(returnPage, returnTotalPages);
  const returnPageStart = (safeReturnPage - 1) * RETURN_ITEMS_PER_PAGE;
  const searchResults = filteredReturnItems.slice(returnPageStart, returnPageStart + RETURN_ITEMS_PER_PAGE);
  const readyToSubmit = canCreate && selectedItems.length > 0 && !missingReason && draft.city.trim();
  const activeStep = !selectedItems.length ? 1 : missingReason ? 2 : !draft.city.trim() ? 3 : 4;
  const guidedStep = Math.min(activeStep, 3);
  const visibleMobileStep = mobileStepOverride && mobileStepOverride <= guidedStep ? mobileStepOverride : guidedStep;
  const nextActionText = !canCreate
    ? `Следующий возврат через ${durationLabel(secondsRemaining)}`
    : !selectedItems.length
      ? 'Добавьте хотя бы один товар'
      : missingReason
        ? 'Заполните причину по каждой позиции'
        : !draft.city.trim()
          ? 'Укажите город отправки'
          : 'Создадим заявку, СДЭК-трек и документ МойСклад';
  const recentReturn = state?.returns?.[0] || null;

  function focusMobileReturnStep(step) {
    if (!isMobile) return;
    window.requestAnimationFrame(() => returnStepRefs.current[step]?.focus({ preventScroll: true }));
  }

  function addReturnItem(item) {
    setSelectedByVendor((current) => ({
      ...current,
      [item.vendorCode]: { ...item, qty: 1, reason: '' }
    }));
    setReturnQuery('');
    setMobileStepOverride(2);
    focusMobileReturnStep(2);
  }

  function stepClass(step) {
    if (activeStep > step) return 'done';
    if (activeStep === step) return 'active';
    return '';
  }

  function patchReturnItem(vendorCode, patch) {
    setMobileStepOverride(2);
    setSelectedByVendor((current) => {
      const item = current[vendorCode];
      if (!item) return current;
      return {
        ...current,
        [vendorCode]: {
          ...item,
          ...patch,
          qty: patch.qty === undefined ? item.qty : Math.max(1, clampQty(patch.qty, item.availableQty))
        }
      };
    });
  }

  function removeReturnItem(vendorCode) {
    setMobileStepOverride(null);
    setSelectedByVendor((current) => {
      const next = { ...current };
      delete next[vendorCode];
      return next;
    });
  }

  async function submitReturn() {
    setError('');
    setMessage('');
    setSubmitting(true);
    try {
      const result = await api('/returns', {
        method: 'POST',
        body: {
          city: draft.city,
          region: draft.region,
          cityCode: draft.cityCode,
          items: selectedItems.map((item) => ({
            vendorCode: item.vendorCode,
            qty: item.qty,
            reason: String(item.reason || '').trim()
          }))
        }
      });
      const cdekLabel = result.return?.cdekNumber || result.return?.cdekOrderUuid || 'формируется';
      setMessage(`Возврат ${result.return.returnNumber} создан. Трек СДЭК: ${cdekLabel}. Документы СДЭК доступны ниже.`);
      setSelectedByVendor({});
      await loadReturns();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="table-panel account-return-panel return-workflow">
      <div className="return-workflow-head">
        <div>
          <h1>Оформить возврат</h1>
          <p>
            {canCreate && hasEligibleItems
              ? `${state?.items?.length || 0} позиций из ваших заказов доступны к возврату`
              : canCreate
                ? 'В текущих заказах нет позиций, доступных к возврату'
                : 'Новый возврат временно недоступен'}
          </p>
        </div>
        <div className={`return-limit-pill ${canCreate && hasEligibleItems ? 'ready' : canCreate ? 'empty' : 'locked'}`}>
          <span>{canCreate && !hasEligibleItems ? 'Позиции' : 'Лимит'}</span>
          <strong>
            {canCreate && hasEligibleItems
              ? 'можно оформить'
              : canCreate
                ? 'нет доступных'
                : durationLabel(secondsRemaining)}
          </strong>
        </div>
      </div>

      {!canCreate ? (
        <div className="return-locked-layout">
          <div className="return-lock-state">
            <Truck size={28} />
            <div>
              <h2>Следующий возврат через {durationLabel(secondsRemaining)}</h2>
              <p>Мы принимаем возвраты СДЭКом за свой счет, поэтому новая заявка доступна раз в 30 дней.</p>
            </div>
          </div>
          {recentReturn && (
            <>
              <div className="return-status-strip">
                <span>Последняя заявка</span>
                <b>{recentReturn.returnNumber}</b>
                {recentReturn.cdekNumber || recentReturn.cdekOrderUuid ? (
                  <strong>{recentReturn.cdekNumber || recentReturn.cdekOrderUuid}</strong>
                ) : (
                  <StatusBadge status={recentReturn.status} />
                )}
                <small>{new Date(recentReturn.createdAt).toLocaleString('ru-RU')}</small>
              </div>
              <ReturnDocuments item={recentReturn} />
            </>
          )}
        </div>
      ) : !hasEligibleItems ? (
        <div className="return-zero-eligible">
          <EmptyState
            title="Нет товаров, которые можно вернуть сейчас"
            description="Здесь появятся позиции из оплаченных заказов, когда они будут доступны по условиям возврата."
            actionLabel="Вернуться к заказам"
            onAction={() => navigate('/account')}
          />
        </div>
      ) : (
        <>
          <div className="return-stepper" aria-label="Шаги оформления возврата">
            {[
              [1, 'Товары'],
              [2, 'Причины'],
              [3, 'Отправка']
            ].map(([step, label]) => (
              <button
                key={step}
                type="button"
                className={`${stepClass(step)} ${isMobile && visibleMobileStep === step ? 'selected' : ''}`.trim()}
                disabled={!isMobile || step > guidedStep}
                aria-current={isMobile && visibleMobileStep === step ? 'step' : undefined}
                onClick={() => {
                  if (isMobile && step <= guidedStep) setMobileStepOverride(step);
                }}
              >
                <b>{step}</b>
                <span>{label}</span>
              </button>
            ))}
          </div>

          <div className="return-workspace">
            <div className="return-flow-main">
              <section
                ref={(node) => { returnStepRefs.current[1] = node; }}
                tabIndex={-1}
                className={`return-step-section ${stepClass(1)} ${visibleMobileStep !== 1 ? 'mobile-step-collapsed' : ''}`}
              >
                <div className="return-section-head">
                  <div>
                    <span>Шаг 1</span>
                    <h2>Найдите товар из ваших заказов</h2>
                  </div>
                  <strong>{selectedItems.length} выбрано</strong>
                </div>
                <label className="return-search-field">
                  <span>Артикул или название</span>
                  <div className="searchbar compact return-searchbar">
                    <Search size={18} />
                    <input
                      value={returnQuery}
                      disabled={loading || submitting}
                      placeholder="Например DIXEL3252"
                      onChange={(event) => setReturnQuery(event.target.value)}
                    />
                  </div>
                </label>
                <div className="return-search-results">
                  {searchResults.map((item) => (
                    <button
                      type="button"
                      className="return-search-row"
                      key={item.vendorCode}
                      disabled={loading || submitting}
                      onClick={() => addReturnItem(item)}
                    >
                      <span className="order-item-code">{item.vendorCode}</span>
                      <span>{item.name}</span>
                      <strong>{money(item.price)}</strong>
                      <small>{item.availableQty} шт.</small>
                      <Plus size={16} />
                    </button>
                  ))}
                  {!loading && !searchResults.length && (
                    <div className="return-empty-state">Совпадений нет. Проверьте артикул или название.</div>
                  )}
                  {filteredReturnItems.length > RETURN_ITEMS_PER_PAGE && (
                    <div className="return-results-pager">
                      <button
                        type="button"
                        onClick={() => setReturnPage((page) => Math.max(1, page - 1))}
                        disabled={safeReturnPage <= 1 || loading || submitting}
                        title="Предыдущая страница"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <span>
                        {returnPageStart + 1}-{Math.min(returnPageStart + RETURN_ITEMS_PER_PAGE, filteredReturnItems.length)} из {filteredReturnItems.length}
                      </span>
                      <button
                        type="button"
                        onClick={() => setReturnPage((page) => Math.min(returnTotalPages, page + 1))}
                        disabled={safeReturnPage >= returnTotalPages || loading || submitting}
                        title="Следующая страница"
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </section>

              <section
                ref={(node) => { returnStepRefs.current[2] = node; }}
                tabIndex={-1}
                className={`return-step-section ${stepClass(2)} ${visibleMobileStep !== 2 ? 'mobile-step-collapsed' : ''}`}
              >
                <div className="return-section-head">
                  <div>
                    <span>Шаг 2</span>
                    <h2>Укажите количество и причину</h2>
                  </div>
                  <strong>{reasonsDone} / {selectedItems.length || 0}</strong>
                </div>
                <div className="return-selected-list">
                  {selectedItems.map((item) => (
                    <article key={item.vendorCode} className="return-selected-card">
                      <button
                        type="button"
                        className="return-remove-button"
                        disabled={submitting}
                        aria-label={`Убрать ${item.vendorCode}`}
                        onClick={() => removeReturnItem(item.vendorCode)}
                      >
                        <X size={16} />
                      </button>
                      <div className="return-selected-top">
                        <div>
                          <b>{item.vendorCode}</b>
                          <span>{item.name}</span>
                        </div>
                        <strong>{money(item.price)}</strong>
                      </div>
                      <div className="return-selected-controls">
                        <div className="return-qty-block">
                          <span>К возврату</span>
                          <QuantityControl
                            qty={item.qty}
                            max={item.availableQty}
                            onChange={(qty) => patchReturnItem(item.vendorCode, { qty })}
                            disabled={submitting}
                            min={1}
                            className="qty compact"
                            label={`Количество к возврату для ${item.name}, артикул ${item.vendorCode}`}
                          />
                          <small>доступно {item.availableQty} шт.</small>
                        </div>
                        <div className="return-reason-block">
                          <span>Причина</span>
                          <textarea
                            className="return-reason-input"
                            value={item.reason || ''}
                            aria-label={`Причина возврата ${item.name}, артикул ${item.vendorCode}`}
                            disabled={submitting}
                            placeholder="Опишите причину возврата и как можно воспроизвести, проверить эту причину"
                            onChange={(event) => patchReturnItem(item.vendorCode, { reason: event.target.value })}
                          />
                        </div>
                      </div>
                    </article>
                  ))}
                  {!selectedItems.length && (
                    <div className="return-empty-state">Выбранные товары появятся здесь.</div>
                  )}
                </div>
              </section>

              <section
                ref={(node) => { returnStepRefs.current[3] = node; }}
                tabIndex={-1}
                className={`return-step-section ${stepClass(3)} ${visibleMobileStep !== 3 ? 'mobile-step-collapsed' : ''}`}
              >
                <div className="return-section-head">
                  <div>
                    <span>Шаг 3</span>
                    <h2>Откуда отправляете СДЭК</h2>
                  </div>
                  {draft.city && <strong>{draft.city}</strong>}
                </div>
                {state?.defaultDeliveryPoint && (
                  <div className="return-default-city">
                    <Truck size={17} />
                    <span>
                      {state.defaultDeliveryPoint.city}
                      {state.defaultDeliveryPoint.code ? ` · ${state.defaultDeliveryPoint.code}` : ''}
                    </span>
                  </div>
                )}
                <div className="return-form-grid">
                  <CdekCityField
                    value={draft.city}
                    region={draft.region}
                    disabled={submitting}
                    onChange={(patch) => {
                      setMobileStepOverride(3);
                      setDraft((current) => ({ ...current, ...patch }));
                    }}
                  />
                </div>
              </section>
            </div>

            <aside className="return-summary-panel">
              <div className="return-summary-title">
                <span>Сводка</span>
                <strong>{money(selectedTotal)}</strong>
              </div>
              <dl className="return-summary-list">
                <dt>Товары</dt>
                <dd>{selectedItems.length ? `${selectedItems.length} поз.` : 'не выбраны'}</dd>
                <dt>Причины</dt>
                <dd>{selectedItems.length ? `${reasonsDone} / ${selectedItems.length}` : 'нет'}</dd>
                <dt>Город</dt>
                <dd>{draft.city || 'не указан'}</dd>
              </dl>
              <div className={readyToSubmit ? 'return-next ready' : 'return-next'}>
                <span>{readyToSubmit ? 'Готово к оформлению' : 'Дальше'}</span>
                <p>{nextActionText}</p>
              </div>
              <IconButton
                icon={submitting ? RefreshCw : Truck}
                className={`primary ${submitting ? 'is-busy' : ''}`}
                onClick={submitReturn}
                disabled={!readyToSubmit || submitting}
              >
                Получить трек СДЭК
              </IconButton>
              {state?.returns?.length > 0 && (
                <div className="return-history">
                  {state.returns.slice(0, 3).map((item) => (
                    <div key={item.id} className="return-history-row">
                      <b>{item.returnNumber}</b>
                      {item.cdekNumber || item.cdekOrderUuid ? (
                        <span>{item.cdekNumber || item.cdekOrderUuid}</span>
                      ) : (
                        <StatusBadge status={item.status} />
                      )}
                      <small>{new Date(item.createdAt).toLocaleString('ru-RU')}</small>
                    </div>
                  ))}
                  <ReturnDocuments item={state.returns[0]} compact />
                </div>
              )}
            </aside>
          </div>
        </>
      )}

      <InlineNotice tone="error" className="return-workflow-notice">{error}</InlineNotice>
      <InlineNotice tone="success" className="return-workflow-notice">{message}</InlineNotice>
    </section>
  );
}

function ReturnPage({ navigate }) {
  return (
    <main className="page return-page">
      <div className="return-page-toolbar">
        <IconButton icon={ArrowLeft} onClick={() => navigate('/account')}>
          Кабинет
        </IconButton>
      </div>
      <ReturnPanel navigate={navigate} />
    </main>
  );
}

function AccountProfileCard({ user, section, navigate }) {
  const sections = [
    { key: 'orders', label: 'Заказы', icon: ClipboardList, route: { name: 'account' } },
    { key: 'export', label: 'Экспорт', icon: FileSpreadsheet, route: { name: 'accountExport' } }
  ];

  return (
    <section className="table-panel account-profile-panel">
      <div className="panel-title">
        <h1>{user.contractorName || user.name}</h1>
      </div>
      <dl className="account-list">
        <dt>Почта</dt>
        <dd>{user.email}</dd>
        <dt>Менеджер</dt>
        <dd>{user.manager ? `${user.manager.name} ${user.manager.phone || ''}` : 'Не назначен'}</dd>
      </dl>
      <nav className="account-section-nav" aria-label="Разделы кабинета">
        {sections.map((item) => {
          const Icon = item.icon;
          const active = item.key === section;
          return (
            <button
              key={item.key}
              type="button"
              className={active ? 'active' : ''}
              aria-current={active ? 'page' : undefined}
              onClick={() => navigate(item.route)}
            >
              <Icon size={16} aria-hidden="true" />
              {item.label}
            </button>
          );
        })}
      </nav>
      <div className="account-profile-actions">
        <IconButton icon={Truck} className="primary account-return-button" onClick={() => navigate({ name: 'accountReturn' })}>
          Оформить возврат
        </IconButton>
      </div>
    </section>
  );
}

function AccountExportPage({ user, navigate }) {
  return (
    <main className="page account-grid">
      <AccountProfileCard user={user} section="export" navigate={navigate} />
      <PriceExportPanel />
    </main>
  );
}

function Account({ user, onRepeatOrder, navigate }) {
  const [orders, setOrders] = useState([]);
  const [orderPage, setOrderPage] = useState(1);
  const [orderPagination, setOrderPagination] = useState(null);
  const [orderSummary, setOrderSummary] = useState({ totalSpent: 0 });
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [ordersStatus, setOrdersStatus] = useState('loading');
  const [ordersError, setOrdersError] = useState('');
  const [ordersRetryKey, setOrdersRetryKey] = useState(0);
  const page = orderPagination?.page || 1;
  const totalPages = orderPagination?.totalPages || 1;
  const totalOrders = orderPagination?.total || orders.length;
  const fromOrder = totalOrders ? (page - 1) * (orderPagination?.limit || orders.length) + 1 : 0;
  const toOrder = totalOrders ? fromOrder + orders.length - 1 : 0;

  useEffect(() => {
    let cancelled = false;
    setOrdersStatus('loading');
    setOrdersError('');
    setOrders([]);
    api(`/orders/my?page=${orderPage}&limit=20`)
      .then((data) => {
        if (cancelled) return;
        setOrders(data.orders || []);
        setOrderPagination(data.pagination || null);
        setOrderSummary(data.summary || { totalSpent: 0 });
        setExpandedOrderId(null);
        setOrdersStatus('success');
      })
      .catch((err) => {
        if (cancelled) return;
        setOrdersError(err.message);
        setOrdersStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [orderPage, ordersRetryKey]);

  async function repeatOrder(orderId) {
    setMessage('');
    setError('');
    try {
      const data = await onRepeatOrder(orderId);
      setMessage(
        data.added
          ? `В корзину добавлено ${data.added} позиций${data.skipped ? `. Пропущено: ${data.skipped}` : ''}.`
          : 'Ничего не добавлено: сейчас нет доступных остатков.'
      );
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <main className="page account-grid">
      <AccountProfileCard user={user} section="orders" navigate={navigate} />
      <section className="table-panel">
        <div className="panel-title account-orders-title">
          <div>
            <h1>Заказы</h1>
            <span>
              {fromOrder}-{toOrder} из {totalOrders}
            </span>
          </div>
          <div className="account-spend">
            <span>За всё время</span>
            <strong>{money(orderSummary.totalSpent)}</strong>
          </div>
          <div className="pager">
            <button onClick={() => setOrderPage(page - 1)} disabled={page <= 1} title="Предыдущая страница">
              <ChevronLeft size={16} />
            </button>
            <span>
              {page} / {totalPages}
            </span>
            <button onClick={() => setOrderPage(page + 1)} disabled={page >= totalPages} title="Следующая страница">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
        {ordersStatus === 'loading' ? (
          <LoadingState variant="panel" count={4} label="Загружаем историю заказов…" />
        ) : null}
        {ordersStatus === 'error' ? (
          <ErrorState
            title="Не удалось загрузить заказы"
            message={ordersError}
            onRetry={() => setOrdersRetryKey((value) => value + 1)}
          />
        ) : null}
        {ordersStatus === 'success' && orders.length > 0 ? <div className="order-head">
          <span>Заказ</span>
          <span>Статус</span>
          <span>Дата</span>
          <span>Сумма</span>
          <span>Действия</span>
        </div> : null}
        {ordersStatus === 'success' && orders.length > 0 ? <div className="orders-list">
          {orders.map((order) => {
            const expanded = expandedOrderId === order.id;
            return (
              <article key={order.id} className="order-card">
                <div className="order-row">
                  <button
                    className="order-toggle"
                    onClick={() => setExpandedOrderId(expanded ? null : order.id)}
                    aria-expanded={expanded}
                  >
                    <ChevronRight size={16} className={expanded ? 'order-toggle-icon open' : 'order-toggle-icon'} />
                    <div className="order-title">
                      <b>{order.order_number}</b>
                      {order.comment && <small>{order.comment}</small>}
                    </div>
                  </button>
                  <StatusBadge status={order.status} className="order-row-status" />
                  <span className="order-row-date">{new Date(order.created_at).toLocaleString('ru-RU')}</span>
                  <strong className="order-row-total">{money(order.total)}</strong>
                  <div className="order-row-actions">
                    <button onClick={() => repeatOrder(order.id)}>Повторить</button>
                  </div>
                </div>
                {expanded && (
                  <div className="order-detail">
                    <div className="order-item-head">
                      <span>Артикул</span>
                      <span>Товар</span>
                      <span>Кол-во</span>
                      <span>Цена</span>
                      <span>Сумма</span>
                      <span>Остаток</span>
                    </div>
                    <div className="order-items">
                      {order.items.map((item) => (
                        <div key={item.id} className="order-item-row">
                          <span className="order-item-code">{item.vendor_code}</span>
                          {item.product_id ? (
                            <button
                              className="order-item-link"
                              onClick={() => navigate({ name: 'product', productId: item.product_id })}
                            >
                              {item.name}
                            </button>
                          ) : (
                            <span>{item.name}</span>
                          )}
                          <span>{item.qty} шт.</span>
                          <span>{money(item.price)}</span>
                          <strong>{money(item.total)}</strong>
                          <span className={item.stock > 0 && item.available ? 'stock ok' : 'stock'}>
                            {item.available ? `${item.stock} шт.` : 'Снят с продажи'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div> : null}
        {ordersStatus === 'success' && !orders.length ? (
          <EmptyState
            title="У вас пока нет заказов"
            description="Оформленные заказы появятся здесь вместе со статусом и возможностью повторить покупку."
            actionLabel="Открыть каталог"
            onAction={() => navigate('/')}
          />
        ) : null}
        <InlineNotice tone="error">{error}</InlineNotice>
        <InlineNotice tone="success">{message}</InlineNotice>
      </section>
    </main>
  );
}

function ProductDetail({ productId, cartQtyById, onSetCartQty, navigate, onBack }) {
  const [data, setData] = useState(null);
  const [activeImage, setActiveImage] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('loading');
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError('');
    setStatus('loading');
    api(`/catalog/products/${productId}`)
      .then((nextData) => {
        if (cancelled) return;
        setData(nextData);
        setActiveImage(nextData.product.imageUrls?.[0] || nextData.product.imageUrl || '');
        setStatus('success');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [productId, retryKey]);

  if (status === 'error') {
    return (
      <main className="page detail-page">
        <section className="table-panel">
          <ErrorState
            title="Не удалось открыть товар"
            message={error}
            onRetry={() => setRetryKey((value) => value + 1)}
          />
        </section>
      </main>
    );
  }

  if (status === 'loading' || !data) {
    return (
      <main className="page detail-page">
        <section className="table-panel">
          <LoadingState variant="detail" count={2} label="Загружаем карточку товара…" />
        </section>
      </main>
    );
  }

  const { product, similarProducts } = data;
  const imageUrls = product.imageUrls?.length ? product.imageUrls : product.imageUrl ? [product.imageUrl] : [];
  const descriptionHtml = sanitizeDescriptionHtml(product.description);
  const stockBreakdown = getProductStockBreakdown(product);

  return (
    <main className="page detail-page">
      <section className="detail-panel">
        <div className="detail-head">
          <button className="category-back" onClick={onBack}>
            <ArrowLeft size={16} /> В каталог
          </button>
          <span>{product.categoryPath}</span>
        </div>
        <div className="detail-grid">
          <div className={`detail-gallery ${imageUrls.length > 1 ? 'has-thumbs' : ''}`}>
            <div className="detail-main-image">
              <ProductImage
                src={activeImage}
                alt={`Фото товара ${product.name}, артикул ${product.vendorCode}`}
                loading="eager"
                fetchPriority="high"
                fallbackIconSize={42}
                fallbackText="Фото недоступно"
              />
            </div>
            {imageUrls.length > 1 && (
              <div className="detail-thumbs">
                {imageUrls.map((imageUrl, index) => (
                  <button
                    key={imageUrl}
                    type="button"
                    className={activeImage === imageUrl ? 'selected' : ''}
                    onClick={() => setActiveImage(imageUrl)}
                    aria-label={`Показать фото ${index + 1} товара ${product.name}`}
                  >
                    <ProductImage src={imageUrl} alt="" fallbackText="Нет фото" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="detail-info">
            <h1>{product.name}</h1>
            <div className="detail-meta">
              <span>Артикул: {product.vendorCode}</span>
            </div>
            <div className="product-purchase-block">
              <div className="product-purchase-primary">
                <strong className="detail-price">{money(product.price)}</strong>
                <span className={`product-purchase-availability ${product.stock > 0 ? 'stock ok' : 'stock'}`}>
                  {product.stock > 0 ? `Доступно ${product.stock} шт.` : 'Нет в наличии'}
                </span>
              </div>
              <div className="product-purchase-quantity">
                <span>Количество в корзине</span>
                <QuantityControl
                  qty={cartQtyById[product.id] || 0}
                  max={product.stock}
                  onChange={(nextQty) => onSetCartQty(product, nextQty)}
                  label={`Количество ${product.name}, артикул ${product.vendorCode}`}
                />
              </div>
              <div className="detail-stock-breakdown" aria-label="Остатки по складам">
                {stockBreakdown.map((row, index) => (
                  <span key={`${row.label}-${index}`}>
                    {row.label} - {formatQuantity(row.quantity)} шт.
                  </span>
                ))}
              </div>
            </div>
            {descriptionHtml && (
              <div
                className="detail-description"
                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
              />
            )}
          </div>
        </div>
      </section>
      {similarProducts?.length > 0 && (
        <section className="table-panel">
          <div className="panel-title">
            <h1>Похожие товары</h1>
          </div>
          <div className="products-grid similar-grid">
            {similarProducts.map((similar) => (
              <ProductCard
                key={similar.id}
                product={similar}
                qty={cartQtyById[similar.id] || 0}
                onQtyChange={(nextQty) => onSetCartQty(similar, nextQty)}
                onOpen={(id) => navigate({ name: 'product', productId: id })}
              />
            ))}
          </div>
        </section>
      )}
    </main>
  );
}

function AdminAuto({ data, reload }) {
  const selector = useAutoCatalogSelector();
  const [tokenDraft, setTokenDraft] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [links, setLinks] = useState([]);
  const [linkPage, setLinkPage] = useState(1);
  const [linkPagination, setLinkPagination] = useState(null);
  const [busy, setBusy] = useState('');
  const [feedback, setFeedback] = useState({ type: '', text: '' });

  useEffect(() => {
    setTokenDraft('');
  }, [data.settings?.tokenMask]);

  useEffect(() => {
    if (!selector.selection.generationId) {
      setLinks([]);
      setLinkPagination(null);
      return;
    }
    const fitment = fitmentQuery(selector.selection);
    api(`/admin/auto-catalog/links?${fitment}&page=${linkPage}&limit=${ADMIN_PAGE_LIMIT}`)
      .then((result) => {
        setLinks(result.links || []);
        setLinkPagination(result.pagination || null);
        if (result.pagination?.page && result.pagination.page !== linkPage) setLinkPage(result.pagination.page);
      })
      .catch(() => {
        setLinks([]);
        setLinkPagination(null);
      });
  }, [selector.selection, linkPage]);

  useEffect(() => {
    setLinkPage(1);
  }, [
    selector.selection.generationId,
    selector.selection.configurationId,
    selector.selection.modificationId
  ]);

  useEffect(() => {
    if (!productSearch.trim()) {
      setProductResults([]);
      return;
    }
    const timer = window.setTimeout(() => {
      api(`/admin/auto-catalog/products?search=${encodeURIComponent(productSearch)}&limit=24`)
        .then((result) => setProductResults(result.products || []))
        .catch(() => setProductResults([]));
    }, 180);
    return () => window.clearTimeout(timer);
  }, [productSearch]);

  async function run(action, task, successMessage) {
    setBusy(action);
    setFeedback({ type: '', text: '' });
    try {
      const result = await task();
      await reload();
      if (selector.selection.generationId) {
        const fitment = fitmentQuery(selector.selection);
        const linksData = await api(`/admin/auto-catalog/links?${fitment}&page=${linkPage}&limit=${ADMIN_PAGE_LIMIT}`);
        setLinks(linksData.links || []);
        setLinkPagination(linksData.pagination || null);
        if (linksData.pagination?.page && linksData.pagination.page !== linkPage) setLinkPage(linksData.pagination.page);
      }
      setFeedback({ type: 'success', text: successMessage(result) });
    } catch (err) {
      setFeedback({ type: 'error', text: err.message || 'Операция не выполнена' });
    } finally {
      setBusy('');
    }
  }

  const selectedGeneration = selector.generations.find((item) => item.id === selector.selection.generationId);
  const selectedConfig = selector.configurations.find((item) => item.id === selector.selection.configurationId);
  const selectedMod = selector.modifications.find((item) => item.id === selector.selection.modificationId);

  return (
    <section className="admin-auto-grid">
      <div className="table-panel admin-auto-panel">
        <div className="panel-title">
          <h1>Авто-каталог</h1>
          <span>{data.counts?.generations || 0} поколений</span>
        </div>
        {feedback.text && <div className={feedback.type === 'error' ? 'error' : 'success'}>{feedback.text}</div>}
        <div className="admin-auto-kpis">
          <article className="admin-auto-kpi">
            <small>Марки</small>
            <b>{data.counts?.marks || 0}</b>
          </article>
          <article className="admin-auto-kpi">
            <small>Модели</small>
            <b>{data.counts?.models || 0}</b>
          </article>
          <article className="admin-auto-kpi">
            <small>Модификации</small>
            <b>{data.counts?.modifications || 0}</b>
          </article>
          <article className="admin-auto-kpi">
            <small>Привязки</small>
            <b>{data.counts?.links || 0}</b>
          </article>
        </div>
        <div className="admin-auto-settings">
          <label>
            <span>Ключ cars-base</span>
            <input
              type="text"
              placeholder={data.settings?.tokenConfigured ? `Сохранён ${data.settings.tokenMask}` : 'Вставь новый ключ'}
              value={tokenDraft}
              onChange={(event) => setTokenDraft(event.target.value)}
            />
          </label>
          <IconButton
            icon={busy === 'save-token' ? RefreshCw : Save}
            onClick={() =>
              run(
                'save-token',
                () => api('/admin/auto-catalog/settings', { method: 'PUT', body: { token: tokenDraft } }),
                () => 'Ключ cars-base сохранён.'
              )
            }
            disabled={!tokenDraft.trim() || busy !== ''}
          >
            Сохранить ключ
          </IconButton>
        </div>
        <div className="import-status">
          <b>{data.active?.running ? 'Синхронизация выполняется' : statusLabel(data.latest?.status, 'Нет запусков')}</b>
          <span>{data.active?.message || data.latest?.message || 'Каталог ещё не синхронизирован.'}</span>
          <progress value={Number(data.active?.processed || data.latest?.processed || 0)} max={Math.max(1, Number(data.active?.total || data.latest?.total || 1))} />
          <small>
            {Number(data.active?.processed || data.latest?.processed || 0)} / {Number(data.active?.total || data.latest?.total || 0)}
          </small>
          <small>
            {data.settings?.lastSuccessAt
              ? `Последний успешный синк: ${data.settings.lastSuccessAt}`
              : 'Успешных синков пока не было'}
          </small>
        </div>
        <div className="button-row">
          <IconButton
            icon={busy === 'sync-inc' ? RefreshCw : Play}
            onClick={() =>
              run(
                'sync-inc',
                () => api('/admin/auto-catalog/sync', { method: 'POST', body: { mode: 'incremental' } }),
                (result) => (result.alreadyRunning ? `Синк уже идёт #${result.runId}.` : `Инкрементальный синк запущен #${result.runId}.`)
              )
            }
            disabled={busy !== '' || data.active?.running}
          >
            Синк обновлений
          </IconButton>
          <IconButton
            icon={busy === 'sync-full' ? RefreshCw : Database}
            onClick={() =>
              run(
                'sync-full',
                () => api('/admin/auto-catalog/sync', { method: 'POST', body: { mode: 'full', forceFull: true } }),
                (result) => (result.alreadyRunning ? `Синк уже идёт #${result.runId}.` : `Полный синк запущен #${result.runId}.`)
              )
            }
            disabled={busy !== '' || data.active?.running}
          >
            Полный синк
          </IconButton>
          <IconButton
            icon={busy === 'cancel-sync' ? RefreshCw : Square}
            onClick={() =>
              run(
                'cancel-sync',
                () => api('/admin/auto-catalog/cancel', { method: 'POST' }),
                (result) => (result.cancelled ? 'Остановка синка запрошена.' : 'Активного синка нет.')
              )
            }
            disabled={busy !== '' || !data.active?.running}
          >
            Остановить
          </IconButton>
          <IconButton
            icon={busy === 'demo-links' ? RefreshCw : Plus}
            onClick={() =>
              run(
                'demo-links',
                () => api('/admin/auto-catalog/demo-links', { method: 'POST' }),
                (result) => (result.result?.skipped ? 'Демо-привязки уже существуют.' : `Создано тестовых привязок: ${result.result?.created || 0}.`)
              )
            }
            disabled={busy !== ''}
          >
            Демо 1-5 товаров
          </IconButton>
          <IconButton icon={RefreshCw} onClick={reload} disabled={busy !== ''}>
            Обновить
          </IconButton>
        </div>
        {(data.downloads?.files || []).length > 0 && (
          <div className="admin-auto-files">
            <b>Файлы cars-base</b>
            {(data.downloads.files || []).map((file) => (
              <div key={file.filename} className="admin-auto-file">
                <span>{file.filename}</span>
                <small>{Math.round((file.size || 0) / 1024 / 1024)} МБ</small>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="table-panel admin-auto-panel">
        <div className="panel-title">
          <h1>Ручная привязка</h1>
          <span>{selectedGeneration ? generationOptionLabel(selectedGeneration) : 'Выбери машину'}</span>
        </div>
        <div className="admin-auto-selector">
          <select value={selector.selection.markId} onChange={(event) => selector.selectMark(event.target.value)}>
            <option value="">Марка</option>
            {selector.marks.map((mark) => (
              <option key={mark.id} value={mark.id}>
                {mark.name}
              </option>
            ))}
          </select>
          <select
            value={selector.selection.modelId}
            onChange={(event) => selector.selectModel(event.target.value)}
            disabled={!selector.selection.markId}
          >
            <option value="">Модель</option>
            {selector.models.map((model) => (
              <option key={model.id} value={model.id}>
                {model.name}
              </option>
            ))}
          </select>
          <select
            value={selector.selection.generationId}
            onChange={(event) => selector.selectGeneration(event.target.value)}
            disabled={!selector.selection.modelId}
          >
            <option value="">Поколение</option>
            {selector.generations.map((generation) => (
              <option key={generation.id} value={generation.id}>
                {generationOptionLabel(generation)}
              </option>
            ))}
          </select>
          <select
            value={selector.selection.configurationId}
            onChange={(event) => selector.selectConfiguration(event.target.value)}
            disabled={!selector.selection.generationId}
          >
            <option value="">Кузов</option>
            {selector.configurations.map((configuration) => (
              <option key={configuration.id} value={configuration.id}>
                {configuration.name}
              </option>
            ))}
          </select>
          <select
            value={selector.selection.modificationId}
            onChange={(event) => selector.updateSelection({ modificationId: event.target.value })}
            disabled={!selector.selection.generationId}
          >
            <option value="">Модификация</option>
            {selector.modifications.map((modification) => (
              <option key={modification.id} value={modification.id}>
                {modification.name}
              </option>
            ))}
          </select>
        </div>
        {selectedGeneration && (
          <div className="admin-auto-target">
            <b>
              {selector.marks.find((item) => item.id === selector.selection.markId)?.name} /{' '}
              {selector.models.find((item) => item.id === selector.selection.modelId)?.name} / {selectedGeneration.name}
            </b>
            <span>
              {selectedConfig ? `${selectedConfig.name}` : 'Все кузова'} {selectedMod ? `• ${selectedMod.name}` : ''}
            </span>
          </div>
        )}
        <div className="searchbar compact">
          <Search size={18} />
          <input
            value={productSearch}
            aria-label="Поиск товара для привязки к автомобилю"
            onChange={(event) => setProductSearch(event.target.value)}
            placeholder="Найти товар по артикулу или названию"
          />
        </div>
        <div className="admin-auto-products">
          {productResults.map((product) => (
            <div key={product.id} className="admin-auto-product-row">
              <div>
                <b>{product.vendorCode}</b>
                <span>{product.name}</span>
                <small>
                  {money(product.price)} • остаток {product.stock} шт.
                </small>
              </div>
              <button
                onClick={() =>
                  run(
                    `link-${product.id}`,
                    () =>
                      api('/admin/auto-catalog/links', {
                        method: 'POST',
                        body: { productId: product.id, ...selector.selection }
                      }),
                    () => `Товар ${product.vendorCode} привязан к выбранной машине.`
                  )
                }
                disabled={!selector.selection.generationId || busy !== ''}
              >
                Привязать
              </button>
            </div>
          ))}
          {!productResults.length && <div className="category-empty">Начни вводить артикул или название товара.</div>}
        </div>
        <div className="admin-auto-links">
          <div className="panel-title">
            <h1>Текущие привязки</h1>
            <span>{linkPagination?.total ?? links.length}</span>
          </div>
          {selectedGeneration && (
            <AdminPager
              title="Привязки"
              pagination={linkPagination}
              itemCount={links.length}
              onPageChange={setLinkPage}
            />
          )}
          {links.map((link) => (
            <div key={link.id} className="admin-auto-link-row">
              <div>
                <b>{link.vendorCode}</b>
                <span>{link.productName}</span>
                <small>{link.source === 'demo' ? 'Демо-данные' : 'Вручную'}</small>
              </div>
              <button
                onClick={() =>
                  run(
                    `unlink-${link.id}`,
                    () => api(`/admin/auto-catalog/links/${link.id}`, { method: 'DELETE' }),
                    () => `Привязка ${link.vendorCode} удалена.`
                  )
                }
                disabled={busy !== ''}
              >
                Удалить
              </button>
            </div>
          ))}
          {!links.length && <div className="category-empty">Для выбранной машины привязок пока нет.</div>}
        </div>
      </div>
    </section>
  );
}

function AdminAI({ data, reload, selectedUserId, onUserChange, onRunPageChange }) {
  const [draftEnabled, setDraftEnabled] = useState(Boolean(data.settings?.enabled));
  const [draftAutoEmbed, setDraftAutoEmbed] = useState(Boolean(data.settings?.autoEmbedNewProducts));
  const [draftAgentModel, setDraftAgentModel] = useState(data.settings?.agentModel || '');
  const [draftAgentPriorityMode, setDraftAgentPriorityMode] = useState(data.settings?.agentPriorityMode !== false);
  const [draftChatBaseUrl, setDraftChatBaseUrl] = useState(data.settings?.chatProvider?.baseUrl || '');
  const [draftChatApiKey, setDraftChatApiKey] = useState('');
  const [draftEmbeddingBaseUrl, setDraftEmbeddingBaseUrl] = useState(data.settings?.embeddingProvider?.baseUrl || '');
  const [draftEmbeddingApiKey, setDraftEmbeddingApiKey] = useState('');
  const [draftEmbeddingModel, setDraftEmbeddingModel] = useState(data.settings?.embeddingModel || '');
  const [draftEmbeddingDimensions, setDraftEmbeddingDimensions] = useState(String(data.settings?.embeddingDimensions || 1024));
  const [activeRun, setActiveRun] = useState(null);
  const settings = data.settings || {};
  const chatProvider = settings.chatProvider || {};
  const embeddingProvider = settings.embeddingProvider || {};
  const indexing = data.indexing || {};
  const usage = data.usage || {};
  const aiClients = data.aiClients || [];
  const topDialog = usage.topDialog || null;
  const progressValue = Number(indexing.processed || 0);
  const progressMax = Math.max(1, Number(indexing.total || 0) || 1);
  const indexStatusLabel = indexing.running
    ? 'Идёт индексация embeddings'
    : indexing.isConfiguredIndexReady
      ? 'Embeddings индекс готов'
      : progressValue > 0
        ? 'Embeddings индекс заполнен частично'
        : 'Embeddings индекс пуст';

  useEffect(() => {
    setDraftEnabled(Boolean(data.settings?.enabled));
    setDraftAutoEmbed(Boolean(data.settings?.autoEmbedNewProducts));
    setDraftAgentModel(data.settings?.agentModel || '');
    setDraftAgentPriorityMode(data.settings?.agentPriorityMode !== false);
    setDraftChatBaseUrl(data.settings?.chatProvider?.baseUrl || '');
    setDraftEmbeddingBaseUrl(data.settings?.embeddingProvider?.baseUrl || '');
    setDraftEmbeddingModel(data.settings?.embeddingModel || '');
    setDraftEmbeddingDimensions(String(data.settings?.embeddingDimensions || 1024));
    setDraftChatApiKey('');
    setDraftEmbeddingApiKey('');
  }, [data.settings]);

  useEffect(() => {
    if (!data.runs?.length) {
      setActiveRun(null);
      return;
    }
    if (!activeRun || !data.runs.some((run) => run.id === activeRun.id)) {
      void openRun(data.runs[0].id);
    }
  }, [data.runs]);

  async function saveSettings() {
    await api('/admin/ai/settings', {
      method: 'PUT',
      body: {
        enabled: draftEnabled,
        autoEmbedNewProducts: draftAutoEmbed,
        agentModel: draftAgentModel,
        agentPriorityMode: draftAgentPriorityMode,
        chatBaseUrl: draftChatBaseUrl,
        chatApiKey: draftChatApiKey,
        embeddingBaseUrl: draftEmbeddingBaseUrl,
        embeddingApiKey: draftEmbeddingApiKey,
        embeddingModel: draftEmbeddingModel,
        embeddingDimensions: Number(draftEmbeddingDimensions || 1024) || 1024
      }
    });
    await reload();
  }

  async function startReindex() {
    await api('/admin/ai/reindex', { method: 'POST', body: {} });
    await reload();
  }

  async function backfillMissingEmbeddings() {
    await api('/admin/ai/reindex-missing', { method: 'POST', body: {} });
    await reload();
  }

  async function openRun(id) {
    const detail = await api(`/admin/ai/runs/${id}`);
    setActiveRun(detail.run);
  }

  return (
    <section className="admin-ai-grid">
      <div className="table-panel admin-ai-panel">
        <div className="admin-ai-section admin-ai-section-hero">
          <div className="panel-title">
            <h1>AI настройки</h1>
            <span>{indexing.indexedProducts || 0} / {indexing.availableProducts || 0}</span>
          </div>
          <div className="admin-ai-form">
            <label>
              <span>Включено</span>
              <input
                type="checkbox"
                checked={draftEnabled}
                onChange={(event) => setDraftEnabled(event.target.checked)}
              />
            </label>
            <label>
              <span>Автодобавление новых товаров в embeddings</span>
              <input
                type="checkbox"
                checked={draftAutoEmbed}
                onChange={(event) => setDraftAutoEmbed(event.target.checked)}
              />
            </label>
            <div className="admin-ai-provider-block">
              <div className="admin-ai-provider-title">
                <b>Основные модели</b>
                <small>
                  {chatProvider.apiKeyConfigured
                    ? `ключ подключен: ${chatProvider.apiKeyPreview || 'скрыт'}`
                    : 'ключ не подключен'}
                </small>
              </div>
              <label className="admin-ai-field">
                <span>Base URL</span>
                <input value={draftChatBaseUrl} onChange={(event) => setDraftChatBaseUrl(event.target.value)} />
              </label>
              <label className="admin-ai-field">
                <span>Agent model</span>
                <input value={draftAgentModel} onChange={(event) => setDraftAgentModel(event.target.value)} />
              </label>
              <label>
                <span>Ускоренный режим агента (~1.5×)</span>
                <input
                  type="checkbox"
                  checked={draftAgentPriorityMode}
                  onChange={(event) => setDraftAgentPriorityMode(event.target.checked)}
                />
              </label>
              <label className="admin-ai-field">
                <span>Новый ключ</span>
                <input
                  type="password"
                  value={draftChatApiKey}
                  placeholder="оставь пустым, если не меняешь"
                  autoComplete="off"
                  onChange={(event) => setDraftChatApiKey(event.target.value)}
                />
              </label>
            </div>
            <div className="admin-ai-provider-block">
              <div className="admin-ai-provider-title">
                <b>OpenAI embeddings</b>
                <small>
                  {embeddingProvider.apiKeyConfigured
                    ? `ключ подключен: ${embeddingProvider.apiKeyPreview || 'скрыт'}`
                    : 'ключ не подключен'}
                </small>
              </div>
              <label className="admin-ai-field">
                <span>Base URL</span>
                <input value={draftEmbeddingBaseUrl} onChange={(event) => setDraftEmbeddingBaseUrl(event.target.value)} />
              </label>
              <label className="admin-ai-field">
                <span>Embedding model</span>
                <input value={draftEmbeddingModel} onChange={(event) => setDraftEmbeddingModel(event.target.value)} />
              </label>
              <label className="admin-ai-field">
                <span>Размерность</span>
                <input
                  type="number"
                  min="256"
                  max="1536"
                  value={draftEmbeddingDimensions}
                  onChange={(event) => setDraftEmbeddingDimensions(event.target.value)}
                />
              </label>
              <label className="admin-ai-field">
                <span>Новый ключ</span>
                <input
                  type="password"
                  value={draftEmbeddingApiKey}
                  placeholder="оставь пустым, если не меняешь"
                  autoComplete="off"
                  onChange={(event) => setDraftEmbeddingApiKey(event.target.value)}
                />
              </label>
            </div>
          </div>
          <div className="button-row admin-ai-actions">
            <IconButton icon={Save} onClick={saveSettings}>
              Применить
            </IconButton>
            <IconButton icon={RefreshCw} onClick={reload}>
              Обновить
            </IconButton>
            <IconButton icon={Zap} onClick={backfillMissingEmbeddings}>
              Доииндексировать пропуски
            </IconButton>
            <IconButton icon={Play} onClick={startReindex}>
              Переиндексировать
            </IconButton>
          </div>
        </div>
        <div className="admin-ai-section">
          <div className="admin-ai-pipeline">
            <article className="admin-ai-pipeline-card admin-ai-pipeline-card-wide">
              <small>Текущий pipeline</small>
              <b>{settings.pipelineName || 'DIXEL AI Agent'}</b>
              <span>Новый tool-loop агент без legacy fallback-ветки</span>
            </article>
            <article className="admin-ai-pipeline-card">
              <small>Agent model</small>
              <b>{settings.agentModel || 'не задана'}</b>
              <span>
                {chatProvider.name || 'GPTProxy'}: {chatProvider.baseUrl || 'base URL не задан'} •{' '}
                {settings.agentPriorityMode === false ? 'обычная скорость' : 'priority ~1.5×'}
              </span>
            </article>
            <article className="admin-ai-pipeline-card">
              <small>Embeddings</small>
              <b>{settings.embeddingModel || 'не задана'}</b>
              <span>{embeddingProvider.name || 'OpenAI'}: {settings.embeddingDimensions || 0}</span>
            </article>
          </div>
        </div>
        <div className="admin-ai-section admin-ai-section-status">
          <div className="import-status admin-ai-status-box">
            <div className="admin-ai-status-head">
              <b>{indexStatusLabel}</b>
              <span>
                {settings.chatApiKeyConfigured ? 'GPTProxy подключен' : 'GPTProxy не подключен'} •{' '}
                {settings.embeddingApiKeyConfigured ? 'OpenAI embeddings подключен' : 'OpenAI embeddings не подключен'}
              </span>
            </div>
            <progress value={progressValue} max={progressMax} />
            <div className="admin-ai-status-meta">
              <small>{progressValue} / {indexing.total || 0}</small>
              <small>Пропущено по текущему профилю: {indexing.missingProducts || 0}</small>
              <small>
                Очередь: {indexing.queue?.queued || 0} в ожидании, {indexing.queue?.processing || 0} в работе, {indexing.queue?.errors || 0} ошибок
              </small>
              <small>
                {indexing.lastIndexedAt
                  ? `Последняя индексация: ${new Date(indexing.lastIndexedAt).toLocaleString('ru-RU')}`
                  : 'Индекс ещё не собран для текущего профиля embeddings'}
              </small>
            </div>
          </div>
        </div>
        <div className="admin-ai-section">
          <div className="admin-ai-kpis">
            <article className="admin-ai-kpi">
              <small>Расход за сегодня</small>
              <b>{aiCost(usage.todayCost)}</b>
            </article>
            <article className="admin-ai-kpi">
              <small>Расход за 7 дней</small>
              <b>{aiCost(usage.sevenDaysCost)}</b>
            </article>
            <article className="admin-ai-kpi admin-ai-kpi-wide">
              <small>Самый дорогой диалог</small>
              {topDialog ? (
                <>
                  <b>{aiCost(topDialog.totalCost)}</b>
                  <span>
                    {topDialog.clientName || 'Клиент'} · диалог #{topDialog.sessionId}, запросов: {topDialog.runCount}
                  </span>
                  <em>{topDialog.label}</em>
                </>
              ) : (
                <>
                  <b>{aiCost(0)}</b>
                  <span>Пока нет сохранённых расходов</span>
                </>
              )}
            </article>
          </div>
        </div>
      </div>
      <div className="table-panel">
        <div className="panel-title">
          <h1>AI trace</h1>
          <span>{selectedUserId ? 'фильтр по клиенту' : 'все клиенты'}</span>
        </div>
        <div className="admin-ai-trace-toolbar">
          <label>
            <span>Клиент</span>
            <select value={selectedUserId} onChange={(event) => onUserChange(event.target.value)}>
              <option value="">Все клиенты</option>
              {aiClients.map((client) => (
                <option key={client.userId} value={client.userId}>
                  {client.clientName} · {client.runCount} запросов · {aiCost(client.totalCost)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <AdminPager
          title="AI trace"
          pagination={data.runsPagination}
          itemCount={(data.runs || []).length}
          onPageChange={onRunPageChange}
        />
        <div className="admin-ai-runs">
          {(data.runs || []).map((run) => (
            <button
              key={run.id}
              className={`admin-ai-run ${activeRun?.id === run.id ? 'active' : ''}`}
              onClick={() => openRun(run.id)}
            >
              <div className="admin-ai-run-top">
                <b>#{run.id}</b>
                <strong>{aiCost(run.sessionTotalCost)}</strong>
              </div>
              <div className="admin-ai-run-client">
                <Users size={14} />
                <b>{run.clientName || 'Клиент'}</b>
                {run.clientEmail && <small>{run.clientEmail}</small>}
              </div>
              <span>{run.requestText}</span>
              <small>{run.normalizedQuery}</small>
              <div className="admin-ai-run-meta">
                <small>Диалог: #{run.sessionId}</small>
                <small>Запросов: {run.sessionRunCount || 0}</small>
                <small>Токены: {Number(run.sessionTotalTokens || 0).toLocaleString('ru-RU')}</small>
              </div>
              <em>{new Date(run.createdAt).toLocaleString('ru-RU')}</em>
            </button>
          ))}
          {!(data.runs || []).length && <div className="category-empty">По выбранному клиенту AI-запросов пока нет.</div>}
        </div>
        {activeRun && (
          <div className="admin-ai-trace">
            <div className="admin-ai-trace-head">
              <b>run #{activeRun.id}</b>
              <span>{activeRun.requestText}</span>
              <small>{activeRun.normalizedQuery}</small>
              <div className="admin-ai-trace-metrics">
                <small>Клиент: {activeRun.clientName || 'Клиент'}</small>
                {activeRun.clientEmail && <small>{activeRun.clientEmail}</small>}
                <small>Стоимость запроса: {aiCost(activeRun.totalCost)}</small>
                <small>Токены: {Number(activeRun.totalTokens || 0).toLocaleString('ru-RU')}</small>
              </div>
            </div>
            <div className="admin-ai-answer-box">
              <b>Ответ клиенту</b>
              <p>{activeRun.answerText}</p>
            </div>
            <div className="admin-ai-steps">
              {(activeRun.trace?.steps || []).map((step, index) => (
                <article key={`${step.step}-${index}`} className="admin-trace-step">
                  <div className="admin-trace-step-head">
                    <span className="admin-trace-step-dot">{index + 1}</span>
                    <div>
                      <b>{traceStepTitle(step.step)}</b>
                      <small>{step.at ? new Date(step.at).toLocaleTimeString('ru-RU') : ''}</small>
                    </div>
                  </div>
                  <p>{traceStepSummary(step)}</p>
                  <details>
                    <summary>payload</summary>
                    <pre>{JSON.stringify(step.payload, null, 2)}</pre>
                  </details>
                </article>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function classifierStatusLabel(status) {
  const map = {
    unclassified: 'Не обработан',
    classified: 'Привязан',
    skipped: 'Пропущен',
    manual_review: 'На модерацию',
    manual_assigned: 'Назначен вручную',
    error: 'Ошибка'
  };
  return map[status] || statusLabel(status, 'Неизвестный статус');
}

function classifierScopeLabel(scope) {
  const map = {
    unclassified: 'новые',
    manual_review: 'модерация',
    errors: 'ошибки',
    skipped: 'пропущенные',
    all: 'все товары'
  };
  return map[scope] || 'Неизвестная выборка';
}

function classifierScopeCount(stats, scope) {
  const counts = stats.scopeCounts || {};
  const map = {
    unclassified: counts.unclassified ?? stats.unclassified,
    manual_review: counts.manual_review ?? stats.manualReview,
    errors: counts.errors ?? stats.errors,
    skipped: counts.skipped ?? stats.skipped,
    all: counts.all ?? stats.availableProducts ?? stats.totalProducts
  };
  return Number(map[scope] || 0);
}

function classifierRunState(run) {
  if (!run) return { label: 'Нет запусков', tone: 'neutral' };
  const errors = Number(run.errors || 0);
  if (run.status === 'retrying') return { label: 'Пауза до ретрая', tone: 'warning' };
  if (run.status === 'queued') return { label: 'В очереди', tone: 'info' };
  if (run.running || run.status === 'running') {
    return errors > 0
      ? { label: 'Идёт с ошибками', tone: 'warning' }
      : { label: 'Идёт обработка', tone: 'info' };
  }
  if (run.status === 'completed') {
    if (errors > 0) return { label: 'Завершено с ошибками', tone: 'danger' };
    return { label: 'Успешно завершено', tone: 'success' };
  }
  if (run.status === 'failed') return { label: 'Сбой запуска', tone: 'danger' };
  if (run.status === 'cancelled') return { label: 'Остановлено', tone: 'warning' };
  return { label: statusLabel(run.status, 'Нет запусков'), tone: 'neutral' };
}

function formatModelPrice(value) {
  const price = Number(value || 0);
  if (!price) return '$0';
  return `$${price.toFixed(price < 0.01 ? 4 : 2)}/M`;
}

function AdminModelPicker({ value, onChange, models, search, onSearch, loading }) {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const selected = models.find((model) => model.id === value) || null;

  useEffect(() => {
    function handleClick(event) {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="admin-model-picker" ref={rootRef}>
      <div className={`admin-model-picker-shell ${open ? 'open' : ''}`}>
        <Search size={16} />
        <input
          ref={inputRef}
          value={open ? search : selected?.id || value}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            onSearch(event.target.value);
            setOpen(true);
          }}
          placeholder="Найти модель провайдера"
        />
        {loading ? <RefreshCw className="spin" size={16} /> : <ChevronRight className={open ? 'open' : ''} size={16} />}
      </div>
      {open && (
        <div className="admin-model-picker-menu">
          {models.map((model) => (
            <button
              type="button"
              key={model.id}
              className={model.id === value ? 'selected' : ''}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onChange(model.id);
                onSearch('');
                setOpen(false);
                window.requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
              }}
            >
              <span>
                <b>{model.name}</b>
                <small>{model.id}</small>
              </span>
              <em>
                {model.hasPricing
                  ? model.isFree
                    ? 'free'
                    : `${formatModelPrice(model.promptPrice)} / ${formatModelPrice(model.completionPrice)}`
                  : 'цена не указана'}
                {model.supportsResponseFormat ? ' • JSON' : ''}
              </em>
            </button>
          ))}
          {!models.length && <div className="vehicle-combobox-empty">Модели не найдены</div>}
        </div>
      )}
    </div>
  );
}

function AdminClassifier({ data, reload, onRunPageChange }) {
  const selector = useAutoCatalogSelector({ includeDetails: false });
  const settings = data.settings || {};
  const stats = data.stats || {};
  const latest = data.latest || null;
  const active = data.active || null;
  const runs = data.runs || [];
  const [modelDraft, setModelDraft] = useState(settings.model || '');
  const [autoRunDraft, setAutoRunDraft] = useState(Boolean(settings.autoRunNewProducts));
  const [modelSearch, setModelSearch] = useState('');
  const [modelOptions, setModelOptions] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [keyInfo, setKeyInfo] = useState(null);
  const [scope, setScope] = useState('unclassified');
  const [limit, setLimit] = useState('25');
  const [busy, setBusy] = useState('');
  const [feedback, setFeedback] = useState({ type: '', text: '' });
  const [itemStatus, setItemStatus] = useState('manual_review');
  const [itemSearch, setItemSearch] = useState('');
  const [itemPage, setItemPage] = useState(1);
  const [items, setItems] = useState([]);
  const [itemPagination, setItemPagination] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);

  useEffect(() => {
    setModelDraft(settings.model || '');
  }, [settings.model]);

  useEffect(() => {
    setAutoRunDraft(Boolean(settings.autoRunNewProducts));
  }, [settings.autoRunNewProducts]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setModelsLoading(true);
      api(`/admin/auto-classifier/models?search=${encodeURIComponent(modelSearch)}&limit=80`)
        .then((result) => {
          setModelOptions(result.models || []);
          setKeyInfo(result.key || null);
        })
        .catch(() => {
          setModelOptions([]);
          setKeyInfo(null);
        })
        .finally(() => setModelsLoading(false));
    }, 180);
    return () => window.clearTimeout(timer);
  }, [modelSearch]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      api(
        `/admin/auto-classifier/items?status=${encodeURIComponent(itemStatus)}&search=${encodeURIComponent(itemSearch)}&page=${itemPage}&limit=${ADMIN_PAGE_LIMIT}`
      )
        .then((result) => {
          setItems(result.items || []);
          setItemPagination(result.pagination || null);
          setSelectedProductId((current) => (result.items || []).some((item) => item.productId === current) ? current : result.items?.[0]?.productId || null);
        })
        .catch(() => {
          setItems([]);
          setItemPagination(null);
        });
    }, 160);
    return () => window.clearTimeout(timer);
  }, [itemStatus, itemSearch, itemPage, data.latest?.id, data.active?.processed]);

  useEffect(() => {
    if (!active?.running) return undefined;
    const timer = window.setInterval(() => {
      reload().catch(() => {});
    }, active.status === 'retrying' ? 15000 : 2500);
    return () => window.clearInterval(timer);
  }, [active?.running, active?.status, reload]);

  useEffect(() => {
    const selectedItem = items.find((item) => item.productId === selectedProductId);
    if (!selectedItem) return;
    if (selectedItem.markId) selector.selectMark(selectedItem.markId);
    else selector.reset();
  }, [selectedProductId, items]);

  useEffect(() => {
    const selectedItem = items.find((item) => item.productId === selectedProductId);
    if (!selectedItem?.markId || !selectedItem?.modelId) return;
    if (selector.selection.markId !== selectedItem.markId) return;
    selector.selectModel(selectedItem.modelId);
  }, [selectedProductId, items, selector.selection.markId]);

  useEffect(() => {
    const selectedItem = items.find((item) => item.productId === selectedProductId);
    if (!selectedItem?.modelId || !selectedItem?.generationId) return;
    if (selector.selection.modelId !== selectedItem.modelId) return;
    selector.selectGeneration(selectedItem.generationId);
  }, [selectedProductId, items, selector.selection.modelId]);

  async function run(action, task, success) {
    setBusy(action);
    setFeedback({ type: '', text: '' });
    try {
      const result = await task();
      await reload();
      setFeedback({ type: 'success', text: success(result) });
    } catch (err) {
      setFeedback({ type: 'error', text: err.message || 'Операция не выполнена' });
    } finally {
      setBusy('');
    }
  }

  const selectedItem = items.find((item) => item.productId === selectedProductId) || null;
  const activeRun = active?.running ? { ...active, status: 'running', running: true } : null;
  const statusRun = activeRun || latest;
  const statusState = classifierRunState(statusRun);
  const scopeCount = classifierScopeCount(stats, scope);
  const requestedLimit = Number(limit || 0) || 0;
  const plannedCount = requestedLimit > 0 ? Math.min(requestedLimit, scopeCount || requestedLimit) : scopeCount;
  const retryErrorsCount = classifierScopeCount(stats, 'errors');

  return (
    <section className="admin-auto-grid">
      <div className="table-panel admin-auto-panel">
        <div className="panel-title">
          <h1>AI классификатор авто</h1>
          <span>{active?.running ? 'идёт обработка' : 'готов к запуску'}</span>
        </div>
        {feedback.text && <div className={feedback.type === 'error' ? 'error' : 'success'}>{feedback.text}</div>}
        <div className="admin-auto-kpis admin-classifier-kpis">
          <article className="admin-auto-kpi">
            <small>Привязано</small>
            <b>{stats.classified || 0}</b>
          </article>
          <article className="admin-auto-kpi">
            <small>Пропущено</small>
            <b>{stats.skipped || 0}</b>
          </article>
          <article className="admin-auto-kpi">
            <small>На модерации</small>
            <b>{stats.manualReview || 0}</b>
          </article>
          <article className={`admin-auto-kpi ${retryErrorsCount > 0 ? 'danger' : ''}`}>
            <small>Ошибки</small>
            <b>{retryErrorsCount}</b>
          </article>
          <article className="admin-auto-kpi">
            <small>Осталось</small>
            <b>{stats.unclassified || 0}</b>
          </article>
        </div>
        <div className="admin-auto-settings admin-classifier-settings">
          <label>
            <span>Модель провайдера</span>
            <AdminModelPicker
              value={modelDraft}
              onChange={setModelDraft}
              models={modelOptions}
              search={modelSearch}
              onSearch={setModelSearch}
              loading={modelsLoading}
            />
          </label>
          <label>
            <span>Щадящий режим очереди</span>
            <input
              className="admin-control-input"
              value={`1 товар • запросы ≥ ${settings.requestIntervalSeconds || 4}с`}
              readOnly
            />
          </label>
          <label className="admin-classifier-toggle">
            <input
              type="checkbox"
              checked={autoRunDraft}
              onChange={(event) => setAutoRunDraft(event.target.checked)}
            />
            <span>Автообработка новых товаров</span>
          </label>
          <IconButton
            icon={busy === 'save-model' ? RefreshCw : Save}
            onClick={() =>
              run(
                'save-model',
                () =>
                  api('/admin/auto-classifier/settings', {
                    method: 'PUT',
                    body: {
                      model: modelDraft,
                      autoRunNewProducts: autoRunDraft
                    }
                  }),
                (result) => {
                  if (result.autoRun?.started) return `Настройки сохранены, автообработка запущена #${result.autoRun.runId}.`;
                  if (result.autoRun?.reason === 'already_running') return `Настройки сохранены, обработка #${result.autoRun.runId} уже выполняется.`;
                  if (result.autoRun?.reason === 'api_key_missing') return 'Настройки сохранены, но ключ провайдера не настроен.';
                  return 'Настройки классификатора сохранены.';
                }
              )
            }
            disabled={!modelDraft.trim() || busy !== ''}
          >
            Сохранить настройки
          </IconButton>
        </div>
        <div className="admin-classifier-controls">
          <select className="admin-control-input" value={scope} onChange={(event) => setScope(event.target.value)}>
            <option value="unclassified">Только новые</option>
            <option value="manual_review">Повторить ручную проверку</option>
            <option value="errors">Повторить ошибки</option>
            <option value="skipped">Повторить пропуски</option>
            <option value="all">Прогнать всё</option>
          </select>
          <input
            className="admin-control-input"
            type="number"
            min="1"
            max="7000"
            value={limit}
            onChange={(event) => setLimit(event.target.value)}
            placeholder="Лимит"
          />
          <div className={`admin-classifier-scope-hint ${scope === 'errors' && scopeCount > 0 ? 'danger' : ''}`}>
            <span>
              В выборке: <b>{scopeCount}</b>
            </span>
            <span>
              Запустится: <b>{plannedCount}</b>
            </span>
          </div>
          <IconButton
            icon={busy === 'start-classifier' ? RefreshCw : Play}
            onClick={() =>
              run(
                'start-classifier',
                () =>
                  api('/admin/auto-classifier/start', {
                    method: 'POST',
                    body: { scope, limit: Number(limit || 0) || 0 }
                  }),
                (result) => (result.alreadyRunning ? `Обработка уже идёт #${result.runId}.` : `Обработка запущена #${result.runId}.`)
              )
            }
            disabled={busy !== '' || active?.running}
          >
            Запустить обработку
          </IconButton>
          <IconButton
            icon={busy === 'cancel-classifier' ? RefreshCw : Square}
            onClick={() =>
              run(
                'cancel-classifier',
                () => api('/admin/auto-classifier/cancel', { method: 'POST' }),
                (result) => (result.cancelled ? 'Остановка обработки запрошена.' : 'Активной обработки нет.')
              )
            }
            disabled={busy !== '' || !active?.running}
          >
            Остановить
          </IconButton>
          <IconButton icon={RefreshCw} onClick={reload} disabled={busy !== ''}>
            Обновить
          </IconButton>
        </div>
        <div className={`import-status admin-classifier-status ${statusState.tone}`}>
          <div className="admin-classifier-status-head">
            <b>{statusRun ? `Обработка #${statusRun.id || statusRun.runId}` : 'Нет запусков'}</b>
            {statusRun && <span className={`admin-classifier-badge ${statusState.tone}`}>{statusState.label}</span>}
          </div>
          <span>{active?.message || latest?.message || 'Пока не было запусков классификатора.'}</span>
          <progress value={Number(active?.processed || latest?.processed || 0)} max={Math.max(1, Number(active?.total || latest?.total || 1))} />
          <small>
            {Number(active?.processed || latest?.processed || 0)} / {Number(active?.total || latest?.total || 0)}
          </small>
          {statusRun && (
            <small>
              Привязано: {statusRun.classified || 0} • Пропущено: {statusRun.skipped || 0} • На модерации: {statusRun.manualReview || 0} • Ошибки:{' '}
              {statusRun.errors || 0}
            </small>
          )}
          <small>
            Токены: {Number(active?.totalTokens || latest?.totalTokens || 0).toLocaleString('ru-RU')} • стоимость: {aiCost(active?.totalCost || latest?.totalCost || 0)}
          </small>
          <small>
            Ключ {settings.providerName || 'провайдера'}: {settings.apiKeyConfigured ? 'подключен' : 'не подключен'} • модель: {settings.model || 'не задана'} • очередь: 1 товар
          </small>
          {active?.queue && (
            <small>
              В очереди: {active.queue.queued || 0} • ретрай: {active.queue.retrying || 0} • выполняется: {active.queue.processing || 0}
              {active.queue.nextRetryAt ? ` • следующий ретрай ${new Date(active.queue.nextRetryAt).toLocaleString('ru-RU')}` : ''}
            </small>
          )}
          <small>
            Паузы: {settings.betweenProductsSeconds || 5}с между товарами • ретраи через {(settings.retryDelaysMinutes || [2, 5, 15, 30]).join(' / ')} мин • максимум {settings.maxAttempts || 5} попыток
          </small>
          <small>
            Лимит ключа: {keyInfo?.data?.limit_remaining ?? '—'} осталось • сегодня: {keyInfo?.data?.usage_daily ?? '—'} • free tier:{' '}
            {keyInfo?.data?.is_free_tier ? 'да' : 'нет'}
          </small>
        </div>
        <div className="admin-auto-files">
          <b>Последние обработки</b>
          <AdminPager
            title="Обработки"
            pagination={data.runsPagination}
            itemCount={runs.length}
            onPageChange={onRunPageChange}
          />
          {(runs || []).map((run) => {
            const runState = classifierRunState(run);
            return (
              <div key={run.id} className={`admin-auto-file admin-classifier-run ${runState.tone}`}>
                <div className="admin-classifier-run-head">
                  <span>
                    #{run.id} • {classifierScopeLabel(run.scope)}
                  </span>
                  <strong className={`admin-classifier-badge ${runState.tone}`}>{runState.label}</strong>
                </div>
                <small>
                  {run.processed}/{run.total} • {aiCost(run.totalCost)} • {run.model}
                </small>
                <div className="admin-classifier-run-metrics">
                  <small>Привязано: {run.classified || 0}</small>
                  <small>Пропущено: {run.skipped || 0}</small>
                  <small>Модерация: {run.manualReview || 0}</small>
                  <small className={Number(run.errors || 0) > 0 ? 'danger' : ''}>Ошибки: {run.errors || 0}</small>
                </div>
              </div>
            );
          })}
          {!runs.length && <div className="category-empty">История запусков пока пустая.</div>}
        </div>
      </div>
      <div className="table-panel admin-auto-panel">
        <div className="panel-title">
          <h1>Ручная модерация</h1>
          <span>{classifierStatusLabel(itemStatus)}</span>
        </div>
        <div className="admin-classifier-filters">
          <select
            className="admin-control-input"
            value={itemStatus}
            onChange={(event) => {
              setItemStatus(event.target.value);
              setItemPage(1);
            }}
          >
            <option value="manual_review">На модерации</option>
            <option value="error">Ошибки</option>
            <option value="skipped">Пропущенные</option>
            <option value="classified">Автопривязанные</option>
            <option value="manual_assigned">Ручные привязки</option>
            <option value="unclassified">Необработанные</option>
            <option value="all">Все</option>
          </select>
          <div className="searchbar compact">
            <Search size={18} />
            <input
              value={itemSearch}
              aria-label="Поиск товара для ручной модерации"
              onChange={(event) => {
                setItemSearch(event.target.value);
                setItemPage(1);
              }}
              placeholder="Артикул или название"
            />
          </div>
        </div>
        <AdminPager
          title="Товары"
          pagination={itemPagination}
          itemCount={items.length}
          onPageChange={setItemPage}
        />
        <div className="admin-classifier-review">
          <div className="admin-classifier-list">
            {items.map((item) => (
              <button
                key={item.productId}
                className={`admin-classifier-item ${selectedProductId === item.productId ? 'active' : ''}`}
                onClick={() => setSelectedProductId(item.productId)}
              >
                <div className="admin-ai-run-top">
                  <b>{item.vendorCode}</b>
                  <strong>{classifierStatusLabel(item.status)}</strong>
                </div>
                <span>{item.name}</span>
                <small>
                  {item.brandHint || '—'} / {item.modelHint || '—'} / {item.generationHint || '—'}
                </small>
                <em>{item.reason || 'Без комментария'}</em>
              </button>
            ))}
            {!items.length && <div className="category-empty">В этой выборке товаров пока нет.</div>}
          </div>
          <div className="admin-classifier-detail">
            {selectedItem ? (
              <>
                <div className="admin-auto-target">
                  <b>
                    {selectedItem.vendorCode} • {selectedItem.name}
                  </b>
                  <span>
                    {classifierStatusLabel(selectedItem.status)} • {selectedItem.confidence || 0}% • {aiCost(selectedItem.totalCost || 0)}
                  </span>
                </div>
                <div className="admin-classifier-meta">
                  <small>AI увидела: {selectedItem.brandHint || '—'} / {selectedItem.modelHint || '—'} / {selectedItem.generationHint || '—'}</small>
                  <small>Резолв: {selectedItem.resolvedMarkName || '—'} / {selectedItem.resolvedModelName || '—'}</small>
                  <small>Привязки: {selectedItem.linkedGenerationNames || selectedItem.generationName || '—'}</small>
                  <small>{selectedItem.reason || 'Причина не сохранена'}</small>
                </div>
                <div className="admin-auto-selector">
                  <select className="admin-control-input" value={selector.selection.markId} onChange={(event) => selector.selectMark(event.target.value)}>
                    <option value="">Марка</option>
                    {selector.marks.map((mark) => (
                      <option key={mark.id} value={mark.id}>
                        {mark.name}
                      </option>
                    ))}
                  </select>
                  <select
                    className="admin-control-input"
                    value={selector.selection.modelId}
                    onChange={(event) => selector.selectModel(event.target.value)}
                    disabled={!selector.selection.markId}
                  >
                    <option value="">Модель</option>
                    {selector.models.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name}
                      </option>
                    ))}
                  </select>
                  <select
                    className="admin-control-input"
                    value={selector.selection.generationId}
                    onChange={(event) => selector.selectGeneration(event.target.value)}
                    disabled={!selector.selection.modelId}
                  >
                    <option value="">Поколение</option>
                    {selector.generations.map((generation) => (
                      <option key={generation.id} value={generation.id}>
                        {generationOptionLabel(generation)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="button-row">
                  <IconButton
                    icon={Save}
                    onClick={() =>
                      run(
                        `resolve-${selectedItem.productId}`,
                        () =>
                          api(`/admin/auto-classifier/items/${selectedItem.productId}/resolve`, {
                            method: 'POST',
                            body: { action: 'assign', generationId: selector.selection.generationId }
                          }),
                        () => `Товар ${selectedItem.vendorCode} вручную привязан к поколению.`
                      )
                    }
                    disabled={!selector.selection.generationId || busy !== ''}
                  >
                    Привязать вручную
                  </IconButton>
                  <IconButton
                    icon={Square}
                    onClick={() =>
                      run(
                        `skip-${selectedItem.productId}`,
                        () =>
                          api(`/admin/auto-classifier/items/${selectedItem.productId}/resolve`, {
                            method: 'POST',
                            body: { action: 'skip' }
                          }),
                        () => `Товар ${selectedItem.vendorCode} помечен как пропущенный.`
                      )
                    }
                    disabled={busy !== ''}
                  >
                    Пометить пропуском
                  </IconButton>
                </div>
                <details className="admin-classifier-raw">
                  <summary>AI payload</summary>
                  <pre>{JSON.stringify(selectedItem.raw, null, 2)}</pre>
                </details>
              </>
            ) : (
              <div className="category-empty">Выбери товар слева, чтобы посмотреть решение AI и вручную поправить привязку.</div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function AdminPager({ title, pagination, itemCount, onPageChange }) {
  const page = pagination?.page || 1;
  const limit = pagination?.limit || Math.max(1, itemCount || 1);
  const total = pagination?.total ?? itemCount ?? 0;
  const totalPages = pagination?.totalPages || Math.max(1, Math.ceil(total / limit));
  const from = total ? (page - 1) * limit + 1 : 0;
  const to = total ? Math.min(total, from + itemCount - 1) : 0;

  return (
    <div className="table-toolbar">
      <div>
        <b>{title}</b>
        <span>
          {from}-{to} из {total}
        </span>
      </div>
      <div className="pager">
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1} title="Предыдущая страница">
          <ChevronLeft size={16} />
        </button>
        <span>
          {page} / {totalPages}
        </span>
        <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages} title="Следующая страница">
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

function Admin() {
  const [tab, setTab] = useState('orders');
  const workspaceRef = useRef(null);
  const [data, setData] = useState({});
  const [search, setSearch] = useState('');
  const [orderPage, setOrderPage] = useState(1);
  const [returnPage, setReturnPage] = useState(1);
  const [userPage, setUserPage] = useState(1);
  const [productPage, setProductPage] = useState(1);
  const [aiRunPage, setAiRunPage] = useState(1);
  const [classifierRunPage, setClassifierRunPage] = useState(1);
  const [aiUserId, setAiUserId] = useState('');
  const [selectedAdminUserId, setSelectedAdminUserId] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [loadStatus, setLoadStatus] = useState('loading');
  const [loadError, setLoadError] = useState('');
  const loadRequestRef = useRef(0);
  const loadedAdminKeyRef = useRef('');
  const currentAdminLoadKeyRef = useRef('');
  const [newUser, setNewUser] = useState({
    contractorName: '',
    email: '',
    priceTier: 'B',
    managerId: '',
    role: 'customer'
  });

  const adminLoadKey = [
    tab,
    tab === 'orders' ? orderPage : '',
    tab === 'returns' ? returnPage : '',
    tab === 'users' ? `${search}:${userPage}` : '',
    tab === 'products' ? `${search}:${productPage}` : '',
    tab === 'ai' ? `${aiUserId}:${aiRunPage}` : '',
    tab === 'classifier' ? classifierRunPage : ''
  ].join('|');
  currentAdminLoadKeyRef.current = adminLoadKey;
  const adminDataIsCurrent = loadedAdminKeyRef.current === adminLoadKey;

  async function load() {
    if (currentAdminLoadKeyRef.current !== adminLoadKey) return;
    const requestId = loadRequestRef.current + 1;
    loadRequestRef.current = requestId;
    const isBackgroundRefresh = loadedAdminKeyRef.current === adminLoadKey;
    const commitData = (nextData) => {
      if (loadRequestRef.current === requestId && currentAdminLoadKeyRef.current === adminLoadKey) setData(nextData);
    };
    if (!isBackgroundRefresh) {
      setLoadStatus('loading');
      setData({});
    }
    setLoadError('');
    try {
      if (tab === 'orders') commitData(await api(`/admin/orders?page=${orderPage}&limit=${ADMIN_PAGE_LIMIT}`));
      if (tab === 'returns') commitData(await api(`/admin/returns?page=${returnPage}&limit=${ADMIN_PAGE_LIMIT}`));
      if (tab === 'users') commitData(await api(`/admin/users?search=${encodeURIComponent(search)}&page=${userPage}&limit=${ADMIN_WIDE_PAGE_LIMIT}`));
      if (tab === 'products') commitData(await api(`/admin/products?search=${encodeURIComponent(search)}&page=${productPage}&limit=${ADMIN_WIDE_PAGE_LIMIT}`));
      if (tab === 'ms') commitData(await api('/admin/ms/status'));
      if (tab === 'import') {
        const [status, settings] = await Promise.all([
          api('/admin/import/status'),
          api('/admin/import/settings')
        ]);
        commitData({ status, settings });
      }
      if (tab === 'ai') {
        const aiRunsQuery = new URLSearchParams({ page: String(aiRunPage), limit: String(ADMIN_PAGE_LIMIT) });
        if (aiUserId) aiRunsQuery.set('userId', aiUserId);
        const [status, runs] = await Promise.all([api('/admin/ai/status'), api(`/admin/ai/runs?${aiRunsQuery}`)]);
        commitData({ ...status, runs: runs.runs || [], aiClients: runs.clients || [], runsPagination: runs.pagination });
      }
      if (tab === 'auto') {
        const [status, downloads] = await Promise.all([
          api('/admin/auto-catalog/status'),
          api('/admin/auto-catalog/downloads').catch(() => ({ files: [] }))
        ]);
        commitData({ ...status, downloads });
      }
      if (tab === 'classifier') {
        const [status, runs] = await Promise.all([
          api('/admin/auto-classifier/status'),
          api(`/admin/auto-classifier/runs?page=${classifierRunPage}&limit=${ADMIN_PAGE_LIMIT}`)
        ]);
        commitData({ ...status, runs: runs.runs || [], runsPagination: runs.pagination });
      }
      if (loadRequestRef.current === requestId && currentAdminLoadKeyRef.current === adminLoadKey) {
        loadedAdminKeyRef.current = adminLoadKey;
        setLoadStatus('success');
      }
    } catch (err) {
      if (loadRequestRef.current !== requestId || currentAdminLoadKeyRef.current !== adminLoadKey) return;
      setLoadError(err.message);
      setLoadStatus(isBackgroundRefresh ? 'success' : 'error');
    }
  }

  useEffect(() => {
    load();
  }, [tab, search, orderPage, returnPage, userPage, productPage, aiRunPage, classifierRunPage, aiUserId]);

  function changeAdminSearch(value) {
    setSearch(value);
    setUserPage(1);
    setProductPage(1);
  }

  function changeAiUser(value) {
    setAiUserId(value);
    setAiRunPage(1);
  }

  function selectAdminTab(nextTab) {
    setTab(nextTab);
    if (nextTab !== 'users') setSelectedAdminUserId(null);
    window.requestAnimationFrame(() => {
      workspaceRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      workspaceRef.current?.focus({ preventScroll: true });
    });
  }

  async function registerUser(event) {
    event.preventDefault();
    const created = await api('/admin/users', { method: 'POST', body: newUser });
    setCredentials(created.credentials);
    setNewUser({ contractorName: '', email: '', priceTier: 'B', managerId: '', role: 'customer' });
    await load();
  }

  async function saveProduct(product) {
    await api(`/admin/products/${product.id}`, { method: 'PATCH', body: product });
    await load();
  }

  return (
    <main className="admin-layout">
      <aside className="admin-tabs" aria-label="Разделы админки">
        <button className={tab === 'orders' ? 'selected' : ''} onClick={() => selectAdminTab('orders')} aria-pressed={tab === 'orders'}>
          <ClipboardList size={18} /> Заказы
        </button>
        <button className={tab === 'returns' ? 'selected' : ''} onClick={() => selectAdminTab('returns')} aria-pressed={tab === 'returns'}>
          <Truck size={18} /> Возвраты
        </button>
        <button className={tab === 'users' ? 'selected' : ''} onClick={() => selectAdminTab('users')} aria-pressed={tab === 'users'}>
          <Users size={18} /> Клиенты
        </button>
        <button className={tab === 'products' ? 'selected' : ''} onClick={() => selectAdminTab('products')} aria-pressed={tab === 'products'}>
          <Package size={18} /> Товары
        </button>
        <button className={tab === 'import' ? 'selected' : ''} onClick={() => selectAdminTab('import')} aria-pressed={tab === 'import'}>
          <Database size={18} /> Импорт
        </button>
        <button className={tab === 'ms' ? 'selected' : ''} onClick={() => selectAdminTab('ms')} aria-pressed={tab === 'ms'}>
          <Settings size={18} /> МойСклад
        </button>
        <button className={tab === 'auto' ? 'selected' : ''} onClick={() => selectAdminTab('auto')} aria-pressed={tab === 'auto'}>
          <CarFront size={18} /> Авто
        </button>
        <button className={tab === 'classifier' ? 'selected' : ''} onClick={() => selectAdminTab('classifier')} aria-pressed={tab === 'classifier'}>
          <Bot size={18} /> Классификатор
        </button>
        <button className={tab === 'ai' ? 'selected' : ''} onClick={() => selectAdminTab('ai')} aria-pressed={tab === 'ai'}>
          <Bot size={18} /> AI
        </button>
      </aside>
      <section className="admin-workspace" ref={workspaceRef} tabIndex="-1" aria-labelledby="admin-workspace-title">
        <h1 className="admin-workspace-title" id="admin-workspace-title">
          {ADMIN_TAB_LABELS[tab]}
        </h1>
        {tab !== 'orders' && tab !== 'returns' && tab !== 'import' && tab !== 'ms' && tab !== 'ai' && tab !== 'auto' && tab !== 'classifier' && !(tab === 'users' && selectedAdminUserId) && (
          <div className="searchbar compact">
            <Search size={18} />
            <input
              value={search}
              aria-label="Поиск в текущем разделе админки"
              onChange={(event) => changeAdminSearch(event.target.value)}
            />
          </div>
        )}
        {loadStatus === 'loading' || (loadStatus === 'success' && !adminDataIsCurrent) ? (
          <LoadingState variant="admin" count={5} label={`Загружаем раздел «${ADMIN_TAB_LABELS[tab]}»…`} />
        ) : null}
        {loadStatus === 'error' ? (
          <ErrorState
            title={`Не удалось загрузить раздел «${ADMIN_TAB_LABELS[tab]}»`}
            message={loadError}
            onRetry={load}
          />
        ) : null}
        {loadStatus === 'success' && adminDataIsCurrent && loadError ? (
          <InlineNotice tone="error">Не удалось обновить данные: {loadError}</InlineNotice>
        ) : null}
        {loadStatus === 'success' && adminDataIsCurrent ? (
          <>
        {tab === 'orders' && (
          <AdminOrders orders={data.orders || []} pagination={data.pagination} onPageChange={setOrderPage} reload={load} />
        )}
        {tab === 'returns' && <AdminReturns data={data} pagination={data.pagination} onPageChange={setReturnPage} reload={load} />}
        {tab === 'users' && (
          <>
            {!selectedAdminUserId && <form className="admin-form" onSubmit={registerUser}>
              <input
                placeholder="Имя контрагента"
                aria-label="Имя нового контрагента"
                value={newUser.contractorName}
                onChange={(event) => setNewUser({ ...newUser, contractorName: event.target.value })}
              />
              <input
                placeholder="Почта"
                aria-label="Почта нового контрагента"
                value={newUser.email}
                onChange={(event) => setNewUser({ ...newUser, email: event.target.value })}
              />
              <select
                value={newUser.priceTier}
                aria-label="Прайс нового контрагента"
                onChange={(event) => setNewUser({ ...newUser, priceTier: event.target.value })}
              >
                {['B', 'B-5', 'B-8', 'B-10'].map((tier) => (
                  <option key={tier}>{tier}</option>
                ))}
              </select>
              <select
                value={newUser.managerId}
                aria-label="Менеджер нового контрагента"
                onChange={(event) => setNewUser({ ...newUser, managerId: event.target.value })}
              >
                <option value="">Менеджер</option>
                {(data.managers || []).map((manager) => (
                  <option key={manager.id} value={manager.id}>
                    {manager.name}
                  </option>
                ))}
              </select>
              <select
                value={newUser.role}
                aria-label="Роль нового контрагента"
                onChange={(event) => setNewUser({ ...newUser, role: event.target.value })}
              >
                <option value="customer">Клиент</option>
                <option value="admin">Админ</option>
              </select>
              <IconButton icon={Plus}>Создать</IconButton>
            </form>}
            {!selectedAdminUserId && credentials && (
              <div className="copy-box">
                <code>
                  Логин: {credentials.login}
                  {'\n'}Пароль: {credentials.password}
                </code>
                <button
                  type="button"
                  onClick={() =>
                    navigator.clipboard.writeText(`Логин: ${credentials.login}\nПароль: ${credentials.password}`)
                  }
                  aria-label="Скопировать логин и пароль"
                  title="Скопировать логин и пароль"
                >
                  <Copy size={18} aria-hidden="true" />
                </button>
              </div>
            )}
            <AdminClientDirectory
              users={data.users || []}
              managers={data.managers || []}
              priceTiers={data.priceTiers || ['B', 'B-5', 'B-8', 'B-10']}
              pagination={data.pagination}
              onPageChange={setUserPage}
              onReload={load}
              selectedUserId={selectedAdminUserId}
              onSelectUser={setSelectedAdminUserId}
            />
          </>
        )}
        {tab === 'products' && (
          <AdminProducts products={data.products || []} pagination={data.pagination} onPageChange={setProductPage} onSave={saveProduct} />
        )}
        {tab === 'import' && <AdminImport data={data} reload={load} />}
        {tab === 'ms' && <AdminMoySklad data={data} reload={load} />}
        {tab === 'auto' && <AdminAuto data={data} reload={load} />}
        {tab === 'classifier' && <AdminClassifier data={data} reload={load} onRunPageChange={setClassifierRunPage} />}
        {tab === 'ai' && <AdminAI data={data} reload={load} selectedUserId={aiUserId} onUserChange={changeAiUser} onRunPageChange={setAiRunPage} />}
          </>
        ) : null}
      </section>
    </main>
  );
}

function AdminOrders({ orders, pagination, onPageChange, reload }) {
  async function exportMs(id) {
    await api(`/admin/orders/${id}/export-ms`, { method: 'POST' });
    await reload();
  }
  return (
    <>
      <AdminPager title="Заказы" pagination={pagination} itemCount={orders.length} onPageChange={onPageChange} />
      <div className="data-table admin-scroll-table admin-cardable-table">
        <div className="data-head five">
          <b>Номер</b>
          <b>Клиент</b>
          <b>Сумма</b>
          <b>МС</b>
          <b></b>
        </div>
        {orders.map((order) => (
          <div className="data-row five admin-compact-card" key={order.id}>
            <span data-label="Номер">{order.order_number}</span>
            <span data-label="Клиент">{order.contractor_name || order.customer_name}</span>
            <strong data-label="Сумма">{money(order.total)}</strong>
            <StatusBadge data-label="МойСклад" status={order.ms_export_status} />
            <div className="order-actions" data-label="Действия">
              {order.ms_ui_url && (
                <a href={order.ms_ui_url} target="_blank" rel="noreferrer" title="Открыть заказ в МойСклад">
                  <ExternalLink size={16} />
                </a>
              )}
              <button onClick={() => exportMs(order.id)}>Выгрузить</button>
            </div>
          </div>
        ))}
        {!orders.length && <div className="category-empty">Заказов пока нет.</div>}
      </div>
    </>
  );
}

function AdminReturns({ data, pagination, onPageChange, reload }) {
  const [busyAction, setBusyAction] = useState('');
  const [feedback, setFeedback] = useState({ type: '', text: '' });
  const returns = data.returns || [];
  const sync = data.cdekPointSync || {};

  async function runAction(action, task, successText) {
    setBusyAction(action);
    setFeedback({ type: '', text: '' });
    try {
      await task();
      await reload();
      setFeedback({ type: 'success', text: successText });
    } catch (err) {
      setFeedback({ type: 'error', text: err.message || 'Операция не выполнена' });
    } finally {
      setBusyAction('');
    }
  }

  return (
    <div className="admin-returns">
      <section className="table-panel">
        <div className="panel-title">
          <h1>Возвраты покупателей</h1>
          <span>
            СДЭК {data.cdek?.configured ? 'настроен' : 'не настроен'} · Синхронизация ПВЗ: {statusLabel(sync.status || 'idle')}
          </span>
        </div>
        {feedback.text && <div className={feedback.type === 'error' ? 'error' : 'success'}>{feedback.text}</div>}
        <div className="button-row">
          <IconButton
            icon={busyAction === 'sync' ? RefreshCw : RefreshCw}
            disabled={busyAction !== ''}
            className={busyAction === 'sync' ? 'is-busy' : ''}
            onClick={() =>
              runAction(
                'sync',
                () => api('/admin/returns/sync-cdek-points', { method: 'POST' }),
                'Синхронизация CDEK-ПВЗ запущена.'
              )
            }
          >
            Синхронизировать ПВЗ
          </IconButton>
        </div>
      </section>
      <AdminPager title="Возвраты" pagination={pagination} itemCount={returns.length} onPageChange={onPageChange} />
      <div className="data-table admin-scroll-table admin-cardable-table">
        <div className="data-head returns">
          <b>Возврат</b>
          <b>Клиент</b>
          <b>CDEK</b>
          <b>МС</b>
          <b>Сумма</b>
          <b>Дата</b>
          <b></b>
        </div>
        {returns.map((item) => (
          <React.Fragment key={item.id}>
            <div className="data-row returns admin-compact-card">
              <span data-label="Возврат">{item.returnNumber}</span>
              <span data-label="Клиент">{item.contractorName || item.email}</span>
              {item.cdekNumber || item.cdekOrderUuid ? (
                <span data-label="СДЭК">{item.cdekNumber || item.cdekOrderUuid}</span>
              ) : (
                <StatusBadge data-label="СДЭК" status={item.cdekProcessingState || item.status} />
              )}
              <StatusBadge data-label="МойСклад" status={item.msExportStatus} />
              <strong data-label="Сумма">{money(item.total)}</strong>
              <span data-label="Дата">{new Date(item.createdAt).toLocaleString('ru-RU')}</span>
              <div className="order-actions" data-label="Действия">
                {item.msUiUrl && (
                  <a href={item.msUiUrl} target="_blank" rel="noreferrer" title="Открыть возврат в МойСклад">
                    <ExternalLink size={16} />
                  </a>
                )}
                <button
                  onClick={() =>
                    runAction(
                      `ms-${item.id}`,
                      () => api(`/admin/returns/${item.id}/export-ms`, { method: 'POST' }),
                      `Возврат ${item.returnNumber} выгружен в МойСклад.`
                    )
                  }
                  disabled={busyAction !== ''}
                >
                  {busyAction === `ms-${item.id}` ? <RefreshCw className="spin" size={16} /> : 'МС'}
                </button>
              </div>
            </div>
            {item.items?.length > 0 && (
              <div className="admin-return-items">
                {item.items.map((returnItem) => (
                  <div key={returnItem.id || returnItem.vendorCode} className="admin-return-item">
                    <b>{returnItem.vendorCode}</b>
                    <span>{returnItem.name}</span>
                    <small>{returnItem.qty} шт.</small>
                    <em>{returnItem.reason || 'Причина не указана'}</em>
                  </div>
                ))}
              </div>
            )}
          </React.Fragment>
        ))}
        {!returns.length && <div className="category-empty">Возвратов пока нет.</div>}
      </div>
    </div>
  );
}

function AdminClientDirectory({ users, managers, priceTiers, pagination, onPageChange, onReload, selectedUserId, onSelectUser }) {
  const [details, setDetails] = useState(null);
  const [draft, setDraft] = useState(null);
  const [detailStatus, setDetailStatus] = useState('idle');
  const [feedback, setFeedback] = useState({ type: '', text: '' });
  const [busyAction, setBusyAction] = useState('');
  const [confirmReturnReset, setConfirmReturnReset] = useState(false);
  const [confirmExportDisable, setConfirmExportDisable] = useState(false);
  const [activeSession, setActiveSession] = useState(null);

  async function loadDetails(userId = selectedUserId) {
    if (!userId) return;
    setDetailStatus('loading');
    try {
      const nextDetails = await api(`/admin/users/${userId}`);
      setDetails(nextDetails);
      setDraft({
        id: nextDetails.user.id,
        role: nextDetails.user.role || 'customer',
        name: nextDetails.user.name || nextDetails.user.contractor_name || nextDetails.user.email,
        contractorName: nextDetails.user.contractor_name || nextDetails.user.name || '',
        phone: nextDetails.user.phone || '',
        priceTier: nextDetails.user.price_tier || 'B',
        managerId: nextDetails.user.manager_id || '',
        isActive: Boolean(nextDetails.user.is_active)
      });
      setDetailStatus('success');
    } catch (err) {
      setFeedback({ type: 'error', text: err.message || 'Не удалось загрузить карточку клиента.' });
      setDetailStatus('error');
    }
  }

  useEffect(() => {
    setDetails(null);
    setDraft(null);
    setFeedback({ type: '', text: '' });
    setConfirmReturnReset(false);
    setConfirmExportDisable(false);
    setActiveSession(null);
    if (selectedUserId) loadDetails(selectedUserId);
  }, [selectedUserId]);

  function updateDraft(patch) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  async function saveClient() {
    setBusyAction('save');
    setFeedback({ type: '', text: '' });
    try {
      await api(`/admin/users/${draft.id}`, { method: 'PATCH', body: draft });
      await Promise.all([onReload(), loadDetails()]);
      setFeedback({ type: 'success', text: 'Данные клиента сохранены.' });
    } catch (err) {
      setFeedback({ type: 'error', text: err.message || 'Не удалось сохранить клиента.' });
    } finally {
      setBusyAction('');
    }
  }

  async function changePassword(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const password = String(new FormData(form).get('password') || '').trim();
    if (password.length < 8) {
      setFeedback({ type: 'error', text: 'Пароль должен быть не короче 8 символов.' });
      return;
    }
    setBusyAction('password');
    setFeedback({ type: '', text: '' });
    try {
      await api(`/admin/users/${selectedUserId}/password`, { method: 'POST', body: { password } });
      form.reset();
      setFeedback({ type: 'success', text: `Пароль для ${details.user.email} обновлён.` });
    } catch (err) {
      setFeedback({ type: 'error', text: err.message || 'Не удалось обновить пароль.' });
    } finally {
      setBusyAction('');
    }
  }

  async function resetReturnLimit() {
    setBusyAction('return');
    setFeedback({ type: '', text: '' });
    try {
      await api(`/admin/users/${selectedUserId}/return-limit/reset`, { method: 'POST' });
      await loadDetails();
      setConfirmReturnReset(false);
      setFeedback({
        type: 'success',
        text: `Время ожидания возврата для ${details.user.email} сброшено. Следующий созданный возврат снова запустит 30 дней.`
      });
    } catch (err) {
      setFeedback({ type: 'error', text: err.message || 'Не удалось сбросить ожидание возврата.' });
    } finally {
      setBusyAction('');
    }
  }

  async function disableClientExport() {
    setBusyAction('export');
    setFeedback({ type: '', text: '' });
    try {
      await api(`/admin/users/${selectedUserId}/price-export`, { method: 'DELETE' });
      await loadDetails();
      setConfirmExportDisable(false);
      setFeedback({ type: 'success', text: `Ссылка на прайс для ${details.user.email} отключена.` });
    } catch (err) {
      setFeedback({ type: 'error', text: err.message || 'Не удалось отключить ссылку на прайс.' });
    } finally {
      setBusyAction('');
    }
  }

  async function openSession(session) {
    if (activeSession?.id === session.id) {
      setActiveSession(null);
      return;
    }
    setActiveSession({ ...session, status: 'loading', messages: [] });
    try {
      const result = await api(`/admin/users/${selectedUserId}/ai-sessions/${session.id}/messages`);
      setActiveSession({ ...session, status: 'success', messages: result.messages || [] });
    } catch (err) {
      setActiveSession({ ...session, status: 'error', error: err.message, messages: [] });
    }
  }

  if (!selectedUserId) {
    return (
      <section className="admin-client-directory">
        <AdminPager title="Клиенты" pagination={pagination} itemCount={users.length} onPageChange={onPageChange} />
        <div className="admin-client-list">
          <div className="admin-client-list-head" aria-hidden="true">
            <b>Клиент</b>
            <b>Менеджер</b>
            <b>Роль</b>
            <b>Прайс</b>
            <b>Последний вход</b>
            <span />
          </div>
          {users.map((user) => (
            <button
              type="button"
              className="admin-client-list-row"
              key={user.id}
              onClick={() => onSelectUser(user.id)}
            >
              <span className="admin-client-list-person">
                <b>{user.contractor_name || user.name || user.email}</b>
                <small>{user.email}</small>
              </span>
              <span data-label="Менеджер">{user.manager_name || 'Не назначен'}</span>
              <span data-label="Роль">{user.role === 'admin' ? 'Админ' : 'Клиент'}</span>
              <span data-label="Прайс">{user.price_tier || 'B'}</span>
              <span data-label="Последний вход">{user.last_login_at ? formatDateTime(user.last_login_at) : 'Ещё не входил'}</span>
              <span className="admin-client-list-open" aria-label="Открыть карточку">
                <ChevronRight size={18} />
              </span>
            </button>
          ))}
          {!users.length && <div className="category-empty">Клиенты не найдены.</div>}
        </div>
      </section>
    );
  }

  if (detailStatus === 'loading' || !details || !draft) {
    return (
      <section className="admin-client-card-shell">
        <button type="button" className="admin-client-back" onClick={() => onSelectUser(null)}>
          <ArrowLeft size={17} /> Все клиенты
        </button>
        {detailStatus === 'error' ? (
          <ErrorState title="Карточка клиента не загрузилась" message={feedback.text} onRetry={() => loadDetails()} />
        ) : (
          <LoadingState variant="admin" count={5} label="Загружаем карточку клиента…" />
        )}
      </section>
    );
  }

  const user = details.user;
  const activity = details.activity || {};
  const returnLimit = details.returnLimit || {};
  const priceExport = details.priceExport || null;
  const ai = details.ai || {};

  return (
    <section className="admin-client-card-shell">
      <button type="button" className="admin-client-back" onClick={() => onSelectUser(null)}>
        <ArrowLeft size={17} /> Все клиенты
      </button>
      {feedback.text && <div className={feedback.type === 'error' ? 'error' : 'success'}>{feedback.text}</div>}

      <header className="admin-client-identity">
        <div>
          <h2>{user.contractor_name || user.name || user.email}</h2>
          <p>{user.email}</p>
          <div className="admin-client-badges">
            <span className={draft.isActive ? 'is-active' : 'is-inactive'}>{draft.isActive ? 'Активен' : 'Отключён'}</span>
            <span>{draft.role === 'admin' ? 'Админ' : 'Клиент'}</span>
            <span>Прайс {draft.priceTier}</span>
          </div>
        </div>
        <button type="button" className="admin-primary-action" onClick={saveClient} disabled={Boolean(busyAction)}>
          {busyAction === 'save' ? <RefreshCw className="spin" size={17} /> : <Save size={17} />}
          Сохранить изменения
        </button>
      </header>

      <section className="admin-client-section">
        <div className="admin-client-section-title">
          <div><Users size={18} /><h3>Основные данные</h3></div>
          <small>МойСклад: {user.ms_counterparty_id ? 'связан' : 'не связан'}</small>
        </div>
        <div className="admin-client-fields">
          <label><span>Контрагент</span><input value={draft.contractorName} onChange={(event) => updateDraft({ contractorName: event.target.value, name: event.target.value })} /></label>
          <label><span>Почта</span><input value={user.email} readOnly /></label>
          <label><span>Телефон</span><input value={draft.phone} onChange={(event) => updateDraft({ phone: event.target.value })} /></label>
          <label><span>Старый логин</span><input value={user.legacy_login || ''} readOnly placeholder="Нет" /></label>
          <label><span>Менеджер</span><select value={draft.managerId} onChange={(event) => updateDraft({ managerId: event.target.value })}><option value="">Не назначен</option>{managers.map((manager) => <option key={manager.id} value={manager.id}>{manager.name}</option>)}</select></label>
          <label><span>Роль</span><select value={draft.role} onChange={(event) => updateDraft({ role: event.target.value })}><option value="customer">Клиент</option><option value="admin">Админ</option></select></label>
          <label><span>Прайс</span><select value={draft.priceTier} onChange={(event) => updateDraft({ priceTier: event.target.value })}>{priceTiers.map((tier) => <option key={tier}>{tier}</option>)}</select></label>
          <label className="admin-client-active-toggle"><input type="checkbox" checked={draft.isActive} onChange={(event) => updateDraft({ isActive: event.target.checked })} /><span>Доступ к сайту активен</span></label>
        </div>
      </section>

      <section className="admin-client-activity" aria-label="Активность клиента">
        <article><Clock size={24} /><span><small>Последний вход</small><b>{user.last_login_at ? formatDateTime(user.last_login_at) : 'Ещё не входил'}</b></span></article>
        <article><Activity size={24} /><span><small>Визиты за 30 дней</small><b>{activity.sessions30d || 0}</b><em>{activity.activeDays30d || 0} активных дней</em></span></article>
        <article><Clock size={24} /><span><small>Время на сайте</small><b>{durationLabel(activity.totalSeconds)}</b></span></article>
        <article><Clock size={24} /><span><small>Средняя сессия</small><b>{durationLabel(activity.averageSessionSeconds)}</b></span></article>
        <p>Активность и время на сайте накапливаются с момента этого обновления.</p>
      </section>

      <div className="admin-client-settings-row">
        <section className="admin-client-section admin-client-return-card">
          <div className="admin-client-section-title"><div><RotateCcw size={18} /><h3>Возвраты</h3></div></div>
          <div className={returnLimit.canCreate ? 'admin-return-state ready' : 'admin-return-state locked'}>
            <RotateCcw size={25} />
            <span><b>{returnLimit.canCreate ? 'Новый возврат доступен' : `Доступен через ${durationLabel(returnLimit.secondsRemaining)}`}</b><small>Ограничение: один возврат раз в 30 дней</small></span>
          </div>
          {!confirmReturnReset ? (
            <button type="button" onClick={() => setConfirmReturnReset(true)} disabled={returnLimit.canCreate || Boolean(busyAction)}>Сбросить ожидание</button>
          ) : (
            <div className="admin-client-inline-confirm"><p>Сбросится только текущее ожидание. История возвратов останется.</p><div><button type="button" onClick={() => setConfirmReturnReset(false)}>Отмена</button><button type="button" className="admin-primary-action" onClick={resetReturnLimit} disabled={Boolean(busyAction)}>{busyAction === 'return' ? <RefreshCw className="spin" size={16} /> : <RotateCcw size={16} />} Разрешить возврат</button></div></div>
          )}
        </section>

        <section className="admin-client-section">
          <div className="admin-client-section-title"><div><KeyRound size={18} /><h3>Безопасность</h3></div></div>
          <form className="admin-client-password" onSubmit={changePassword}>
            <label><span>Новый пароль</span><input name="password" type="password" minLength="8" autoComplete="new-password" placeholder="Минимум 8 символов" /></label>
            <button type="submit" className="admin-primary-action" disabled={Boolean(busyAction)}>{busyAction === 'password' ? <RefreshCw className="spin" size={16} /> : <Shield size={16} />} Обновить пароль</button>
          </form>
        </section>
      </div>

      <section className="admin-client-section admin-client-export">
        <div className="admin-client-section-title">
          <div><FileSpreadsheet size={18} /><h3>Экспорт прайса</h3></div>
          <small>{priceExport ? 'Ссылка активна' : 'Ссылка не создана'}</small>
        </div>
        {priceExport ? (
          <div className="admin-client-export-body">
            <dl className="admin-client-export-facts">
              <div><dt>Обновление</dt><dd>{priceExportIntervalOption(priceExport.interval).label}</dd></div>
              <div><dt>Создана</dt><dd>{formatDateTime(priceExport.createdAt)}</dd></div>
              <div><dt>Последнее скачивание</dt><dd>{priceExport.lastDownloadAt ? formatDateTime(priceExport.lastDownloadAt) : 'Ещё не скачивали'}</dd></div>
              <div><dt>Скачиваний</dt><dd>{priceExport.downloadCount || 0}</dd></div>
            </dl>
            {!confirmExportDisable ? (
              <button type="button" onClick={() => setConfirmExportDisable(true)} disabled={Boolean(busyAction)}>Отключить ссылку</button>
            ) : (
              <div className="admin-client-inline-confirm"><p>Ссылка клиента сразу перестанет работать. Новую клиент сможет создать сам в кабинете, в разделе «Экспорт».</p><div><button type="button" onClick={() => setConfirmExportDisable(false)}>Отмена</button><button type="button" className="admin-primary-action" onClick={disableClientExport} disabled={Boolean(busyAction)}>{busyAction === 'export' ? <RefreshCw className="spin" size={16} /> : <Link2Off size={16} />} Отключить</button></div></div>
            )}
          </div>
        ) : (
          <p className="admin-client-export-empty">Клиент ещё не создавал ссылку на прайс в разделе «Экспорт» личного кабинета.</p>
        )}
      </section>

      <section className="admin-client-section admin-client-ai">
        <div className="admin-client-ai-head">
          <div className="admin-client-section-title"><div><Bot size={18} /><h3>AI-агент</h3></div></div>
          <div className="admin-client-ai-kpis">
            <span><WalletCards size={16} /><small>Потрачено</small><b>{aiCost(ai.totalCost)}</b></span>
            <span><MessageSquare size={16} /><small>Диалогов</small><b>{ai.sessionCount || 0}</b></span>
            <span><Clock size={16} /><small>Последний</small><b>{ai.lastRunAt ? formatDateTime(ai.lastRunAt) : 'Нет запросов'}</b></span>
          </div>
        </div>
        <div className="admin-client-ai-sessions">
          {(ai.sessions || []).map((session) => (
            <React.Fragment key={session.id}>
              <button type="button" className={activeSession?.id === session.id ? 'active' : ''} onClick={() => openSession(session)}>
                <MessageSquare size={17} />
                <span><b>{session.title}</b><small>{formatDateTime(session.updatedAt)}</small></span>
                <small>{session.messageCount} сообщений</small>
                <strong>{aiCost(session.totalCost)}</strong>
                <ChevronRight size={17} />
              </button>
              {activeSession?.id === session.id && (
                <div className="admin-client-chat">
                  {activeSession.status === 'loading' && <LoadingState variant="inline" count={3} label="Загружаем переписку…" />}
                  {activeSession.status === 'error' && <InlineNotice tone="error">{activeSession.error}</InlineNotice>}
                  {activeSession.status === 'success' && activeSession.messages.map((message) => (
                    <article key={message.id} className={message.role === 'assistant' ? 'assistant' : 'user'}>
                      <small>{message.role === 'assistant' ? 'AI-агент' : 'Клиент'} · {formatDateTime(message.createdAt)}</small>
                      <p>{message.text || (message.imageName ? `Изображение: ${message.imageName}` : 'Сообщение без текста')}</p>
                    </article>
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}
          {!(ai.sessions || []).length && <div className="category-empty">У клиента пока нет диалогов с AI-агентом.</div>}
        </div>
      </section>
    </section>
  );
}

function LegacyAdminUsers({ users, managers, priceTiers, pagination, onPageChange, onSave, onReload }) {
  const [drafts, setDrafts] = useState({});
  const [busyPasswordUserId, setBusyPasswordUserId] = useState(null);
  const [busyReturnResetUserId, setBusyReturnResetUserId] = useState(null);
  const [pendingReturnResetUser, setPendingReturnResetUser] = useState(null);
  const [userActionFeedback, setUserActionFeedback] = useState({ type: '', text: '' });

  useEffect(() => {
    setDrafts({});
  }, [users]);

  function draft(user) {
    return drafts[user.id] || {
      id: user.id,
      role: user.role || 'customer',
      name: user.name || user.contractor_name || user.email,
      contractorName: user.contractor_name || user.name || '',
      phone: user.phone || '',
      priceTier: user.price_tier || 'B',
      managerId: user.manager_id || '',
      isActive: Boolean(user.is_active),
      newPassword: ''
    };
  }

  function setDraft(id, patch) {
    setDrafts((prev) => ({ ...prev, [id]: { ...draft(users.find((user) => user.id === id)), ...patch } }));
  }

  async function changePassword(user) {
    const row = draft(user);
    const password = String(row.newPassword || '').trim();
    setUserActionFeedback({ type: '', text: '' });
    if (password.length < 8) {
      setUserActionFeedback({ type: 'error', text: `Для ${user.email}: пароль должен быть не короче 8 символов.` });
      return;
    }
    setBusyPasswordUserId(user.id);
    try {
      await api(`/admin/users/${user.id}/password`, { method: 'POST', body: { password } });
      setDraft(user.id, { newPassword: '' });
      setUserActionFeedback({ type: 'success', text: `Пароль для ${user.email} обновлен.` });
    } catch (err) {
      setUserActionFeedback({ type: 'error', text: err.message || `Не удалось обновить пароль для ${user.email}.` });
    } finally {
      setBusyPasswordUserId(null);
    }
  }

  async function resetReturnLimit(user) {
    setBusyReturnResetUserId(user.id);
    setUserActionFeedback({ type: '', text: '' });
    try {
      await api(`/admin/users/${user.id}/return-limit/reset`, { method: 'POST' });
      setUserActionFeedback({ type: 'success', text: `Ограничение возвратов для ${user.email} сброшено.` });
      setPendingReturnResetUser(null);
      await onReload();
    } catch (err) {
      setUserActionFeedback({ type: 'error', text: err.message || `Не удалось сбросить ограничение для ${user.email}.` });
    } finally {
      setBusyReturnResetUserId(null);
    }
  }

  return (
    <>
      {userActionFeedback.text && (
        <div className={userActionFeedback.type === 'error' ? 'error' : 'success'}>{userActionFeedback.text}</div>
      )}
      {pendingReturnResetUser ? (
        <section className="admin-confirm-reset" role="alertdialog" aria-labelledby="return-reset-title">
          <div>
            <strong id="return-reset-title">Разрешить новый возврат?</strong>
            <span>
              Для клиента «{pendingReturnResetUser.contractor_name || pendingReturnResetUser.name || pendingReturnResetUser.email}»{' '}
              30-дневное ограничение будет снято. История возвратов останется без изменений.
            </span>
          </div>
          <div className="admin-confirm-reset-actions">
            <button type="button" onClick={() => setPendingReturnResetUser(null)} disabled={busyReturnResetUserId !== null}>
              Отмена
            </button>
            <button
              type="button"
              className="admin-primary-action"
              onClick={() => resetReturnLimit(pendingReturnResetUser)}
              disabled={busyReturnResetUserId !== null}
            >
              {busyReturnResetUserId === pendingReturnResetUser.id ? (
                <RefreshCw className="spin" size={16} aria-hidden="true" />
              ) : (
                <RotateCcw size={16} aria-hidden="true" />
              )}
              Разрешить возврат
            </button>
          </div>
        </section>
      ) : null}
      <AdminPager title="Клиенты" pagination={pagination} itemCount={users.length} onPageChange={onPageChange} />
      <div className="data-table admin-scroll-table admin-editor-table">
        <div className="data-head user admin-desktop-only">
          <b>Контрагент</b>
          <b>Почта</b>
          <b>Старый логин</b>
          <b>Телефон</b>
          <b>Прайс</b>
          <b>Менеджер</b>
          <b>Роль</b>
          <b>Активен</b>
          <b>Новый пароль</b>
          <b>МС</b>
          <b></b>
          <b>Действия</b>
        </div>
        {users.map((user) => {
          const row = draft(user);
          return (
            <React.Fragment key={user.id}>
              <div className="data-row user admin-desktop-only">
                <input
                  value={row.contractorName}
                  aria-label={`Контрагент ${user.email}`}
                  onChange={(event) => setDraft(user.id, { contractorName: event.target.value, name: event.target.value })}
                />
                <span>{user.email}</span>
                <span>{user.legacy_login || ''}</span>
                <input
                  value={row.phone}
                  aria-label={`Телефон ${user.email}`}
                  onChange={(event) => setDraft(user.id, { phone: event.target.value })}
                />
                <select
                  value={row.priceTier}
                  aria-label={`Прайс ${user.email}`}
                  onChange={(event) => setDraft(user.id, { priceTier: event.target.value })}
                >
                  {priceTiers.map((tier) => (
                    <option key={tier}>{tier}</option>
                  ))}
                </select>
                <select
                  value={row.managerId}
                  aria-label={`Менеджер ${user.email}`}
                  onChange={(event) => setDraft(user.id, { managerId: event.target.value })}
                >
                  <option value="">Нет</option>
                  {managers.map((manager) => (
                    <option key={manager.id} value={manager.id}>
                      {manager.name}
                    </option>
                  ))}
                </select>
                <select
                  value={row.role}
                  aria-label={`Роль ${user.email}`}
                  onChange={(event) => setDraft(user.id, { role: event.target.value })}
                >
                  <option value="customer">Клиент</option>
                  <option value="admin">Админ</option>
                </select>
                <label className="check-cell">
                  <input
                    type="checkbox"
                    checked={row.isActive}
                    aria-label={`Активен ${user.email}`}
                    onChange={(event) => setDraft(user.id, { isActive: event.target.checked })}
                  />
                </label>
                <input
                  type="text"
                  value={row.newPassword}
                  placeholder="Минимум 8 символов"
                  aria-label={`Новый пароль ${user.email}`}
                  onChange={(event) => setDraft(user.id, { newPassword: event.target.value })}
                />
                <span>{user.ms_counterparty_id ? 'связан' : ''}</span>
                <button
                  type="button"
                  onClick={() => changePassword(user)}
                  disabled={busyPasswordUserId === user.id}
                  aria-label={`Обновить пароль ${user.email}`}
                  title="Обновить пароль"
                >
                  {busyPasswordUserId === user.id ? (
                    <RefreshCw className="spin" size={16} aria-hidden="true" />
                  ) : (
                    <Shield size={16} aria-hidden="true" />
                  )}
                </button>
                <div className="admin-user-actions">
                  <button
                    type="button"
                    onClick={() => setPendingReturnResetUser(user)}
                    disabled={Boolean(user.return_limit?.canCreate) || busyReturnResetUserId !== null}
                    aria-label={`Сбросить ограничение возвратов ${user.email}`}
                    title={user.return_limit?.canCreate ? 'Новый возврат уже доступен' : 'Разрешить новый возврат сейчас'}
                  >
                    {busyReturnResetUserId === user.id ? (
                      <RefreshCw className="spin" size={16} aria-hidden="true" />
                    ) : (
                      <RotateCcw size={16} aria-hidden="true" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => onSave(row)}
                    aria-label={`Сохранить клиента ${user.email}`}
                    title="Сохранить клиента"
                  >
                    <Save size={16} aria-hidden="true" />
                  </button>
                </div>
              </div>
              <details className="admin-mobile-editor admin-mobile-only">
                <summary>
                  <span>
                    <b>{row.contractorName || user.email}</b>
                    <small>{user.email}</small>
                  </span>
                  <ChevronRight size={18} aria-hidden="true" />
                </summary>
                <div className="admin-mobile-fields">
                  <label>
                    <span>Контрагент</span>
                    <input
                      value={row.contractorName}
                      onChange={(event) => setDraft(user.id, { contractorName: event.target.value, name: event.target.value })}
                    />
                  </label>
                  <div className="admin-mobile-readonly">
                    <span>Почта</span>
                    <b>{user.email}</b>
                  </div>
                  {user.legacy_login && (
                    <div className="admin-mobile-readonly">
                      <span>Старый логин</span>
                      <b>{user.legacy_login}</b>
                    </div>
                  )}
                  <label>
                    <span>Телефон</span>
                    <input value={row.phone} onChange={(event) => setDraft(user.id, { phone: event.target.value })} />
                  </label>
                  <label>
                    <span>Прайс</span>
                    <select value={row.priceTier} onChange={(event) => setDraft(user.id, { priceTier: event.target.value })}>
                      {priceTiers.map((tier) => (
                        <option key={tier}>{tier}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>Менеджер</span>
                    <select value={row.managerId} onChange={(event) => setDraft(user.id, { managerId: event.target.value })}>
                      <option value="">Нет</option>
                      {managers.map((manager) => (
                        <option key={manager.id} value={manager.id}>
                          {manager.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>Роль</span>
                    <select value={row.role} onChange={(event) => setDraft(user.id, { role: event.target.value })}>
                      <option value="customer">Клиент</option>
                      <option value="admin">Админ</option>
                    </select>
                  </label>
                  <label className="admin-mobile-check">
                    <input
                      type="checkbox"
                      checked={row.isActive}
                      onChange={(event) => setDraft(user.id, { isActive: event.target.checked })}
                    />
                    <span>Активен</span>
                  </label>
                  <label>
                    <span>Новый пароль</span>
                    <input
                      type="text"
                      value={row.newPassword}
                      placeholder="Минимум 8 символов"
                      onChange={(event) => setDraft(user.id, { newPassword: event.target.value })}
                    />
                  </label>
                  <div className="admin-mobile-readonly">
                    <span>МойСклад</span>
                    <b>{user.ms_counterparty_id ? 'Связан' : 'Не связан'}</b>
                  </div>
                  <div className="admin-mobile-readonly">
                    <span>Новый возврат</span>
                    <b>
                      {user.return_limit?.canCreate
                        ? 'Доступен'
                        : `Через ${durationLabel(user.return_limit?.secondsRemaining)}`}
                    </b>
                  </div>
                  <div className="admin-mobile-actions">
                    <button type="button" onClick={() => changePassword(user)} disabled={busyPasswordUserId === user.id}>
                      {busyPasswordUserId === user.id ? <RefreshCw className="spin" size={16} /> : <Shield size={16} />}
                      Обновить пароль
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingReturnResetUser(user)}
                      disabled={Boolean(user.return_limit?.canCreate) || busyReturnResetUserId !== null}
                    >
                      {busyReturnResetUserId === user.id ? (
                        <RefreshCw className="spin" size={16} />
                      ) : (
                        <RotateCcw size={16} />
                      )}
                      Сбросить 30 дней
                    </button>
                    <button type="button" className="admin-primary-action" onClick={() => onSave(row)}>
                      <Save size={16} /> Сохранить
                    </button>
                  </div>
                </div>
              </details>
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
}

function AdminProducts({ products, pagination, onPageChange, onSave }) {
  const [drafts, setDrafts] = useState({});
  const productDraftKey = products
    .map((product) => `${product.id}:${product.updatedAt || ''}:${product.supplierStock ?? ''}:${product.msStock ?? ''}:${product.stock ?? ''}`)
    .join('|');

  useEffect(() => {
    setDrafts({});
  }, [productDraftKey]);

  function draft(product) {
    return drafts[product.id] || adminProductDraft(product);
  }

  function setDraft(id, patch) {
    const product = products.find((item) => item.id === id);
    if (!product) return;
    setDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] || adminProductDraft(product)), ...patch } }));
  }

  return (
    <>
      <AdminPager title="Товары" pagination={pagination} itemCount={products.length} onPageChange={onPageChange} />
      <div className="data-table admin-scroll-table admin-editor-table">
        <div className="data-head product admin-desktop-only">
          <b>Артикул</b>
          <b>Название</b>
          <b>Б</b>
          <b>Б-5</b>
          <b>Б-8</b>
          <b>Б-10</b>
          <div className="admin-product-stock-head">
            <b>Склад #1</b>
            <b>Итого</b>
            <b>МС</b>
          </div>
          <b></b>
        </div>
        {products.map((product) => {
          const row = draft(product);
          const totalStock = productTotalStock(product);
          const msStock = productMsStock(product);
          return (
            <React.Fragment key={product.id}>
              <div className="data-row product admin-desktop-only">
                <input
                  value={row.vendorCode}
                  aria-label={`Артикул товара ${product.vendorCode || product.id}`}
                  onChange={(event) => setDraft(product.id, { vendorCode: event.target.value })}
                />
                <input
                  value={row.name}
                  aria-label={`Название товара ${product.vendorCode || product.id}`}
                  onChange={(event) => setDraft(product.id, { name: event.target.value })}
                />
                {['B', 'B-5', 'B-8', 'B-10'].map((tier) => (
                  <input
                    key={tier}
                    value={row.prices[tier]}
                    aria-label={`Цена ${tier} товара ${product.vendorCode || product.id}`}
                    onChange={(event) =>
                      setDraft(product.id, {
                        prices: { ...row.prices, [tier]: Number(event.target.value || 0) }
                      })
                    }
                  />
                ))}
                <div className="admin-product-stock-fields">
                  <input
                    value={row.supplierStock}
                    aria-label={`Склад #1 товара ${product.vendorCode || product.id}`}
                    title="Склад #1 / Поставщик"
                    onChange={(event) => setDraft(product.id, { supplierStock: Number(event.target.value || 0) })}
                  />
                  <span title="Общий остаток">{formatQuantity(totalStock)}</span>
                  <span title="МойСклад">{formatQuantity(msStock)}</span>
                </div>
                <button
                  type="button"
                  onClick={() => onSave(adminProductSavePayload(row))}
                  aria-label={`Сохранить товар ${product.vendorCode || product.id}`}
                  title="Сохранить товар"
                >
                  <Save size={16} aria-hidden="true" />
                </button>
              </div>
              <details className="admin-mobile-editor admin-mobile-only">
                <summary>
                  <span>
                    <b>{row.vendorCode || 'Без артикула'}</b>
                    <small>{row.name || 'Без названия'}</small>
                  </span>
                  <ChevronRight size={18} aria-hidden="true" />
                </summary>
                <div className="admin-mobile-fields admin-mobile-product-fields">
                  <label>
                    <span>Артикул</span>
                    <input value={row.vendorCode} onChange={(event) => setDraft(product.id, { vendorCode: event.target.value })} />
                  </label>
                  <label>
                    <span>Название</span>
                    <input value={row.name} onChange={(event) => setDraft(product.id, { name: event.target.value })} />
                  </label>
                  {['B', 'B-5', 'B-8', 'B-10'].map((tier) => (
                    <label key={tier}>
                      <span>Цена {tier}</span>
                      <input
                        value={row.prices[tier]}
                        inputMode="decimal"
                        onChange={(event) =>
                          setDraft(product.id, {
                            prices: { ...row.prices, [tier]: Number(event.target.value || 0) }
                          })
                        }
                      />
                    </label>
                  ))}
                  <label>
                    <span>Склад #1 / Поставщик</span>
                    <input
                      value={row.supplierStock}
                      inputMode="decimal"
                      onChange={(event) => setDraft(product.id, { supplierStock: Number(event.target.value || 0) })}
                    />
                  </label>
                  <div className="admin-mobile-readonly">
                    <span>Итого / МойСклад</span>
                    <b>{formatQuantity(totalStock)} / {formatQuantity(msStock)}</b>
                  </div>
                  <div className="admin-mobile-actions">
                    <button
                      type="button"
                      className="admin-primary-action"
                      onClick={() => onSave(adminProductSavePayload(row))}
                    >
                      <Save size={16} /> Сохранить товар
                    </button>
                  </div>
                </div>
              </details>
            </React.Fragment>
          );
        })}
        {!products.length && <div className="category-empty">Товаров не найдено.</div>}
      </div>
    </>
  );
}

function AdminImport({ data, reload }) {
  const latest = data.status?.latest;
  const supplierStockLatest = data.status?.supplierStock?.latest;
  const [intervalHours, setIntervalHours] = useState(data.settings?.intervalHours || 168);
  const [showOutOfStock, setShowOutOfStock] = useState(data.settings?.catalogVisibility?.showOutOfStock !== false);
  const [busyAction, setBusyAction] = useState('');
  const [feedback, setFeedback] = useState({ type: '', text: '' });
  const [lastReloadAt, setLastReloadAt] = useState('');

  useEffect(() => {
    setIntervalHours(data.settings?.intervalHours || 168);
  }, [data.settings?.intervalHours]);

  useEffect(() => {
    setShowOutOfStock(data.settings?.catalogVisibility?.showOutOfStock !== false);
  }, [data.settings?.catalogVisibility?.showOutOfStock]);

  useEffect(() => {
    if (!data.status) return;
    setLastReloadAt(new Date().toLocaleTimeString('ru-RU'));
  }, [data.status]);

  useEffect(() => {
    const catalogActive = ['queued', 'running'].includes(latest?.status || '');
    const supplierStockActive = ['queued', 'running'].includes(supplierStockLatest?.status || '');
    if (!catalogActive && !supplierStockActive) return undefined;
    const timer = window.setInterval(() => {
      reload().catch(() => {});
    }, 3000);
    return () => window.clearInterval(timer);
  }, [latest?.status, reload, supplierStockLatest?.status]);

  async function runAction(action, task, successMessage) {
    setBusyAction(action);
    setFeedback({ type: '', text: '' });
    try {
      const result = await task();
      await reload();
      setFeedback({ type: 'success', text: successMessage(result) });
    } catch (err) {
      setFeedback({ type: 'error', text: err.message || 'Операция не выполнена' });
    } finally {
      setBusyAction('');
    }
  }

  const isImportActive = ['queued', 'running'].includes(latest?.status || '');
  const isSupplierStockActive = ['queued', 'running'].includes(supplierStockLatest?.status || '');
  const progressMax = Math.max(Number(latest?.total || 0), 1);
  const progressValue = Math.max(0, Math.min(progressMax, Number(latest?.processed || 0)));
  const supplierStockProgressMax = Math.max(Number(supplierStockLatest?.total || 0), 1);
  const supplierStockProgressValue = Math.max(
    0,
    Math.min(supplierStockProgressMax, Number(supplierStockLatest?.processed || 0))
  );

  return (
    <section className="table-panel">
      <div className="panel-title">
        <h1>Импорт каталога и остатков</h1>
        <span>{showOutOfStock ? 'в выдаче все товары' : 'в выдаче только остаток'}</span>
      </div>
      {feedback.text && <div className={feedback.type === 'error' ? 'error' : 'success'}>{feedback.text}</div>}
      <div className="settings-row">
        <label>
          Интервал каталога и фотографий, часов
          <input
            type="number"
            min="1"
            max="168"
            value={intervalHours}
            onChange={(event) => setIntervalHours(event.target.value)}
          />
        </label>
        <IconButton
          icon={busyAction === 'settings' ? RefreshCw : Save}
          onClick={() =>
            runAction(
              'settings',
              () =>
                api('/admin/import/settings', {
                  method: 'PUT',
                  body: { intervalHours: Number(intervalHours), showOutOfStock }
                }),
              (result) =>
                `Настройки сохранены: каталог и фотографии каждые ${result.intervalHours} ч., ${
                  result.catalogVisibility?.showOutOfStock ? 'товары без остатка видны' : 'товары без остатка скрыты'
                }.`
            )
          }
          disabled={busyAction !== ''}
          className={busyAction === 'settings' ? 'is-busy' : ''}
        >
          Сохранить
        </IconButton>
      </div>
      <label className="catalog-visibility-toggle">
        <input
          type="checkbox"
          checked={showOutOfStock}
          onChange={(event) => setShowOutOfStock(event.target.checked)}
        />
        <span>
          <b>Показывать товары без остатка</b>
          <small>Если выключить, клиентский каталог, авто-подбор и AI-поиск не будут находить позиции с остатком 0.</small>
        </span>
      </label>
      <div className="import-status">
        <b>Каталог, цены и фотографии</b>
        <StatusBadge status={latest?.status} fallback="Нет запусков" />
        <span>{latest?.message || 'История запусков пока пустая.'}</span>
        <progress value={progressValue} max={progressMax} />
        <small>
          {latest?.processed || 0} / {latest?.total || 0}
        </small>
        <small>
          {isImportActive ? 'Импорт идет, статус обновляется автоматически.' : `Последнее обновление: ${lastReloadAt || 'сейчас'}`}
        </small>
      </div>
      <div className="import-status">
        <b>Остатки поставщика · каждые {data.settings?.supplierStockIntervalHours || 2} ч.</b>
        <StatusBadge status={supplierStockLatest?.status} fallback="Нет запусков" />
        <span>{supplierStockLatest?.message || 'История запусков пока пустая.'}</span>
        <progress value={supplierStockProgressValue} max={supplierStockProgressMax} />
        <small>
          Сопоставлено: {supplierStockLatest?.matched || 0} / {supplierStockLatest?.processed || 0}, изменено:{' '}
          {supplierStockLatest?.updated || 0}
        </small>
        <small>
          {isSupplierStockActive
            ? 'Остатки обновляются.'
            : `Последний запуск: ${formatDateTime(supplierStockLatest?.finished_at) || 'ещё не запускался'}`}
        </small>
      </div>
      <div className="button-row">
        <IconButton
          icon={busyAction === 'start' ? RefreshCw : Play}
          onClick={() =>
            runAction(
              'start',
              () => api('/admin/import/start', { method: 'POST' }),
              (result) =>
                result.alreadyRunning
                  ? `Импорт уже выполняется. Активный запуск #${result.runId}.`
                  : `Импорт запущен. Запуск #${result.runId}.`
            )
          }
          disabled={busyAction !== '' || isImportActive}
          className={busyAction === 'start' ? 'is-busy' : ''}
        >
          Обновить каталог
        </IconButton>
        <IconButton
          icon={busyAction === 'stock' || isSupplierStockActive ? RefreshCw : Package}
          onClick={() =>
            runAction(
              'stock',
              () => api('/admin/import/stock/start', { method: 'POST' }),
              (result) =>
                result.alreadyRunning
                  ? `Остатки уже обновляются. Запуск #${result.runId}.`
                  : `Обновление остатков запущено. Запуск #${result.runId}.`
            )
          }
          disabled={busyAction !== '' || isSupplierStockActive}
          className={busyAction === 'stock' ? 'is-busy' : ''}
        >
          Обновить остатки
        </IconButton>
        <IconButton
          icon={busyAction === 'cancel' ? RefreshCw : Square}
          onClick={() =>
            runAction(
              'cancel',
              () => api('/admin/import/cancel', { method: 'POST' }),
              (result) => (result.cancelled ? 'Импорт остановлен.' : 'Активного импорта сейчас нет.')
            )
          }
          disabled={busyAction !== '' || !isImportActive}
          className={busyAction === 'cancel' ? 'is-busy' : ''}
        >
          Отмена
        </IconButton>
        <IconButton
          icon={busyAction === 'reload' ? RefreshCw : RefreshCw}
          onClick={() =>
            runAction(
              'reload',
              () => reload(),
              () => 'Статус импорта обновлен.'
            )
          }
          disabled={busyAction !== ''}
          className={busyAction === 'reload' ? 'is-busy' : ''}
        >
          Обновить
        </IconButton>
      </div>
    </section>
  );
}

function AdminMsSyncCard({ title, summary, sync = {}, onStart, busy, disabled }) {
  const isRunning = sync.status === 'running';
  const total = Number(sync.total || summary?.total || 0);
  const processed = Number(sync.processed || 0);
  const linked = Number(sync.linked || 0);
  const failed = Number(sync.failed || 0);
  const progressMax = Math.max(total, 1);
  const progressValue = Math.max(0, Math.min(progressMax, isRunning || sync.status === 'done' ? processed : 0));
  const failures = sync.failures || [];

  return (
    <section className="table-panel ms-sync-card">
      <div className="panel-title">
        <h1>{title}</h1>
        <span>{summary?.linked || 0} / {summary?.total || 0}</span>
      </div>
      <div className="import-status">
        <StatusBadge status={sync.status || 'idle'} />
        <span>{sync.message || 'Еще не запускалось'}</span>
        <progress value={progressValue} max={progressMax} />
        <small>
          Обработано: {processed} / {total}
        </small>
        <small>
          Связано: {linked || summary?.linked || 0}, не связано: {failed}
        </small>
      </div>
      <div className="button-row">
        <IconButton
          icon={isRunning || busy ? RefreshCw : Settings}
          onClick={onStart}
          disabled={disabled || isRunning || busy}
          className={isRunning || busy ? 'is-busy' : ''}
        >
          Связать
        </IconButton>
      </div>
      {failures.length > 0 && (
        <div className="ms-failures">
          <b>Не удалось связать</b>
          {failures.map((failure, index) => (
            <div className="ms-failure-row" key={`${failure.id || failure.code}-${index}`}>
              <strong>{failure.name || failure.code}</strong>
              <span>{failure.email || failure.code}</span>
              <small>{failure.reason}</small>
            </div>
          ))}
          {sync.failuresTruncated > 0 && <small>Еще скрыто: {sync.failuresTruncated}</small>}
        </div>
      )}
    </section>
  );
}

function AdminMsStockCard({ stocks, onStart, busy, disabled }) {
  const status = stocks.status || 'idle';
  const isRunning = status === 'running';
  const processed = numberOrNull(firstDefined(stocks.processed, stocks.matched, stocks.updated)) ?? 0;
  const total = numberOrNull(firstDefined(stocks.total, stocks.totalProducts, stocks.productCount, stocks.matched, stocks.updated)) ?? 0;
  const progressMax = Math.max(total, processed, 1);
  const progressValue = Math.max(0, Math.min(progressMax, isRunning || status === 'done' ? processed : 0));
  const resultAt = formatDateTime(stocks.finishedAt || stocks.lastRunAt || stocks.updatedAt);
  const counts = [
    ['Обновлено', stocks.updated],
    ['Найдено', stocks.matched],
    ['Пропущено', stocks.skipped],
    ['Отрицательные', stocks.negative]
  ].filter(([, value]) => value !== null && value !== undefined);

  return (
    <section className="table-panel ms-sync-card ms-stock-card">
      <div className="panel-title">
        <h1>Остатки</h1>
        <span>{statusLabel(status)}</span>
      </div>
      <div className="import-status">
        <StatusBadge status={status} />
        <span>{stocks.message || 'Еще не запускалось'}</span>
        <progress value={progressValue} max={progressMax} />
        <small>
          Обработано: {formatQuantity(processed)} / {formatQuantity(total)}
        </small>
        <div className="ms-stock-meta">
          <span>Склад МойСклад: {stocks.storeName || 'Основной склад'}</span>
          <span>Автоподгрузка: каждые 2 часа</span>
          {resultAt && <span>Последний результат: {resultAt}</span>}
        </div>
      </div>
      {counts.length > 0 && (
        <div className="ms-stock-counts">
          {counts.map(([label, value]) => (
            <span key={label}>
              {label}: {formatQuantity(value)}
            </span>
          ))}
        </div>
      )}
      <div className="button-row">
        <IconButton
          icon={isRunning || busy ? RefreshCw : Package}
          onClick={onStart}
          disabled={disabled || isRunning || busy}
          className={isRunning || busy ? 'is-busy' : ''}
        >
          Синхронизировать
        </IconButton>
      </div>
    </section>
  );
}

function AdminMoySklad({ data, reload }) {
  const [tokenDraft, setTokenDraft] = useState('');
  const [vatDraft, setVatDraft] = useState('7');
  const [organizationDraft, setOrganizationDraft] = useState('');
  const [storeDraft, setStoreDraft] = useState('');
  const [busyAction, setBusyAction] = useState('');
  const [feedback, setFeedback] = useState({ type: '', text: '' });
  const settings = data.settings || {};
  const organizations = data.organizations || [];
  const stores = data.stores || [];
  const counterparties = data.counterparties || {};
  const products = data.products || {};
  const stocks = resolveMsStockStatus(data);
  const anySyncRunning = counterparties.status === 'running' || products.status === 'running' || stocks.status === 'running';

  useEffect(() => {
    if (!anySyncRunning) return undefined;
    const timer = window.setInterval(() => {
      reload().catch(() => {});
    }, 1500);
    return () => window.clearInterval(timer);
  }, [anySyncRunning, reload]);

  useEffect(() => {
    const nextVat = settings.vat?.vatRate == null ? 'none' : String(settings.vat.vatRate);
    setVatDraft(nextVat);
  }, [settings.vat?.vatRate]);

  useEffect(() => {
    setOrganizationDraft(settings.organization?.href || '');
  }, [settings.organization?.href]);

  useEffect(() => {
    setStoreDraft(settings.store?.href || '');
  }, [settings.store?.href]);

  async function runAction(action, task, successMessage) {
    setBusyAction(action);
    setFeedback({ type: '', text: '' });
    try {
      await task();
      await reload();
      setFeedback({ type: 'success', text: successMessage });
      return true;
    } catch (err) {
      setFeedback({ type: 'error', text: err.message || 'Операция не выполнена' });
      return false;
    } finally {
      setBusyAction('');
    }
  }

  async function saveToken(event) {
    event.preventDefault();
    const saved = await runAction(
      'token',
      () =>
        api('/admin/ms/settings', {
          method: 'PUT',
          body: { token: tokenDraft, vatRate: vatDraft, organizationHref: organizationDraft, storeHref: storeDraft }
        }),
      'Настройки МС сохранены на сервере.'
    );
    if (saved) setTokenDraft('');
  }

  const tokenReady = Boolean(settings.tokenConfigured);
  const currentVat = settings.vat?.vatRate == null ? 'none' : String(settings.vat.vatRate);
  const currentOrganization = settings.organization?.href || '';
  const currentStore = settings.store?.href || '';
  const organizationLabel = settings.organization?.name || 'первая из МС';
  const storeLabel = settings.store?.name || 'первый склад в МС';
  const settingsChanged =
    tokenDraft.trim() || vatDraft !== currentVat || organizationDraft !== currentOrganization || storeDraft !== currentStore;

  return (
    <div className="ms-admin-grid">
      <section className="table-panel ms-token-panel">
        <div className="panel-title">
          <h1>МойСклад</h1>
          <span>
            {tokenReady ? `токен ${settings.tokenMask}` : 'токен не задан'} · НДС {settings.vat?.vatLabel || '7%'} ·{' '}
            {organizationLabel} · {storeLabel}
          </span>
        </div>
        {feedback.text && <div className={feedback.type === 'error' ? 'error' : 'success'}>{feedback.text}</div>}
        {data.organizationError && <div className="error">{data.organizationError}</div>}
        {data.storeError && <div className="error">{data.storeError}</div>}
        <form className="ms-token-row" onSubmit={saveToken}>
          <label>
            Токен
            <input
              type="password"
              value={tokenDraft}
              placeholder={tokenReady ? `Сохранен ${settings.tokenMask}` : 'Вставь токен МойСклад'}
              onChange={(event) => setTokenDraft(event.target.value)}
            />
          </label>
          <label>
            НДС
            <select value={vatDraft} onChange={(event) => setVatDraft(event.target.value)}>
              <option value="none">Без НДС</option>
              <option value="0">0%</option>
              <option value="5">5%</option>
              <option value="7">7%</option>
              <option value="10">10%</option>
              <option value="20">20%</option>
            </select>
          </label>
          <label>
            Юрлицо
            <select
              value={organizationDraft}
              onChange={(event) => setOrganizationDraft(event.target.value)}
              disabled={!tokenReady || !organizations.length}
            >
              <option value="">Авто: первое в МС</option>
              {organizations.map((organization) => (
                <option key={organization.href} value={organization.href}>
                  {organization.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Склад
            <select
              value={storeDraft}
              onChange={(event) => setStoreDraft(event.target.value)}
              disabled={!tokenReady || !stores.length}
            >
              <option value="">Авто: первый склад</option>
              {stores.map((store) => (
                <option key={store.href} value={store.href}>
                  {store.name}
                </option>
              ))}
            </select>
          </label>
          <IconButton
            icon={busyAction === 'token' ? RefreshCw : Save}
            disabled={!settingsChanged || busyAction !== ''}
            className={busyAction === 'token' ? 'is-busy' : ''}
          >
            Сохранить
          </IconButton>
        </form>
      </section>
      <AdminMsSyncCard
        title="Контрагенты"
        summary={data.summary?.counterparties}
        sync={counterparties}
        busy={busyAction === 'counterparties'}
        disabled={!tokenReady || busyAction !== ''}
        onStart={() =>
          runAction(
            'counterparties',
            () => api('/admin/ms/counterparties/link', { method: 'POST' }),
            'Связь контрагентов запущена.'
          )
        }
      />
      <AdminMsSyncCard
        title="Товары"
        summary={data.summary?.products}
        sync={products}
        busy={busyAction === 'products'}
        disabled={!tokenReady || busyAction !== ''}
        onStart={() =>
          runAction(
            'products',
            () => api('/admin/ms/products/link', { method: 'POST' }),
            'Связь товаров запущена.'
          )
        }
      />
      <AdminMsStockCard
        stocks={stocks}
        busy={busyAction === 'stocks'}
        disabled={!tokenReady || busyAction !== ''}
        onStart={() =>
          runAction(
            'stocks',
            () => api('/admin/ms/stocks/sync', { method: 'POST' }),
            'Синхронизация остатков запущена.'
          )
        }
      />
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [route, setRoute] = useState(getRoute());
  const [catalogState, setCatalogState] = useState(DEFAULT_CATALOG_STATE);
  const [cart, setCart] = useState({ items: [], total: 0, count: 0 });
  const [cartSyncing, setCartSyncing] = useState(false);
  const [cartStatus, setCartStatus] = useState('idle');
  const [cartError, setCartError] = useState('');
  const [booting, setBooting] = useState(true);
  const [aiOpen, setAiOpen] = useState(false);
  const historyIndexRef = useRef(
    typeof window.history.state?.appIndex === 'number' ? window.history.state.appIndex : 0
  );
  const cartLoadedRef = useRef(false);
  const cartRequestRef = useRef(0);
  const isMobile = useMediaQuery('(max-width: 900px)');

  function currentLocationPath() {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
  }

  function mergedHistoryState(index) {
    const currentState = window.history.state;
    return {
      ...(currentState && typeof currentState === 'object' ? currentState : {}),
      appIndex: index
    };
  }

  function navigate(next, options = {}) {
    const path = routePath(next);
    if (path !== currentLocationPath()) {
      if (options.replace) {
        window.history.replaceState(mergedHistoryState(historyIndexRef.current), '', path);
      } else {
        const nextIndex = historyIndexRef.current + 1;
        window.history.pushState({ appIndex: nextIndex }, '', path);
        historyIndexRef.current = nextIndex;
      }
    }
    window.scrollTo({ top: 0, behavior: 'auto' });
    setRoute(getRoute());
  }

  function navigateBack(fallback = '/') {
    if (historyIndexRef.current > 0) {
      window.history.back();
      return;
    }
    navigate(fallback, { replace: true });
  }

  function openCatalogHome() {
    setCatalogState(DEFAULT_CATALOG_STATE);
    navigate('/');
  }

  async function refreshCart({ silent = false } = {}) {
    const requestId = cartRequestRef.current + 1;
    cartRequestRef.current = requestId;
    if (!silent) setCartSyncing(true);
    if (!cartLoadedRef.current) setCartStatus('loading');
    setCartError('');
    try {
      const data = await api('/cart');
      if (cartRequestRef.current !== requestId) return;
      setCart({
        ...data,
        count: data.items.reduce((sum, item) => sum + item.qty, 0)
      });
      cartLoadedRef.current = true;
      setCartStatus('success');
    } catch (err) {
      if (cartRequestRef.current !== requestId) return;
      setCartError(err.message || 'Ошибка загрузки корзины');
      setCartStatus(cartLoadedRef.current ? 'success' : 'error');
    } finally {
      if (cartRequestRef.current === requestId) setCartSyncing(false);
    }
  }

  async function setCartQty(product, qty) {
    await api(`/cart/items/${product.id}`, { method: 'PUT', body: { qty: clampQty(qty, product.stock) } });
    await refreshCart();
  }

  async function checkout(comment, deliveryMethod) {
    const data = await api('/orders', { method: 'POST', body: { comment, deliveryMethod } });
    if (data.user) setUser((current) => ({ ...current, ...data.user }));
    await refreshCart();
    return data;
  }

  async function repeatOrder(orderId) {
    const data = await api(`/orders/${orderId}/repeat`, { method: 'POST' });
    await refreshCart();
    return data;
  }

  useEffect(() => {
    if (typeof window.history.state?.appIndex !== 'number') {
      window.history.replaceState(mergedHistoryState(historyIndexRef.current), '', currentLocationPath());
    }
    const syncRoute = (event) => {
      historyIndexRef.current = typeof event.state?.appIndex === 'number' ? event.state.appIndex : 0;
      setRoute(getRoute());
    };
    window.addEventListener('popstate', syncRoute);
    return () => window.removeEventListener('popstate', syncRoute);
  }, []);

  useEffect(() => {
    api('/me')
      .then((data) => {
        setUser(data.user);
        refreshCart({ silent: true });
      })
      .catch(() => {})
      .finally(() => setBooting(false));
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    const syncCart = () => {
      if (!document.hidden) refreshCart({ silent: true });
    };
    window.addEventListener('focus', syncCart);
    document.addEventListener('visibilitychange', syncCart);
    const intervalId = window.setInterval(syncCart, 15000);
    return () => {
      window.removeEventListener('focus', syncCart);
      document.removeEventListener('visibilitychange', syncCart);
      window.clearInterval(intervalId);
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user || user.role === 'admin') return undefined;
    const heartbeat = () => {
      if (document.hidden) return;
      const sessionId = getActivitySessionId();
      api('/activity/heartbeat', { method: 'POST', body: { sessionId } }).catch(() => {});
    };
    heartbeat();
    const intervalId = window.setInterval(heartbeat, 60000);
    window.addEventListener('focus', heartbeat);
    document.addEventListener('visibilitychange', heartbeat);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', heartbeat);
      document.removeEventListener('visibilitychange', heartbeat);
    };
  }, [user?.id, user?.role]);

  useEffect(() => {
    if (user && route.name === 'cart') refreshCart({ silent: true });
  }, [user?.id, route.name]);

  useEffect(() => {
    if (user && user.role !== 'admin' && route.name === 'admin') {
      navigate('/');
    }
  }, [user, route.name]);

  useEffect(() => {
    if (booting) return undefined;
    document.title = user ? routeTitle(route) : 'Вход — DIXEL';
    if (!user) return undefined;

    const frameId = window.requestAnimationFrame(() => {
      const main = document.querySelector('main');
      if (!(main instanceof HTMLElement)) return;
      main.id = 'main-content';
      main.tabIndex = -1;
      main.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [booting, route.name, route.productId, user?.id]);

  async function onLogout() {
    await api('/auth/logout', { method: 'POST' });
    cartRequestRef.current += 1;
    setUser(null);
    setCart({ items: [], total: 0, count: 0 });
    cartLoadedRef.current = false;
    setCartStatus('idle');
    setCartError('');
    setCatalogState(DEFAULT_CATALOG_STATE);
    setAiOpen(false);
    navigate('/');
  }

  if (booting) {
    return (
      <main className="boot" aria-live="polite" aria-busy="true">
        <Logo />
        <p>Загружаем рабочее пространство…</p>
      </main>
    );
  }
  if (!user) {
    return (
      <Login
        onLogin={(nextUser) => {
          cartRequestRef.current += 1;
          cartLoadedRef.current = false;
          setCartStatus('loading');
          setCartError('');
          setUser(nextUser);
          refreshCart();
        }}
      />
    );
  }

  const cartQtyById = Object.fromEntries(cart.items.map((item) => [item.product.id, item.qty]));

  let content = (
    <Catalog
      cartQtyById={cartQtyById}
      onSetCartQty={setCartQty}
      navigate={navigate}
      catalogState={catalogState}
      setCatalogState={setCatalogState}
    />
  );
  if (route.name === 'cart') {
    content = (
      <Cart
        cart={cart}
        cartStatus={cartStatus}
        cartLoadError={cartError}
        user={user}
        onSetCartQty={setCartQty}
        onCheckout={checkout}
        onRefresh={() => refreshCart()}
        refreshing={cartSyncing}
        navigate={navigate}
      />
    );
  } else if (route.name === 'auto') {
    content = <AutoCatalogPage cartQtyById={cartQtyById} onSetCartQty={setCartQty} navigate={navigate} />;
  } else if (route.name === 'accountReturn') {
    content = <ReturnPage navigate={navigate} />;
  } else if (route.name === 'account') {
    content = <Account user={user} onRepeatOrder={repeatOrder} navigate={navigate} />;
  } else if (route.name === 'accountExport') {
    content = <AccountExportPage user={user} navigate={navigate} />;
  } else if (route.name === 'admin' && user.role === 'admin') {
    content = <Admin />;
  } else if (route.name === 'product') {
    content = (
      <ProductDetail
        productId={route.productId}
        cartQtyById={cartQtyById}
        onSetCartQty={setCartQty}
        navigate={navigate}
        onBack={() => navigateBack('/')}
      />
    );
  }

  return (
    <Layout
      user={user}
      route={route}
      navigate={navigate}
      onCatalogHome={openCatalogHome}
      onLogout={onLogout}
      cartTotal={cart.total}
      cartCount={cart.count}
      aiEnabled={user?.aiEnabled}
      aiOpen={aiOpen}
      onToggleAi={() => setAiOpen((value) => !value)}
    >
      {content}
      {user?.aiEnabled ? (
        <AIChatWidget
          user={user}
          cartQtyById={cartQtyById}
          onSetCartQty={setCartQty}
          navigate={navigate}
          open={aiOpen}
          onOpenChange={setAiOpen}
          isMobile={isMobile}
        />
      ) : null}
    </Layout>
  );
}

createRoot(document.getElementById('root')).render(<App />);
